<template>
    <v-container>
        <!-- Loading overlay -->
        <v-overlay v-model="overlay" color="white" opacity="1" persistent>
            <v-container style="text-align: center">
                <v-progress-circular :size="50" color="#234a98" indeterminate></v-progress-circular>
                <p class="pt-2" style="font-size: 14px; color: #234a98">
                    {{ $t('common.loading') }}
                </p>
            </v-container>
        </v-overlay>

        <!-- Add New Widget Button -->
        <div class="mt-4 mb-4 ml-4">
            <v-btn color="#213A8F" dark @click="openAddWidgetDialog" elevation="2">
                <v-icon left>mdi-plus-circle-outline</v-icon>
                {{ $t('dashboard.addNewWidget') }}
            </v-btn>
        </div>

        <!-- Main widget grid -->
        <div class="row">
            <!-- Draggable widgets area -->
            <draggable 
                v-model="widgets" 
                :animation="150"
                ghost-class="ghost-widget"
                @start="dragStart"
                @end="dragEnd"
                class="draggable-widgets"
                handle=".widget-drag-handle"
            >
                <!-- Widget Container -->
                <v-col v-for="(widget, index) in widgets" 
                       :key="widget.id" 
                       :cols="getWidgetSize(widget.size)"
                       class="widget-container"
                >
                    <v-card 
                        class="widget-card"
                        :class="widget.size"
                    >
                        <!-- Widget Header with management icons -->
                        <v-card-title class="widget-header">
                            <v-icon class="widget-drag-handle mr-2" color="grey">mdi-drag</v-icon>
                            <span class="widget-title-text" v-if="widget.kpiType !== 'dispenser_fill_heatmap' && widget.kpiType !== 'dispenser_consumption_analysis'">{{ widget.title }}</span>
                            <v-menu offset-y>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn icon small v-bind="attrs" v-on="on" class="widget-settings-btn">
                                        <v-icon>mdi-cog</v-icon>
                                    </v-btn>
                                </template>
                                <v-list>
                                    <v-list-item @click="editWidget(widget, index)">
                                        <v-list-item-icon><v-icon>mdi-pencil</v-icon></v-list-item-icon>
                                        <v-list-item-title>{{ $t('common.edit') }}</v-list-item-title>
                                    </v-list-item>
                                    <v-divider></v-divider>
                                    <v-list-item-group>
                                        <v-subheader>{{ $t('dashboard.size') }}</v-subheader>
                                        <v-list-item 
                                            v-for="size in widgetSizes" 
                                            :key="size.value"
                                            @click="changeWidgetSize(widget, size.value)"
                                            :disabled="widget.size === size.value"
                                        >
                                            <v-list-item-icon>
                                                <v-icon v-if="widget.size === size.value">mdi-check</v-icon>
                                            </v-list-item-icon>
                                            <v-list-item-content>
                                                <v-list-item-title>{{ size.text }}</v-list-item-title>
                                                <v-list-item-subtitle class="text--secondary">
                                                    {{ size.description }}
                                                </v-list-item-subtitle>
                                            </v-list-item-content>
                                        </v-list-item>
                                    </v-list-item-group>
                                    <v-divider></v-divider>
                                    <v-list-item @click="removeWidget(index)" class="error--text">
                                        <v-list-item-icon><v-icon color="error">mdi-delete</v-icon></v-list-item-icon>
                                        <v-list-item-title>{{ $t('common.delete') }}</v-list-item-title>
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                        </v-card-title>

                        <!-- Widget Content -->
                        <v-card-text class="widget-content">
                            


                            <!-- KPI Widget -->
                            <template v-if="widget.type === 'kpi'">

                                <!-- Dispenser Fill Level Heatmap -->
                                <template v-if="widget.kpiType === 'dispenser_fill_heatmap'">
                                    <div class="heatmap-widget-container">
                                        <!-- Header: titolo centrato + legenda a destra -->
                                        <div class="heatmap-header-row">
                                            <div class="heatmap-title-area">
                                                <div class="heatmap-main-title">{{ $t('dashboard.kpiTypes.dispenserFillHeatmap.tableTitle') }}</div>
                                                <div class="heatmap-main-subtitle">{{ $t('dashboard.kpiTypes.dispenserFillHeatmap.tableSubtitle') }}</div>
                                            </div>
                                            <div class="heatmap-legend">
                                                <span class="heatmap-legend-label">Full</span>
                                                <div class="heatmap-legend-bar">
                                                    <div v-for="i in 5" :key="i"
                                                        class="heatmap-legend-cell"
                                                        :style="{ background: heatmapCellColor((i-1) * 25) }">
                                                    </div>
                                                </div>
                                                <span class="heatmap-legend-label">Critical (Empty)</span>
                                            </div>
                                        </div>
                                        <!-- Table -->
                                        <div class="heatmap-table-wrapper">
                                            <table class="heatmap-table">
                                                <thead>
                                                    <tr>
                                                        <th class="heatmap-th heatmap-th-location">{{ $t('dashboard.kpiTypes.dispenserFillHeatmap.colLocation') }}</th>
                                                        <th class="heatmap-th" v-for="slot in heatmapTimeSlots" :key="slot">{{ slot }}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr v-for="row in (widget.heatmapData || [])" :key="row.id">
                                                        <td class="heatmap-td-location">
                                                            <div class="heatmap-dispenser-name">{{ row.name }}</div>
                                                            <div v-if="row.type" class="heatmap-dispenser-type">{{ row.type }}</div>
                                                            <div class="heatmap-dispenser-location">
                                                                <v-icon x-small color="#5c6bc0">mdi-map-marker</v-icon>
                                                                {{ row.location }}
                                                            </div>
                                                        </td>
                                                        <td v-for="(fill, idx) in row.fills" :key="idx"
                                                            class="heatmap-td-cell"
                                                            :style="{ background: heatmapCellColor(100 - fill), color: fill < 45 ? '#fff' : '#213A8F' }">
                                                            {{ fill }}%
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                        <!-- Footer -->
                                        <div class="period-info">
                                            <span class="period-label">{{ widget.period || $t('dashboard.periodOptions.last14Days') }}</span>
                                            <span class="period-date" v-if="widget.periodEnd">Up to {{ formatDate(widget.periodEnd) }}</span>
                                        </div>
                                    </div>
                                </template>

                                <!-- Dispenser Consumption Analysis -->
                                <template v-else-if="widget.kpiType === 'dispenser_consumption_analysis'">
                                    <div class="dca-container">
                                        <!-- Header -->
                                        <div class="dca-header">
                                            <div class="dca-title">{{ $t('dashboard.dispenserConsumptionAnalysis.tableTitle') }}</div>
                                            <div class="dca-subtitle">{{ $t('dashboard.dispenserConsumptionAnalysis.tableSubtitle') }}</div>
                                        </div>

                                        <!-- Table -->
                                        <div class="dca-table-wrapper">
                                            <table class="dca-table">
                                                <thead>
                                                    <tr>
                                                        <th class="dca-th dca-col-dispenser">{{ $t('dashboard.dispenserConsumptionAnalysis.colDispenser') }}</th>
                                                        <th class="dca-th dca-col-location">{{ $t('dashboard.dispenserConsumptionAnalysis.colLocation') }}</th>
                                                        <th class="dca-th dca-col-consumption">{{ $t('dashboard.dispenserConsumptionAnalysis.colTotalConsumption') }}</th>
                                                        <th class="dca-th dca-col-avg">{{ $t('dashboard.dispenserConsumptionAnalysis.colAvgPerDay') }}</th>
                                                        <th class="dca-th dca-col-visits">{{ $t('dashboard.dispenserConsumptionAnalysis.colConsPer100Visits') }}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <template v-for="group in (widget.dispenserGroups || [])">
                                                        <!-- Group header row -->
                                                        <tr :key="'gh-' + group.nameKey" class="dca-group-header-row">
                                                            <td class="dca-group-name" colspan="2">
                                                                {{ $t('dashboard.dispenserConsumptionAnalysis.' + group.nameKey) }}
                                                            </td>
                                                            <td class="dca-group-unit" colspan="3">
                                                                {{ $t('dashboard.dispenserConsumptionAnalysis.' + group.unitKey) }}
                                                            </td>
                                                        </tr>
                                                        <!-- Item rows -->
                                                        <tr v-for="item in group.items" :key="'item-' + item.id" class="dca-item-row">
                                                            <td class="dca-td-dispenser">
                                                                <div class="dca-dispenser-name">{{ item.name }}</div>
                                                                <div class="dca-dispenser-type">{{ item.type }}</div>
                                                            </td>
                                                            <td class="dca-td-location">{{ item.location }}</td>
                                                            <td class="dca-td-consumption">
                                                                <div class="dca-bar-cell">
                                                                    <div class="dca-bar-track">
                                                                        <div class="dca-bar-fill"
                                                                            :style="{ width: Math.round((item.totalConsumption / group.maxConsumption) * 100) + '%' }">
                                                                        </div>
                                                                    </div>
                                                                    <span class="dca-bar-value">{{ item.totalConsumption }}</span>
                                                                </div>
                                                            </td>
                                                            <td class="dca-td-avg">{{ item.avgPerDay }}</td>
                                                            <td class="dca-td-visits"
                                                                :class="item.consPer100Visits === null ? '' : (item.consPer100Visits >= 0.6 ? 'dca-visits-high' : 'dca-visits-low')">
                                                                {{ item.consPer100Visits !== null ? item.consPer100Visits : '--' }}
                                                            </td>
                                                        </tr>
                                                        <!-- Group total row -->
                                                        <tr :key="'gt-' + group.nameKey" class="dca-group-total-row">
                                                            <td class="dca-total-label" colspan="2">
                                                                {{ $t('dashboard.dispenserConsumptionAnalysis.groupTotal') }}
                                                            </td>
                                                            <td class="dca-total-value">{{ group.groupTotal.totalConsumption }}</td>
                                                            <td class="dca-total-value">{{ group.groupTotal.avgPerDay }}</td>
                                                            <td class="dca-total-dash">--</td>
                                                        </tr>
                                                    </template>
                                                </tbody>
                                            </table>
                                        </div>

                                        <!-- Footer -->
                                        <div class="period-info">
                                            <span class="period-label">{{ $t('dashboard.period') }}: {{ widget.period || $t('dashboard.periodOptions.last30Days') }}</span>
                                            <span class="period-date" v-if="widget.periodEnd">Up to {{ formatDate(widget.periodEnd) }}</span>
                                        </div>
                                    </div>
                                </template>

                                <!-- Table-based KPI (Total Paper Consumption) -->
                                <template v-else-if="isTableKpi(widget.kpiType)">
                                    <div class="table-kpi-container">
                                        <v-data-table
                                            :headers="getTableHeaders(widget)"
                                            :items="widget.tableData || []"
                                            :sort-by="getDefaultSortBy(widget.kpiType)"
                                            :sort-desc="true"
                                            hide-default-footer
                                            disable-pagination
                                            class="kpi-table"
                                        >
                                            <!-- Template per Refill Frequency values -->
                                            <template v-if="widget.kpiType === 'refill_frequency_avg_day'" slot="item.refillsPerDay" slot-scope="{ item }">
                                                <span class="refill-value">{{ item.refillsPerDay }}</span>
                                            </template>
                                            
                                            <!-- Template per Packs Used (Total Paper Consumption) -->
                                            <template v-if="widget.kpiType === 'total_paper_consumption'" slot="item.packsUsed" slot-scope="{ item }">
                                                <span class="packs-value">{{ item.packsUsed }}</span>
                                            </template>
                                            
                                            <!-- Template per Device Name con Location (Restroom Daily Usage) -->
                                            <template v-if="widget.kpiType === 'restroom_daily_usage_avg'" slot="item.deviceName" slot-scope="{ item }">
                                                <div class="restroom-info">
                                                    <div class="restroom-name">{{ item.deviceName }}</div>
                                                    <div class="restroom-location">
                                                        <v-icon small color="grey">mdi-map-marker</v-icon>
                                                        {{ item.location }}
                                                    </div>
                                                </div>
                                            </template>
                                            
                                            <!-- Template per Avg Entries (Restroom Daily Usage) -->
                                            <template v-if="widget.kpiType === 'restroom_daily_usage_avg'" slot="item.avgEntries" slot-scope="{ item }">
                                                <span class="entries-value">{{ item.avgEntries }}</span>
                                            </template>
                                            
                                            <!-- Template per Daily Pattern AM/PM (Restroom Daily Usage) -->
                                            <template v-if="widget.kpiType === 'restroom_daily_usage_avg'" slot="item.dailyPattern" slot-scope="{ item }">
                                                <div class="daily-pattern-container">
                                                    <!-- Barra di progresso -->
                                                    <div class="pattern-bar">
                                                        <div class="pattern-am" :style="{ width: item.amPercentage + '%' }">
                                                            <span v-if="item.amPercentage > 15" class="pattern-label">{{ item.amPercentage }}%</span>
                                                        </div>
                                                        <div class="pattern-pm" :style="{ width: item.pmPercentage + '%' }">
                                                            <span v-if="item.pmPercentage > 15" class="pattern-label">{{ item.pmPercentage }}%</span>
                                                        </div>
                                                    </div>
                                                    <!-- Etichette sotto la barra -->
                                                    <div class="pattern-labels">
                                                        <span class="label-am">{{ item.amPercentage }}%</span>
                                                        <span class="label-pm">{{ item.pmPercentage }}%</span>
                                                    </div>
                                                </div>
                                            </template>
                                            
                                            <!-- Template per Avg Alerts (Alert Activity) -->
                                            <template v-if="widget.kpiType === 'alert_activity_avg_day'" slot="item.avgAlerts" slot-scope="{ item }">
                                                <span class="alerts-value">{{ item.avgAlerts }}</span>
                                            </template>
                                            
                                            <!-- Template per trend (comune a tutti i KPI tabellari) -->
                                            <template slot="item.trend" slot-scope="{ item }">
                                                <span :class="getTrendClass(item.trend)">
                                                    <v-icon 
                                                        :color="getTrendColor(item.trend)" 
                                                        size="16" 
                                                        class="mr-1"
                                                    >
                                                        {{ getTrendIcon(item.trend) }}
                                                    </v-icon>
                                                    {{ Math.abs(item.trend).toFixed(1) }}%
                                                </span>
                                            </template>
                                        </v-data-table>
                                        
                                        <!-- Period Info -->
                                        <div class="period-info">
                                            <span class="period-label">Period: {{ widget.period || 'Last 30 days' }}</span>
                                            <span class="period-date" v-if="widget.periodEnd">Up to {{ formatDate(widget.periodEnd) }}</span>
                                        </div>
                                    </div>
                                </template>
                                
                                <!-- Numeric KPI Display -->
                                <template v-else-if="isNumericKpi(widget.kpiType)">
                                    <div class="kpi-container">
                                        <div class="kpi-value">{{ widget.value }}</div>
                                        <div class="kpi-label">{{ widget.label }}</div>
                                    </div>
                                </template>
                                
                                <!-- Chart-based KPI (Comparisons, Heatmap) -->
                                <template v-else>
                                    <apexchartComponent
                                        :type="getChartTypeForKpi(widget.kpiType)"
                                        :options="getKpiChartOptions(widget)"
                                        :series="widget.series || []"
                                        :height="getWidgetHeight(widget.size)"
                                        ref="kpiChart"
                                    />
                                </template>
                            </template>
                        </v-card-text>
                    </v-card>
                </v-col>
            </draggable>
        </div>

        <!-- Main dialog for adding/editing a widget -->
        <v-dialog v-model="widgetDialog" max-width="800px">
            <v-card>
                <v-card-title class="headline">
                    <span v-if="editingWidgetIndex !== null && editingWidgetIndex !== undefined">
                        {{ $t('dashboard.editWidget') }}
                    </span>
                    <span v-else>
                        {{ currentStep === 1 ? $t('dashboard.kpiWidget') : $t('dashboard.configureKpiWidget') }}
                    </span>
                    <v-spacer></v-spacer>
                    <v-btn icon @click="closeWidgetDialog">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>

                <v-card-text>
                    <!-- Step 1: KPI Type Selection -->
                    <v-window v-model="currentStep">
                        <v-window-item :value="1">
                            <v-row>
                                <v-col cols="6" v-for="kpi in kpiTypes" :key="kpi.value">
                                    <v-card
                                        class="kpi-type-card"
                                        :class="{ selected: selectedKpiType === kpi.value }"
                                        @click="selectKpiType(kpi.value)"
                                        elevation="2"
                                        height="120"
                                    >
                                        <v-card-text class="pa-3">
                                            <div class="d-flex align-center mb-2">
                                                <v-icon :color="kpi.color" class="mr-3" size="24">{{ kpi.icon }}</v-icon>
                                                <div class="kpi-title">{{ kpi.text }}</div>
                                            </div>
                                            <div class="kpi-description">{{ kpi.description }}</div>
                                        </v-card-text>
                                    </v-card>
                                </v-col>
                            </v-row>
                        </v-window-item>

                        <!-- Step 2: KPI Configuration -->
                        <v-window-item :value="2">
                            <v-form ref="form" v-model="isFormValid">
                                <v-text-field
                                    v-model="widgetTitle"
                                    :label="$t('dashboard.widgetTitle')"
                                    outlined
                                    required
                                    class="widget-title-input"
                                    :style="{ 'font-weight': '600', 'color': '#2c3e50' }"
                                ></v-text-field>
                                    <!-- Data Level Selection - nascosto per Restroom Daily Usage, Usage Frequency Comparison e Dispenser Fill Heatmap -->
                                    <v-select
                                        v-if="selectedKpiType !== 'restroom_daily_usage_avg' && selectedKpiType !== 'usage_frequency_comparison' && selectedKpiType !== 'dispenser_fill_heatmap'"
                                        v-model="selectedDataLevel"
                                        :items="selectedKpiType === 'dispenser_consumption_analysis' ? dataLevelOptionsForDca : dataLevelOptions"
                                        :label="$t('dashboard.dataLevel')"
                                        outlined
                                        required
                                        @change="onDataLevelChange"
                                    ></v-select>

                                    <!-- Hierarchical Selection based on Data Level -->
                                    
                                    <!-- Client Level: Multiple clients selection -->
                                    <template v-if="selectedDataLevel === 'client'">
                                        <v-select
                                            v-model="selectedClients"
                                            :items="availableClients"
                                            :label="$t('dashboard.selectClients')"
                                            outlined
                                            required
                                            multiple
                                            chips
                                            deletable-chips
                                            :loading="loadingEntities"
                                            item-text="text"
                                            item-value="value"
                                            prepend-inner-icon="mdi-account"
                                            class="mb-3"
                                        ></v-select>
                                    </template>

                                    <!-- Building Level: Client → Building (multiple buildings only) -->
                                    <template v-if="selectedDataLevel === 'building'">
                                        <v-select
                                            v-model="selectedClient"
                                            :items="availableClients"
                                            :label="$t('dashboard.selectClient')"
                                            outlined
                                            required
                                            :loading="loadingEntities"
                                            item-text="text"
                                            item-value="value"
                                            prepend-inner-icon="mdi-account"
                                            class="mb-3"
                                            @change="onHierarchicalClientChange"
                                        ></v-select>

                                        <v-select
                                            v-if="selectedClient"
                                            v-model="selectedBuildings"
                                            :items="availableBuildings"
                                            :label="$t('dashboard.selectBuildings')"
                                            outlined
                                            required
                                            multiple
                                            chips
                                            deletable-chips
                                            :loading="loadingBuildings"
                                            item-text="text"
                                            item-value="value"
                                            prepend-inner-icon="mdi-home"
                                        ></v-select>
                                    </template>

                                    <!-- Floor Level: Client → Building → Floor (multiple floors only) -->
                                    <template v-if="selectedDataLevel === 'floor'">
                                        <v-select
                                            v-model="selectedClient"
                                            :items="availableClients"
                                            :label="$t('dashboard.selectClient')"
                                            outlined
                                            required
                                            :loading="loadingEntities"
                                            item-text="text"
                                            item-value="value"
                                            prepend-inner-icon="mdi-account"
                                            class="mb-3"
                                            @change="onHierarchicalClientChange"
                                        ></v-select>

                                        <v-select
                                            v-if="selectedClient"
                                            v-model="selectedBuilding"
                                            :items="availableBuildings"
                                            :label="$t('dashboard.selectBuilding')"
                                            outlined
                                            required
                                            :loading="loadingBuildings"
                                            item-text="text"
                                            item-value="value"
                                            prepend-inner-icon="mdi-home"
                                            class="mb-3"
                                            @change="onHierarchicalBuildingChange"
                                        ></v-select>

                                        <v-select
                                            v-if="selectedBuilding"
                                            v-model="selectedFloors"
                                            :items="availableFloors"
                                            :label="$t('dashboard.selectFloors')"
                                            outlined
                                            required
                                            multiple
                                            chips
                                            deletable-chips
                                            :loading="loadingFloors"
                                            item-text="text"
                                            item-value="value"
                                            prepend-inner-icon="mdi-floor-plan"
                                        ></v-select>
                                    </template>

                                    <!-- Area Level: Client → Building → Floor → Area (multiple areas only) -->
                                    <template v-if="selectedDataLevel === 'area'">
                                        <v-select
                                            v-model="selectedClient"
                                            :items="availableClients"
                                            :label="$t('dashboard.selectClient')"
                                            outlined
                                            required
                                            :loading="loadingEntities"
                                            item-text="text"
                                            item-value="value"
                                            prepend-inner-icon="mdi-account"
                                            class="mb-3"
                                            @change="onHierarchicalClientChange"
                                        ></v-select>

                                        <v-select
                                            v-if="selectedClient"
                                            v-model="selectedBuilding"
                                            :items="availableBuildings"
                                            :label="$t('dashboard.selectBuilding')"
                                            outlined
                                            required
                                            :loading="loadingBuildings"
                                            item-text="text"
                                            item-value="value"
                                            prepend-inner-icon="mdi-home"
                                            class="mb-3"
                                            @change="onHierarchicalBuildingChange"
                                        ></v-select>

                                        <v-select
                                            v-if="selectedBuilding"
                                            v-model="selectedFloor"
                                            :items="availableFloors"
                                            :label="$t('dashboard.selectFloor')"
                                            outlined
                                            required
                                            :loading="loadingFloors"
                                            item-text="text"
                                            item-value="value"
                                            prepend-inner-icon="mdi-floor-plan"
                                            class="mb-3"
                                            @change="onHierarchicalFloorChange"
                                        ></v-select>

                                        <v-select
                                            v-if="selectedFloor"
                                            v-model="selectedAreas"
                                            :items="availableAreas"
                                            :label="$t('dashboard.selectAreas')"
                                            outlined
                                            required
                                            multiple
                                            chips
                                            deletable-chips
                                            :loading="loadingAreas"
                                            item-text="text"
                                            item-value="value"
                                            prepend-inner-icon="mdi-texture-box"
                                        ></v-select>
                                    </template>

                                    <!-- Sensor Level: Single selections until sensors (multiple sensors only) -->
                                    <template v-if="selectedDataLevel === 'sensor'">
                                        <!-- Per Restroom Daily Usage e Usage Frequency Comparison: solo Client e Sensors -->
                                        <template v-if="selectedKpiType === 'restroom_daily_usage_avg' || selectedKpiType === 'usage_frequency_comparison'">
                                            <v-select
                                                v-model="selectedClient"
                                                :items="availableClients"
                                                :label="$t('dashboard.selectClient')"
                                                outlined
                                                required
                                                :loading="loadingEntities"
                                                item-text="text"
                                                item-value="value"
                                                prepend-inner-icon="mdi-account"
                                                class="mb-3"
                                                @change="onHierarchicalClientChangeForWashroomKpis"
                                            ></v-select>

                                            <v-select
                                                v-if="selectedClient"
                                                v-model="selectedSensors"
                                                :items="availableSensorsWithSelectAll"
                                                :label="$t('dashboard.selectSensors')"
                                                outlined
                                                required
                                                multiple
                                                chips
                                                deletable-chips
                                                :loading="loadingSensors"
                                                item-text="text"
                                                item-value="value"
                                                prepend-inner-icon="mdi-access-point-network"
                                                @change="onSensorsSelectionChange"
                                            ></v-select>
                                        </template>

                                        <!-- Per altri KPI: gerarchia completa -->
                                        <template v-else>
                                            <v-select
                                                v-model="selectedClient"
                                                :items="availableClients"
                                                :label="$t('dashboard.selectClient')"
                                                outlined
                                                required
                                                :loading="loadingEntities"
                                                item-text="text"
                                                item-value="value"
                                                prepend-inner-icon="mdi-account"
                                                class="mb-3"
                                                @change="onHierarchicalClientChange"
                                            ></v-select>

                                            <v-select
                                                v-if="selectedClient"
                                                v-model="selectedBuilding"
                                                :items="availableBuildings"
                                                :label="$t('dashboard.selectBuilding')"
                                                outlined
                                                required
                                                :loading="loadingBuildings"
                                                item-text="text"
                                                item-value="value"
                                                prepend-inner-icon="mdi-home"
                                                class="mb-3"
                                                @change="onHierarchicalBuildingChange"
                                            ></v-select>

                                            <v-select
                                                v-if="selectedBuilding"
                                                v-model="selectedFloor"
                                                :items="availableFloors"
                                                :label="$t('dashboard.selectFloor')"
                                                outlined
                                                required
                                                :loading="loadingFloors"
                                                item-text="text"
                                                item-value="value"
                                                prepend-inner-icon="mdi-floor-plan"
                                                class="mb-3"
                                                @change="onHierarchicalFloorChange"
                                            ></v-select>

                                            <v-select
                                                v-if="selectedFloor"
                                                v-model="selectedArea"
                                                :items="availableAreas"
                                                :label="$t('dashboard.selectArea')"
                                                outlined
                                                required
                                                :loading="loadingAreas"
                                                item-text="text"
                                                item-value="value"
                                                prepend-inner-icon="mdi-texture-box"
                                                class="mb-3"
                                                @change="onHierarchicalAreaChange"
                                            ></v-select>

                                            <!-- Per la heatmap la selezione si ferma all'area: tutti i dispenser vengono caricati automaticamente -->
                                            <v-select
                                                v-if="selectedArea && selectedKpiType !== 'dispenser_fill_heatmap'"
                                                v-model="selectedSensors"
                                                :items="availableSensorsWithSelectAll"
                                                :label="$t('dashboard.selectSensors')"
                                                outlined
                                                required
                                                multiple
                                                chips
                                                deletable-chips
                                                :loading="loadingSensors"
                                                item-text="text"
                                                item-value="value"
                                                prepend-inner-icon="mdi-access-point-network"
                                                @change="onSensorsSelectionChange"
                                            ></v-select>

                                        </template>
                                    </template>

                                    <!-- Period Selection: sempre visibile nello step 2, senza condizioni -->
                                    <v-select
                                        v-model="selectedPeriod"
                                        :items="periodOptions"
                                        :label="$t('dashboard.selectPeriod')"
                                        outlined
                                        required
                                        prepend-inner-icon="mdi-calendar"
                                        class="mb-3"
                                        @change="onPeriodChange"
                                    ></v-select>

                                    <!-- Custom Period Selection -->
                                    <template v-if="selectedPeriod === 'custom'">
                                        <v-menu
                                            ref="customDateMenu"
                                            v-model="customDateMenu"
                                            :close-on-content-click="false"
                                            :return-value.sync="customDateRange"
                                            transition="scale-transition"
                                            offset-y
                                            min-width="auto"
                                        >
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-text-field
                                                    outlined
                                                    v-model="customDateRangeFormatted"
                                                    :label="$t('dashboard.selectDateRange')"
                                                    prepend-inner-icon="mdi-calendar"
                                                    readonly
                                                    v-bind="attrs"
                                                    v-on="on"
                                                    class="mb-3"
                                                ></v-text-field>
                                            </template>
                                            <v-date-picker
                                                v-model="customDateRange"
                                                no-title
                                                scrollable
                                                :locale="$i18n.locale"
                                                range
                                                :max="getYesterday()"
                                                :min="getMinSelectableDate()"
                                            >
                                                <v-spacer></v-spacer>
                                                <v-btn text color="primary" @click="customDateMenu = false">
                                                    {{ $t('common.cancel') }}
                                                </v-btn>
                                                <v-btn text color="primary" @click="$refs.customDateMenu.save(customDateRange)">
                                                    {{ $t('common.ok') }}
                                                </v-btn>
                                            </v-date-picker>
                                        </v-menu>
                                        
                                        <!-- Period Validation Info -->
                                        <v-alert 
                                            v-if="showPeriodValidation"
                                            :type="periodValidationType"
                                            text 
                                            dense 
                                            class="mb-3"
                                            border="left"
                                        >
                                            {{ periodValidationMessage }}
                                        </v-alert>
                                    </template>

                                    <!-- Period Info Display -->
                                    <v-card 
                                        outlined 
                                        class="period-info-card mb-4"
                                        v-if="selectedPeriod"
                                    >
                                        <v-card-text class="py-2">
                                            <div class="period-info-text">
                                                <v-icon small color="#666" class="mr-2">mdi-information-outline</v-icon>
                                                <span class="text--secondary">{{ getPeriodDisplayText() }}</span>
                                            </div>
                                        </v-card-text>
                                    </v-card>

                                <!-- Per Dispenser Consumption Analysis la dimensione è fissa a Large -->
                                <v-alert
                                    v-if="selectedKpiType === 'dispenser_consumption_analysis'"
                                    type="info"
                                    text
                                    dense
                                    border="left"
                                    class="mb-3"
                                >
                                    {{ $t('dashboard.sizes.large') }} — {{ $t('dashboard.sizes.largeDesc') }}
                                </v-alert>
                                <v-select
                                    v-else
                                    v-model="widgetSize"
                                    :items="widgetSizes"
                                    :label="$t('dashboard.widgetSize')"
                                    outlined
                                    required
                                >
                                    <template v-slot:item="{ item }">
                                        <v-list-item-content>
                                            <v-list-item-title>{{ item.text }}</v-list-item-title>
                                            <v-list-item-subtitle class="text--secondary">
                                                {{ item.description }}
                                            </v-list-item-subtitle>
                                            <div class="font-italic">{{ item.recommendedUse }}</div>
                                        </v-list-item-content>
                                    </template>
                                </v-select>
                            </v-form>
                        </v-window-item>
                    </v-window>
                </v-card-text>

                <v-divider></v-divider>

                <v-card-actions>
                    <v-btn
                        text
                        @click="handleBackOrCancel"
                    >
                        {{ currentStep > 1 ? $t('common.back') : $t('common.cancel') }}
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                        color="primary"
                        text
                        @click="saveWidget()"
                        :disabled="currentStep === 2 && (!isFormValid || !isWidgetConfigurationComplete)"
                        v-if="currentStep === 2"
                    >
                        <span v-if="editingWidgetIndex !== null && editingWidgetIndex !== undefined">
                            {{ $t('common.update') }}
                        </span>
                        <span v-else>
                            {{ $t('common.save') }}
                        </span>
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import draggable from 'vuedraggable';
import axios from 'axios';
import { applyDoorCountFloor } from '@/utils/sensorUtils';
import { formatDateDisplay, toBackendDate, toBackendDateTime, normalizeTimezone, parseDateAsCET } from '@/utils/dateTimezone';
import { applyTimezoneToApexCharts } from '@/utils/chartDateFormatter';

export default {
    name: 'NewDashboard',
    components: {
        draggable
    },
    data: () => ({
        overlay: false,
        widgets: [],
        widgetDialog: false,
        editingWidget: {
            title: '',
            type: null,
            size: 'medium',
            entities: [],
            dataLevel: null,
            articleType: null,
            startDate: null,
            endDate: null
        },
        drag: false,

        // Form data
        selectedWidgetType: null,
        selectedChartType: null,
        selectedParameters: [],
        selectedKpiType: null,
        widgetTitle: '',
        widgetSize: 'medium',
        selectedChartTemplate: null,
        selectedComparisonLevel: null,
        selectedEntities: [],
        selectedPeriod: null,
        startDate: null,
        endDate: null,
        customDateMenu: false,
        loadingEntities: false,
        availableEntities: [],
        selectedEntity: null,

        // Available options
        widgetTypes: [
            { text: 'KPI Widget', value: 'kpi' }
        ],
        chartTypes: [
            { text: 'Line', value: 'line' },
            { text: 'Bar', value: 'bar' },
            { text: 'Area', value: 'area' }
        ],
        availableParameters: [
            { text: 'Floors', value: 'floors' },
            { text: 'Areas', value: 'areas' },
            { text: 'Periods', value: 'periods' },
            { text: 'Costs', value: 'costs' }
        ],
        currentStep: 1,
        chartTemplates: [
            {
                text: 'Paper Consumption Comparison',
                value: 'paper_consumption',
                description: 'Compare paper consumption between different areas or floors over time',
                defaultType: 'bar',
                updateInterval: 'Daily update',
                preview: '/chart-previews/bar-chart.svg'
            },
            {
                text: 'Usage Trend',
                value: 'usage_trend',
                description: 'View dispenser usage trend over time',
                defaultType: 'line',
                updateInterval: 'Real-time update',
                preview: '/chart-previews/line-chart.svg'
            },
            {
                text: 'Average Daily Consumption',
                value: 'daily_consumption',
                description: 'Show average daily consumption by area or floor',
                defaultType: 'area',
                updateInterval: 'Daily update',
                preview: '/chart-previews/area-chart.svg'
            },
            {
                text: 'Alert Events',
                value: 'alert_events',
                description: 'Monitor alert events and system anomalies',
                defaultType: 'bar',
                updateInterval: 'Real-time update',
                preview: '/chart-previews/alert-chart.svg'
            },
            {
                text: 'Sensor Comparison',
                value: 'sensor_comparison',
                description: 'Compare data between selected sensors',
                defaultType: 'line',
                updateInterval: 'Real-time update',
                preview: '/chart-previews/comparison-chart.svg'
            }
        ],
        comparisonLevels: [
            { text: 'Client', value: 'client' },
            { text: 'Building', value: 'building' },
            { text: 'Floor', value: 'floor' },
            { text: 'Area', value: 'area' },
            { text: 'Sensor', value: 'sensor' }
        ],
        kpiParameters: {
            dataLevel: [],
            period: [
                { text: 'Last 7 days', value: '7d' },
                { text: 'This month', value: 'month' },
                { text: 'Custom range', value: 'custom' }
            ],
            articleType: [
                { text: 'Z Fold', value: 'z_fold' },
                { text: 'Jumbo Roll', value: 'jumbo_roll' }
            ],
            size: [
                { text: 'Small', value: 'small' },
                { text: 'Medium', value: 'medium' },
                { text: 'Large', value: 'large' }
            ]
        },
        editingWidgetIndex: null,
        isDragging: false,
        selectedDataLevel: '',
        // Single selections for hierarchical navigation
        selectedClient: '',
        selectedBuilding: '',
        selectedFloor: '',
        selectedArea: '',
        // Multiple selections for final level
        selectedClients: [], // Used when client is the final level
        selectedBuildings: [], // Used when building is the final level  
        selectedFloors: [], // Used when floor is the final level
        selectedAreas: [], // Used when area is the final level
        selectedSensors: [], // Used when sensor is the final level
        availableClients: [],
        availableBuildings: [],
        availableFloors: [],
        availableAreas: [],
        availableSensors: [],
        availableSensorsFullData: [], // dati completi (con DispenserSubtype) usati dalla heatmap
        // Loading states for hierarchical selection
        loadingBuildings: false,
        loadingFloors: false,
        loadingAreas: false,
        loadingSensors: false,
        // Period selection
        customDateRange: [],
    }),

    computed: {
        widgetSizes() {
            return [
                { 
                    text: this.$t('dashboard.sizes.small'),
                    value: 'small',
                    description: this.$t('dashboard.sizes.smallDesc'),
                    cols: 4,
                    height: 200
                },
                { 
                    text: this.$t('dashboard.sizes.medium'),
                    value: 'medium',
                    description: this.$t('dashboard.sizes.mediumDesc'),
                    cols: 6,
                    height: 350
                },
                { 
                    text: this.$t('dashboard.sizes.large'),
                    value: 'large',
                    description: this.$t('dashboard.sizes.largeDesc'),
                    cols: 12,
                    height: 500
                }
            ];
        },

        heatmapTimeSlots() {
            return ['00:00 - 04:00', '04:00 - 08:00', '08:00 - 12:00', '12:00 - 16:00', '16:00 - 20:00', '20:00 - 24:00'];
        },

        isYoutissueAdmin() {
            // Accesso diretto allo store per garantire il tracking reattivo in Vue 2
            const userRole = this.$store.state.userRole;
            let roles = userRole;
            if (!roles || roles === '' || (Array.isArray(roles) && roles.length === 0)) {
                try {
                    const saved = localStorage.getItem('userRole');
                    if (saved) roles = JSON.parse(saved);
                } catch (e) { /* ignore */ }
            }
            if (Array.isArray(roles)) {
                return roles.some(r => (r.Name || r.name || '').toLowerCase() === 'youtissue admin');
            }
            if (typeof roles === 'string') {
                return roles.toLowerCase().includes('youtissue admin');
            }
            return false;
        },

        canSeeDispenserWidgets() {
            // Visibili a Youtissue Admin oppure alle aziende con idCompany specifici
            const ALLOWED_COMPANY_IDS = ['5', '12', '25', '26'];
            const companyId = String(this.$store.state.company || '');
            return this.isYoutissueAdmin || ALLOWED_COMPANY_IDS.includes(companyId);
        },

        kpiTypes() {
            const types = [
                {
                    text: this.$t('dashboard.kpiTypes.restroomDailyUsage.title'),
                    value: 'restroom_daily_usage_avg',
                    description: this.$t('dashboard.kpiTypes.restroomDailyUsage.description'),
                    icon: 'mdi-door-open',
                    format: 'entries/day',
                    color: '#009CAB'
                },
                {
                    text: this.$t('dashboard.kpiTypes.usageFrequencyComparison.title'),
                    value: 'usage_frequency_comparison',
                    description: this.$t('dashboard.kpiTypes.usageFrequencyComparison.description'),
                    icon: 'mdi-chart-line',
                    format: 'comparison',
                    color: '#3F51B5'
                }
            ];
            // Widget dispenser visibili a Youtissue Admin e alle aziende abilitate
            if (this.canSeeDispenserWidgets) {
                types.push({
                    text: this.$t('dashboard.kpiTypes.dispenserFillHeatmap.title'),
                    value: 'dispenser_fill_heatmap',
                    description: this.$t('dashboard.kpiTypes.dispenserFillHeatmap.description'),
                    icon: 'mdi-thermometer-lines',
                    format: 'heatmap',
                    color: '#213A8F'
                });
                types.push({
                    text: this.$t('dashboard.kpiTypes.dispenserConsumptionAnalysis.title'),
                    value: 'dispenser_consumption_analysis',
                    description: this.$t('dashboard.kpiTypes.dispenserConsumptionAnalysis.description'),
                    icon: 'mdi-package-variant-closed',
                    format: 'grouped-table',
                    color: '#009CAB'
                });
            }
            return types;
        },

        periodOptions() {
            return [
                { text: this.$t('dashboard.periodOptions.lastDay'), value: 'last_day' },
                { text: this.$t('dashboard.periodOptions.last7Days'), value: 'last_7_days' },
                { text: this.$t('dashboard.periodOptions.last14Days'), value: 'last_14_days' },
                { text: this.$t('dashboard.periodOptions.last30Days'), value: 'last_30_days' },
                { text: this.$t('dashboard.periodOptions.customPeriod'), value: 'custom' }
            ];
        },

        dataLevelOptions() {
            return [
                { text: this.$t('dashboard.client'), value: 'client' },
                { text: this.$t('dashboard.building'), value: 'building' },
                { text: this.$t('dashboard.floor'), value: 'floor' },
                { text: this.$t('dashboard.area'), value: 'area' },
                { text: this.$t('dashboard.sensor'), value: 'sensor' }
            ];
        },

        // Per Dispenser Consumption Analysis il livello Sensor non è disponibile
        dataLevelOptionsForDca() {
            return this.dataLevelOptions.filter(o => o.value !== 'sensor');
        },

        // Computed properties for "Select All" options in sensor level
        availableClientsWithSelectAll() {
            if (!this.availableClients || this.availableClients.length === 0) {
                return [];
            }
            return [
                { text: this.$t('dashboard.selectAll'), value: 'all' },
                ...this.availableClients
            ];
        },

        availableBuildingsWithSelectAll() {
            if (!this.availableBuildings || this.availableBuildings.length === 0) {
                return [];
            }
            return [
                { text: this.$t('dashboard.selectAll'), value: 'all' },
                ...this.availableBuildings
            ];
        },

        availableFloorsWithSelectAll() {
            if (!this.availableFloors || this.availableFloors.length === 0) {
                return [];
            }
            return [
                { text: this.$t('dashboard.selectAll'), value: 'all' },
                ...this.availableFloors
            ];
        },

        availableAreasWithSelectAll() {
            if (!this.availableAreas || this.availableAreas.length === 0) {
                return [];
            }
            return [
                { text: this.$t('dashboard.selectAll'), value: 'all' },
                ...this.availableAreas
            ];
        },

        availableSensorsWithSelectAll() {
            if (!this.availableSensors || this.availableSensors.length === 0) {
                return [];
            }
            return [
                { text: this.$t('dashboard.selectAll'), value: 'all' },
                ...this.availableSensors
            ];
        },

        // Period validation computed properties
        // Computed per formattare il range di date in stringa leggibile
        customDateRangeFormatted() {
            if (!this.customDateRange || this.customDateRange.length === 0) {
                return '';
            }
            if (this.customDateRange.length === 1) {
                return this.customDateRange[0];
            }
            // Ordina le date (il v-date-picker range può restituirle in ordine casuale)
            const sortedDates = [...this.customDateRange].sort();
            return `${sortedDates[0]} ~ ${sortedDates[1]}`;
        },

        // Computed per compatibilità con il codice esistente
        customStartDate() {
            if (!this.customDateRange || this.customDateRange.length === 0) return '';
            // Ordina le date per assicurarsi che la prima sia la più vecchia
            const sortedDates = [...this.customDateRange].sort();
            return sortedDates[0];
        },

        customEndDate() {
            if (!this.customDateRange || this.customDateRange.length < 2) return '';
            // Ordina le date per assicurarsi che la seconda sia la più recente
            const sortedDates = [...this.customDateRange].sort();
            return sortedDates[1];
        },

        showPeriodValidation() {
            return this.selectedPeriod === 'custom' && this.customStartDate && this.customEndDate;
        },

        periodValidationType() {
            if (!this.customStartDate || !this.customEndDate) return 'info';
            
            const yesterday = this.getYesterday();
            const minDate = this.getMinSelectableDate();
            const diffDays = this.getDateDifference(this.customStartDate, this.customEndDate);
            
            // Controlla se le date sono nel range valido
            if (this.customEndDate > yesterday) return 'error'; // Futuro non permesso
            if (this.customStartDate < minDate) return 'error'; // Troppo indietro
            if (this.customStartDate > this.customEndDate) return 'error'; // Date invertite
            if (diffDays < 1) return 'error'; // Periodo troppo corto (non dovrebbe mai succedere ora)
            if (diffDays > 14) return 'warning'; // Periodo lungo ma valido
            
            return 'success';
        },

        periodValidationMessage() {
            if (!this.customStartDate || !this.customEndDate) return '';
            
            const yesterday = this.getYesterday();
            const minDate = this.getMinSelectableDate();
            const diffDays = this.getDateDifference(this.customStartDate, this.customEndDate);
            
            // Messaggi di errore specifici
            if (this.customEndDate > yesterday) {
                return this.$t('dashboard.validation.futureDateNotAllowed');
            }
            if (this.customStartDate < minDate) {
                return this.$t('dashboard.validation.startDateTooOld');
            }
            if (this.customStartDate > this.customEndDate) {
                return this.$t('dashboard.validation.endDateBeforeStart');
            }
            if (diffDays < 1) {
                return this.$t('dashboard.validation.periodTooShort');
            }
            if (diffDays > 14) {
                return this.$t('dashboard.validation.periodTooLong', { days: diffDays });
            }
            
            return this.$t('dashboard.validation.validPeriod', { days: diffDays });
        },

        dateValidationRules() {
            return {
                startDate: (value) => {
                    if (!value) return this.$t('dashboard.validation.startDateRequired');
                    return true;
                },
                endDate: (value) => {
                    if (!value) return this.$t('dashboard.validation.endDateRequired');
                    if (this.customStartDate && value < this.customStartDate) {
                        return this.$t('dashboard.validation.endDateAfterStart');
                    }
                    return true;
                }
            };
        },

        isWidgetConfigurationComplete() {
            // Check if all required fields are filled
            const hasValidDataLevel = this.selectedDataLevel;
            const hasValidPeriod = this.selectedPeriod;
            const hasValidTitle = this.widgetTitle && this.widgetTitle.trim() !== '';
            
            // For custom period, ensure both dates are selected and valid
            let hasValidCustomPeriod = true;
            if (this.selectedPeriod === 'custom') {
                const yesterday = this.getYesterday();
                const minDate = this.getMinSelectableDate();
                
                hasValidCustomPeriod = this.customStartDate && 
                                     this.customEndDate && 
                                     this.customStartDate <= this.customEndDate &&
                                     this.customStartDate >= minDate && // Non più di 1 mese fa
                                     this.customEndDate <= yesterday && // Non nel futuro
                                     this.getDateDifference(this.customStartDate, this.customEndDate) >= 1; // Almeno 1 giorno
            }
            
            // Per Restroom Daily Usage e Usage Frequency Comparison, verifica che sia selezionato il livello sensor
            if ((this.selectedKpiType === 'restroom_daily_usage_avg' || this.selectedKpiType === 'usage_frequency_comparison') && this.selectedDataLevel !== 'sensor') {
                return false;
            }

            // Per Dispenser Consumption Analysis il livello sensor non è ammesso
            if (this.selectedKpiType === 'dispenser_consumption_analysis' && this.selectedDataLevel === 'sensor') {
                return false;
            }
            
            // Check entity selection based on data level
            let hasValidEntitySelection = true;
            switch (this.selectedDataLevel) {
                case 'client':
                    // Multiple clients selection required
                    hasValidEntitySelection = this.selectedClients?.length > 0;
                    break;
                case 'building':
                    // Single client + multiple buildings required
                    hasValidEntitySelection = this.selectedClient && this.selectedBuildings?.length > 0;
                    break;
                case 'floor':
                    // Single client + single building + multiple floors required
                    hasValidEntitySelection = this.selectedClient && 
                                            this.selectedBuilding && 
                                            this.selectedFloors?.length > 0;
                    break;
                case 'area':
                    // Single client + single building + single floor + multiple areas required
                    hasValidEntitySelection = this.selectedClient && 
                                            this.selectedBuilding && 
                                            this.selectedFloor && 
                                            this.selectedAreas?.length > 0;
                    break;
                case 'sensor':
                    // Per Restroom Daily Usage e Usage Frequency Comparison: solo client + sensors richiesti
                    if (this.selectedKpiType === 'restroom_daily_usage_avg' || this.selectedKpiType === 'usage_frequency_comparison') {
                        hasValidEntitySelection = this.selectedClient && 
                                                this.selectedSensors?.length > 0;
                    } else if (this.selectedKpiType === 'dispenser_fill_heatmap') {
                        // Per heatmap: basta selezionare fino all'area (i sensori si caricano automaticamente)
                        hasValidEntitySelection = this.selectedClient && 
                                                this.selectedBuilding && 
                                                this.selectedFloor && 
                                                !!this.selectedArea;
                    } else {
                        // Per altri KPI: Single selections until area + multiple sensors required
                        hasValidEntitySelection = this.selectedClient && 
                                                this.selectedBuilding && 
                                                this.selectedFloor && 
                                                this.selectedArea && 
                                                this.selectedSensors?.length > 0;
                    }
                    break;
            }

            return hasValidDataLevel && hasValidPeriod && hasValidTitle && hasValidCustomPeriod && hasValidEntitySelection;
        }
    },

    methods: {
        // Helper method per applicare l'aggiustamento Door Counter
        applyDoorCounterAdjustment(value, sensorSubtype) {
            // Per sensori Door Count: tutti i dati con decimali approssimati per difetto
            return applyDoorCountFloor(value, sensorSubtype, { divideBy2: true });
        },
        
        getWidgetSize(size) {
            const sizeConfig = this.widgetSizes.find(s => s.value === size);
            return sizeConfig ? sizeConfig.cols : 6;
        },

        getWidgetHeight(size) {
            const sizeConfig = this.widgetSizes.find(s => s.value === size);
            return sizeConfig ? sizeConfig.height : 350;
        },

        selectWidgetType(type) {
            this.selectedWidgetType = type;
        },

        selectChartTemplate(template) {
            this.selectedChartTemplate = template;
            const selectedTemplate = this.chartTemplates.find(t => t.value === template);
            if (selectedTemplate) {
                this.selectedChartType = selectedTemplate.defaultType;
                this.widgetTitle = selectedTemplate.text;
            }
        },

        openAddWidgetDialog() {
            this.resetForm(); // Reset completo di tutti i campi
            this.editingWidget = this.defaultWidgetState(); // Oggetto vuoto per nuovo widget
            this.selectedWidgetType = 'kpi'; // Imposta automaticamente KPI
            this.currentStep = 1;
            this.widgetDialog = true;
        },

        handleBackOrCancel() {
            if (this.currentStep > 1) {
                // Se stiamo modificando un widget, non permettere di tornare indietro
                if (this.editingWidgetIndex !== null && this.editingWidgetIndex !== undefined) {
                    // Cancel modifica: chiudi il dialog
                    this.resetForm();
                    this.editingWidgetIndex = null;
                    this.widgetDialog = false;
                } else {
                    // Back: resetta la form e torna al passo 1 (solo in creazione)
                    this.resetForm();
                    this.selectedWidgetType = 'kpi'; // Reimposta automaticamente KPI
                    this.currentStep = 1;
                }
            } else {
                // Cancel: resetta la form e chiudi il dialog
                this.resetForm();
                this.editingWidgetIndex = null;
                this.widgetDialog = false;
            }
        },

        closeWidgetDialog() {
            // Chiusura dialog (tasto X): resetta la form e chiudi
            this.resetForm();
            this.editingWidgetIndex = null;
            this.widgetDialog = false;
        },

        async editWidget(widget, index) {
            try {
                this.overlay = true;
                
                this.editingWidgetIndex = index;
                this.editingWidget = { ...this.defaultWidgetState(), ...widget };
                
                // Resetta la form prima di popolarla
                this.resetForm();
                
                // Popola la form con i dati del widget esistente
                await this.populateFormFromWidget(widget);
                
                // Imposta lo step 2 (configurazione) saltando la selezione del tipo KPI
                this.selectedWidgetType = 'kpi';
                this.selectedKpiType = widget.kpiType;
                this.widgetTitle = widget.title || '';
                this.widgetSize = widget.size || 'medium';
                this.currentStep = 2;
                
                // Apri il dialog principale (widgetDialog) invece di editDialog
                this.widgetDialog = true;
            } catch (error) {
                console.error('Errore durante il caricamento del widget per modifica:', error);
                alert(this.$t('common.error') || 'Errore nel caricamento del widget');
            } finally {
                this.overlay = false;
            }
        },

        async populateFormFromWidget(widget) {
            try {
                console.log('Populating form from widget:', widget);
                
                // Popola i campi principali della form
                this.selectedKpiType = widget.kpiType || null;
                this.widgetTitle = widget.title || '';
                this.widgetSize = widget.size || 'medium';
                this.selectedDataLevel = widget.dataLevel || '';
                
                // Per Restroom Daily Usage e Usage Frequency Comparison, assicura che il data level sia sensor
                if (widget.kpiType === 'restroom_daily_usage_avg' || widget.kpiType === 'usage_frequency_comparison') {
                    this.selectedDataLevel = 'sensor';
                }
                
                // Popola informazioni del periodo
                this.selectedPeriod = widget.periodType || 'last_7_days';
                if (widget.periodType === 'custom' && widget.periodStartDate && widget.periodEndDate) {
                    this.customDateRange = [widget.periodStartDate, widget.periodEndDate];
                }
                
                // Ricostruisci le selezioni gerarchiche dalla hierarchicalInfo salvata
                if (widget.hierarchicalInfo) {
                    await this.reconstructHierarchicalSelections(widget);
                } else if (widget.dataLevel && widget.entityId) {
                    // Fallback per widget legacy senza hierarchicalInfo
                    console.warn('Widget senza hierarchicalInfo, usando fallback');
                    await this.reconstructFromEntityIdOnly(widget);
                }
                
                // Popola editingWidget con TUTTE le informazioni necessarie per le chiamate API
                this.editingWidget.dataLevel = widget.dataLevel;
                this.editingWidget.entityId = widget.entityId;
                this.editingWidget.entityNames = widget.entityNames; // IMPORTANTE: serve per le chiamate API
                this.editingWidget.hierarchicalInfo = widget.hierarchicalInfo; // IMPORTANTE: serve per ricostruire la gerarchia
                this.editingWidget.periodType = widget.periodType;
                this.editingWidget.periodDays = widget.periodDays;
                this.editingWidget.periodStartDate = widget.periodStartDate;
                this.editingWidget.periodEndDate = widget.periodEndDate;
                this.editingWidget.icon = widget.icon;
                this.editingWidget.color = widget.color;
                this.editingWidget.clientInfo = widget.clientInfo;
                this.editingWidget.summary = widget.summary;
                this.editingWidget.tooltipDetails = widget.tooltipDetails;
                
                console.log('Form populated successfully');
            } catch (error) {
                console.error('Errore durante il popolamento del form:', error);
                throw error;
            }
        },

        async reconstructHierarchicalSelections(widget) {
            // Ricostruisce le selezioni gerarchiche dal widget esistente
            // Prima resetta le selezioni
            this.resetEntitySelections();
            
            if (!widget.dataLevel) return;
            
            try {
                // Carica sempre i clienti
                await this.loadClients();
                
                // Se abbiamo hierarchicalInfo salvate, usale per una ricostruzione accurata
                if (widget.hierarchicalInfo) {
                    await this.reconstructFromHierarchicalInfo(widget.hierarchicalInfo);
                } else {
                    // Fallback: ricostruzione semplificata usando solo entityId
                    await this.reconstructFromEntityIdOnly(widget);
                }
            } catch (error) {
                console.error('Errore nella ricostruzione delle selezioni gerarchiche:', error);
                // Se la ricostruzione fallisce, almeno imposta il data level
                this.selectedDataLevel = widget.dataLevel;
            }
        },

        async reconstructFromHierarchicalInfo(hierarchicalInfo) {
            // Ricostruzione accurata usando le informazioni gerarchiche salvate
            const info = hierarchicalInfo;
            
            // Ripristina le selezioni singole
            this.selectedClient = info.selectedClient || '';
            this.selectedBuilding = info.selectedBuilding || '';
            this.selectedFloor = info.selectedFloor || '';
            this.selectedArea = info.selectedArea || '';
            
            // Ripristina le selezioni multiple
            this.selectedClients = [...(info.selectedClients || [])];
            this.selectedBuildings = [...(info.selectedBuildings || [])];
            this.selectedFloors = [...(info.selectedFloors || [])];
            this.selectedAreas = [...(info.selectedAreas || [])];
            this.selectedSensors = [...(info.selectedSensors || [])];
            
            console.log('Reconstructing hierarchical selections:', info);
            
            // Ricarica le opzioni disponibili seguendo la gerarchia
            if (this.selectedClient) {
                // Per Restroom Daily Usage e Usage Frequency Comparison: carica sensori direttamente dal client
                if (this.selectedKpiType === 'restroom_daily_usage_avg' || this.selectedKpiType === 'usage_frequency_comparison') {
                    console.log('Loading washroom sensors for client:', this.selectedClient);
                    await this.onHierarchicalClientChangeForWashroomKpis(this.selectedClient);
                    
                    // Ripristina i sensori selezionati dopo il caricamento
                    await this.$nextTick();
                    this.selectedSensors = [...(info.selectedSensors || [])];
                    console.log('Sensors restored:', this.selectedSensors);
                } else {
                    // Per altri KPI: gerarchia completa
                    await this.onHierarchicalClientChange(this.selectedClient);
                    
                    if (this.selectedBuilding) {
                        await this.onHierarchicalBuildingChange(this.selectedBuilding);
                        
                        if (this.selectedFloor) {
                            await this.onHierarchicalFloorChange(this.selectedFloor);
                            
                            if (this.selectedArea) {
                                await this.onHierarchicalAreaChange(this.selectedArea);
                                
                                // Ripristina i sensori selezionati dopo il caricamento dell'area
                                await this.$nextTick();
                                this.selectedSensors = [...(info.selectedSensors || [])];
                            }
                        }
                    }
                }
            }
        },

        async reconstructFromEntityIdOnly(widget) {
            // Fallback: ricostruzione semplificata per widget esistenti senza hierarchicalInfo
            switch (widget.dataLevel) {
                case 'client':
                    this.selectedClients = Array.isArray(widget.entityId) ? widget.entityId : [widget.entityId];
                    break;
                    
                case 'building':
                    // Implementazione semplificata
                    if (Array.isArray(widget.entityId) && widget.entityId.length > 0) {
                        this.selectedClient = this.getClientIdFromBuildingIds(widget.entityId);
                        await this.onHierarchicalClientChange(this.selectedClient);
                        this.selectedBuildings = widget.entityId;
                    }
                    break;
                    
                // Altri casi per floor, area, sensor potrebbero essere aggiunti se necessario
                default:
                    console.warn(`Ricostruzione limitata per dataLevel: ${widget.dataLevel}`);
            }
        },

        // Helper per la ricostruzione della gerarchia legacy
        getClientIdFromBuildingIds(buildingIds) { // eslint-disable-line no-unused-vars
            // Fallback per widget esistenti senza hierarchicalInfo
            // TODO: implementare la logica per trovare il client corretto basandosi sui buildingIds
            return this.availableClients.length > 0 ? this.availableClients[0].value : '';
        },

        getHierarchicalInfo() {
            // Risolvi il nome del client selezionato per renderlo disponibile al reload
            // senza dover ricaricare availableClients
            const resolveClientName = (id) => {
                if (!id) return '';
                const found = this.availableClients.find(c => c.value === id);
                return found ? found.text : '';
            };
            const primaryClientId = this.selectedClient
                || (this.selectedClients && this.selectedClients[0])
                || null;

            return {
                dataLevel: this.selectedDataLevel,
                selectedClient: this.selectedClient,
                selectedClientName: resolveClientName(primaryClientId),
                selectedBuilding: this.selectedBuilding,
                selectedFloor: this.selectedFloor,
                selectedArea: this.selectedArea,
                selectedClients: [...(this.selectedClients || [])],
                selectedBuildings: [...(this.selectedBuildings || [])],
                selectedFloors: [...(this.selectedFloors || [])],
                selectedAreas: [...(this.selectedAreas || [])],
                selectedSensors: [...(this.selectedSensors || [])]
            };
        },

        async removeWidget(index) {
            const widget = this.widgets[index];
            
            try {
                this.overlay = true;
                
                // Elimina la configurazione dal backend
                await this.deleteWidgetConfiguration(widget.id);
                
                // Rimuovi il widget dalla lista locale
                this.widgets.splice(index, 1);
            } catch (error) {
                console.error('Errore nella rimozione del widget:', error);
                // Mostra un messaggio di errore
                alert(this.$t('common.error') || 'Errore nella rimozione del widget');
            } finally {
                this.overlay = false;
            }
        },

        async deleteWidgetConfiguration(widgetId) {
            try {
                const token = localStorage.getItem('jwtToken');
                
                // Leggi tutte le preferenze attuali dal server per non perdere altri dati
                const getUserPreferencesUrl = this.$store.state.BASE_URL + this.$store.state.URL_GET_USER_PREFERENCES;
                const preferencesResponse = await axios.get(getUserPreferencesUrl, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                // Estrai tutte le preferenze esistenti
                // La risposta di /v1/users/me contiene userPreferences come oggetto root
                const allPreferences = preferencesResponse.data?.userPreferences || {};
                const currentCharts = allPreferences.selectedCharts || [];
                
                console.log('Current charts before delete:', currentCharts.length, 'widgets');
                console.log('Deleting widget with id:', widgetId);
                
                // Rimuovi il widget dalle preferenze - mantieni TUTTI gli altri
                const updatedCharts = currentCharts.filter(chart => chart.id !== widgetId);
                console.log('Charts after delete:', updatedCharts.length, 'widgets');

                // Prepara tutte le preferenze da inviare (mantieni tutto e modifica solo selectedCharts)
                const updatedPreferences = {
                    ...allPreferences,
                    selectedCharts: updatedCharts
                };
                
                // Calcola la dimensione del payload
                const payloadSize = new Blob([JSON.stringify({ userPreferences: updatedPreferences })]).size;
                console.log('Payload size after delete:', payloadSize, 'bytes', '(limit: 1048576 bytes / 1 MB)');

                const patchUrl = this.$store.state.BASE_URL + this.$store.state.URL_PATCH_USER_PREFERENCES;
                
                // La PATCH richiede la struttura userPreferences: { selectedCharts: [...] }
                await axios.patch(patchUrl, {
                    userPreferences: updatedPreferences
                }, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });

                // Aggiorna lo store locale con le nuove preferenze complete
                this.$store.state.userPreferences = updatedPreferences;

                console.log('Configurazione widget eliminata con successo');
            } catch (error) {
                console.error('Errore nell\'eliminazione della configurazione del widget:', error);
                throw error;
            }
        },

        async loadWidgetConfigurations() {
            try {
                this.overlay = true;
                const token = localStorage.getItem('jwtToken');
                
                if (!token) {
                    console.warn('JWT token non disponibile');
                    return;
                }
                
                // Fai sempre una chiamata GET a /v1/users/me per ottenere i dati aggiornati
                // (importante quando si ricarica la pagina e lo store è vuoto)
                console.log('Fetching user preferences from /v1/users/me...');
                const url = this.$store.state.BASE_URL + this.$store.state.URL_GET_USER_PREFERENCES;
                
                const response = await axios.get(url, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                
                // Aggiorna lo store con i dati dell'utente
                if (response.data) {
                    const userData = response.data;
                    this.$store.state.userId = userData.id || '';
                    this.$store.state.userEmail = userData.email || '';
                    this.$store.state.userName = userData.username || '';
                    this.$store.state.tenantId = userData.tenantId || '';
                    this.$store.state.userPreferences = userData.userPreferences || { selectedCharts: [] };
                    this.$store.state.permissions = userData.permissions || { allowedAreas: '' };
                    const tz = userData.userPreferences?.timezone
                        ? normalizeTimezone(String(userData.userPreferences.timezone))
                        : 'Europe/Rome';
                    this.$store.state.timezone = tz;
                    
                    console.log('User preferences loaded:', this.$store.state.userPreferences);
                }
                
                const selectedCharts = this.$store.state.userPreferences?.selectedCharts || [];
                
                console.log('Loading widget configurations:', selectedCharts);
                
                if (Array.isArray(selectedCharts) && selectedCharts.length > 0) {
                    // Carica le configurazioni e ricarica i dati per ciascun widget
                    for (const config of selectedCharts) {
                        try {
                            // Imposta valori di default per il periodo se mancanti
                            const periodType = config.periodType || 'last_7_days';
                            // Use nullish coalescing to allow 0 but catch null/undefined
                            const periodDays = config.periodDays ?? (periodType === 'last_day' ? 1 : 7);
                            
                            // Calcola le date se non presenti
                            let periodStartDate = config.periodStartDate;
                            let periodEndDate = config.periodEndDate;
                            
                            if (!periodStartDate || !periodEndDate) {
                                const yesterday = new Date();
                                yesterday.setDate(yesterday.getDate() - 1);
                                periodEndDate = toBackendDate(yesterday);
                                
                                const startDate = new Date(yesterday);
                                startDate.setDate(startDate.getDate() - periodDays + 1);
                                periodStartDate = toBackendDate(startDate);
                            }
                            
                            // Ricostruisci il widget dalla configurazione salvata
                            const widgetData = {
                                id: config.id,
                                type: config.type,
                                title: config.title,
                                size: config.size,
                                kpiType: config.kpiType,
                                dataLevel: config.dataLevel,
                                entityId: config.entityId,
                                hierarchicalInfo: config.hierarchicalInfo,
                                entityNames: config.entityNames,
                                periodType: periodType,
                                periodDays: periodDays,
                                periodStartDate: periodStartDate,
                                periodEndDate: periodEndDate
                            };
                            
                            // Rigenera icon e color dal kpiType
                            const kpiTypeInfo = this.kpiTypes.find(k => k.value === config.kpiType);
                            if (kpiTypeInfo) {
                                widgetData.icon = kpiTypeInfo.icon;
                                widgetData.color = kpiTypeInfo.color;
                            }
                            
                            // Rigenera clientInfo, summary, tooltipDetails da hierarchicalInfo
                            const entityInfo = this.regenerateEntityInfo(config);
                            widgetData.clientInfo = entityInfo.clientInfo;
                            widgetData.summary = entityInfo.summary;
                            widgetData.tooltipDetails = entityInfo.tooltipDetails;

                            console.log(`Caricamento widget ${config.id} - ${config.title}`);
                            console.log('Widget config:', widgetData);

                            // Ricarica i dati del widget dal server
                            if (widgetData.kpiType === 'dispenser_fill_heatmap') {
                                // Se i dispenser reali non sono ancora salvati, li recuperiamo dal backend
                                if (!widgetData.dispensers || widgetData.dispensers.length === 0) {
                                    const realDispensers = await this.fetchAreaDispensers(widgetData.hierarchicalInfo);
                                    if (realDispensers && realDispensers.length > 0) {
                                        widgetData.dispensers = realDispensers;
                                    }
                                }
                                widgetData.heatmapData = this.generateDispenserFillHeatmapData(widgetData);
                                // period è già salvato dalla configurazione originale
                                widgetData.periodEnd = periodEndDate;
                            } else if (widgetData.kpiType === 'dispenser_consumption_analysis' && config.dispenserGroups?.length > 0) {
                                // Ripristina i gruppi direttamente dalle preferences salvate
                                // (evita la dipendenza da availableClients che non è ancora caricato)
                                widgetData.dispenserGroups = config.dispenserGroups;
                                widgetData.period = this.formatPeriodText(periodType, periodStartDate, periodEndDate);
                                widgetData.periodEnd = periodEndDate;
                            } else {
                                const kpiData = await this.fetchKpiData(widgetData);

                                if (widgetData.kpiType === 'dispenser_consumption_analysis') {
                                    widgetData.dispenserGroups = kpiData.groups || [];
                                    widgetData.period = this.formatPeriodText(periodType, periodStartDate, periodEndDate);
                                    widgetData.periodEnd = periodEndDate;
                                } else if (this.isTableKpi(widgetData.kpiType)) {
                                    widgetData.tableData = kpiData.tableData || [];
                                    widgetData.period = this.formatPeriodText(periodType, periodStartDate, periodEndDate);
                                    widgetData.periodEnd = periodEndDate;
                                    widgetData.sortBy = this.getDefaultSortBy(widgetData.kpiType);
                                } else if (this.isNumericKpi(widgetData.kpiType)) {
                                    widgetData.value = kpiData.value;
                                    widgetData.label = kpiData.label;
                                } else {
                                    widgetData.series = kpiData.series || [];
                                    widgetData.chartData = kpiData.chartData || {};
                                    console.log(`Widget ${config.id} series data:`, widgetData.series);
                                }
                            }

                            this.widgets.push(widgetData);
                            console.log(`Widget ${config.id} caricato con successo`);
                        } catch (widgetError) {
                            console.error(`Errore nel caricamento del widget ${config.id}:`, widgetError);
                            // Continua con il prossimo widget invece di interrompere tutto
                        }
                    }
                }

                console.log('Configurazioni widget caricate con successo');
                console.log('Ordine finale widget:', this.widgets.map(w => ({ id: w.id, title: w.title })));
            } catch (error) {
                console.error('Errore nel caricamento delle configurazioni dei widget:', error);
                // Non mostrare errore se è solo perché non ci sono configurazioni salvate
                if (error.response && error.response.status !== 404) {
                    console.warn('Impossibile caricare le configurazioni salvate');
                }
            } finally {
                this.overlay = false;
            }
        },

        resetForm() {
            this.selectedWidgetType = null;
            this.selectedChartTemplate = null;
            this.selectedChartType = null;
            this.selectedParameters = [];
            this.selectedKpiType = null;
            this.widgetTitle = '';
            this.widgetSize = 'medium';
            this.currentStep = 1;
            this.selectedComparisonLevel = null;
            this.selectedEntities = [];
            this.selectedPeriod = '';
            this.customDateRange = [];
            this.startDate = null;
            this.endDate = null;
            this.customDateMenu = false;
            this.loadingEntities = false;
            this.availableEntities = [];
            this.selectedEntity = null;
            // Reset data level selection
            this.selectedDataLevel = '';
            // Reset form validation
            this.isFormValid = false;
            // Reset hierarchical selection
            this.resetEntitySelections();
        },

        async saveWidget() {
            this.overlay = true;
            
            try {
                // Se editingWidgetIndex è impostato, stiamo modificando un widget esistente
                const isEditing = this.editingWidgetIndex !== null && this.editingWidgetIndex !== undefined;
                
                const widgetData = {
                    id: isEditing ? this.editingWidget.id : Date.now(),
                    type: this.selectedWidgetType,
                    title: this.widgetTitle,
                    size: this.widgetSize
                };

                // Calcola le informazioni del client
                let selectedEntityInfo;
                if (this.selectedWidgetType === 'kpi' && this.selectedKpiType === 'total_paper_consumption') {
                    selectedEntityInfo = this.getPaperConsumptionEntityInfo();
                } else {
                    selectedEntityInfo = this.getSelectedEntityInfo();
                }
                widgetData.clientInfo = selectedEntityInfo.clientInfo;
                widgetData.summary = selectedEntityInfo.summary;
                widgetData.tooltipDetails = selectedEntityInfo.tooltipDetails;
                
                // Aggiungi informazioni del periodo
                const periodInfo = this.getSelectedPeriodInfo();
                if (periodInfo) {
                    widgetData.periodType = periodInfo.type;
                    widgetData.periodDays = periodInfo.days;
                    widgetData.periodStartDate = periodInfo.startDate;
                    widgetData.periodEndDate = periodInfo.endDate;
                }
                
                // Aggiungi icona e colore del KPI
                const selectedKpi = this.kpiTypes.find(k => k.value === this.selectedKpiType);
                if (selectedKpi) {
                    widgetData.icon = selectedKpi.icon;
                    widgetData.color = selectedKpi.color;
                }

                if (this.selectedWidgetType === 'kpi') {
                    widgetData.kpiType = this.selectedKpiType;
                    widgetData.dataLevel = this.selectedDataLevel;
                    widgetData.entityId = this.getSelectedEntityId();
                    
                    // Salva informazioni gerarchiche per la ricostruzione durante l'editing
                    widgetData.hierarchicalInfo = this.getHierarchicalInfo();
                    
                    // Salva anche i nomi testuali per le chiamate API
                    widgetData.entityNames = this.getEntityNames();
                    
                    if (this.selectedKpiType === 'dispenser_fill_heatmap') {
                        // Usa i dispenser già caricati da onHierarchicalAreaChange (nessuna chiamata extra)
                        if (this.availableSensorsFullData && this.availableSensorsFullData.length > 0) {
                            widgetData.dispensers = [...this.availableSensorsFullData];
                        }
                        widgetData.heatmapData = this.generateDispenserFillHeatmapData(widgetData);
                        widgetData.period = this.getPeriodDisplayText();
                        widgetData.periodEnd = periodInfo ? periodInfo.endDate : toBackendDateTime(new Date());
                    } else {
                        const kpiData = await this.fetchKpiData(widgetData);
                        
                        if (this.selectedKpiType === 'dispenser_consumption_analysis') {
                            widgetData.dispenserGroups = kpiData.groups || [];
                            widgetData.period = this.getPeriodDisplayText();
                            widgetData.periodEnd = periodInfo ? periodInfo.endDate : toBackendDateTime(new Date());
                        } else if (this.isTableKpi(this.selectedKpiType)) {
                            widgetData.tableData = kpiData.tableData || [];
                            widgetData.period = this.getPeriodDisplayText();
                            widgetData.periodEnd = periodInfo ? periodInfo.endDate : toBackendDateTime(new Date());
                            widgetData.sortBy = this.getDefaultSortBy(this.selectedKpiType);
                        } else if (this.isNumericKpi(this.selectedKpiType)) {
                            widgetData.value = kpiData.value;
                            widgetData.label = kpiData.label;
                        } else {
                            widgetData.series = kpiData.series || [];
                            widgetData.chartData = kpiData.chartData || {};
                        }
                    }
                }

                // Salva la configurazione del widget tramite API (passa indice in modifica per fallback)
                await this.saveWidgetConfiguration(
                    widgetData,
                    isEditing && this.editingWidgetIndex !== null ? this.editingWidgetIndex : null
                );

                // Se stiamo modificando, aggiorna il widget esistente, altrimenti aggiungi nuovo
                if (isEditing && this.editingWidgetIndex !== null) {
                    this.$set(this.widgets, this.editingWidgetIndex, widgetData);
                    console.log('Widget modificato con successo');
                } else {
                    this.widgets.push(widgetData);
                    console.log('Nuovo widget creato con successo');
                }

                this.widgetDialog = false;
                this.resetForm();
                this.editingWidgetIndex = null; // Reset dell'indice di modifica
            } catch (error) {
                console.error('Errore nel salvataggio del widget:', error);
                // Mostra un messaggio di errore all'utente
                alert(this.$t('common.error') || 'Errore nel salvataggio del widget');
            } finally {
                this.overlay = false;
            }
        },

        compactHierarchicalInfo(hierarchicalInfo) {
            // Salva solo le selezioni effettivamente utilizzate (compatto)
            if (!hierarchicalInfo) return null;
            
            const compact = {
                dataLevel: hierarchicalInfo.dataLevel
            };
            
            // Salva solo le selezioni singole (non null/undefined)
            if (hierarchicalInfo.selectedClient) compact.selectedClient = hierarchicalInfo.selectedClient;
            if (hierarchicalInfo.selectedBuilding) compact.selectedBuilding = hierarchicalInfo.selectedBuilding;
            if (hierarchicalInfo.selectedFloor) compact.selectedFloor = hierarchicalInfo.selectedFloor;
            if (hierarchicalInfo.selectedArea) compact.selectedArea = hierarchicalInfo.selectedArea;
            
            // Salva solo gli array non vuoti
            if (hierarchicalInfo.selectedClients?.length > 0) compact.selectedClients = hierarchicalInfo.selectedClients;
            if (hierarchicalInfo.selectedBuildings?.length > 0) compact.selectedBuildings = hierarchicalInfo.selectedBuildings;
            if (hierarchicalInfo.selectedFloors?.length > 0) compact.selectedFloors = hierarchicalInfo.selectedFloors;
            if (hierarchicalInfo.selectedAreas?.length > 0) compact.selectedAreas = hierarchicalInfo.selectedAreas;
            if (hierarchicalInfo.selectedSensors?.length > 0) compact.selectedSensors = hierarchicalInfo.selectedSensors;
            
            return compact;
        },

        compactEntityNames(entityNames) {
            // Salva solo i devEui necessari per le chiamate API (compatto)
            if (!entityNames) return null;
            
            const compact = {};
            
            // Per i sensori, salva SOLO i devEui (indispensabili per le API)
            if (entityNames.sensorsDevEui?.length > 0) {
                compact.sensorsDevEui = entityNames.sensorsDevEui;
            }
            
            // Per altri livelli, salva solo i nomi essenziali (non gli array completi)
            if (entityNames.client) compact.client = entityNames.client;
            if (entityNames.building) compact.building = entityNames.building;
            if (entityNames.floor) compact.floor = entityNames.floor;
            if (entityNames.area) compact.area = entityNames.area;
            
            return Object.keys(compact).length > 0 ? compact : null;
        },

        regenerateEntityInfo(config) {
            // Rigenera clientInfo, summary e tooltipDetails da hierarchicalInfo salvati
            const clientNames = [];
            const details = [];
            const counts = {
                clients: 0,
                buildings: 0,
                floors: 0,
                areas: 0,
                sensors: 0
            };
            
            const info = config.hierarchicalInfo || {};
            
            // Calcola i conteggi dalle selezioni salvate
            if (info.selectedClients?.length > 0) counts.clients = info.selectedClients.length;
            else if (info.selectedClient) counts.clients = 1;
            
            if (info.selectedBuildings?.length > 0) counts.buildings = info.selectedBuildings.length;
            else if (info.selectedBuilding) counts.buildings = 1;
            
            if (info.selectedFloors?.length > 0) counts.floors = info.selectedFloors.length;
            else if (info.selectedFloor) counts.floors = 1;
            
            if (info.selectedAreas?.length > 0) counts.areas = info.selectedAreas.length;
            else if (info.selectedArea) counts.areas = 1;
            
            if (info.selectedSensors?.length > 0) counts.sensors = info.selectedSensors.length;
            
            // Usa i nomi salvati in entityNames (se disponibili)
            if (config.entityNames?.client) {
                clientNames.push(config.entityNames.client);
            }
            
            // Calcola clientInfo
            let clientInfo = '';
            if (counts.clients === 1 && clientNames.length > 0) {
                clientInfo = clientNames[0];
            } else if (counts.clients > 1) {
                clientInfo = `${counts.clients} Clients selected`;
            }
            
            // Calcola summary
            const summaryParts = [];
            if (counts.clients > 1) summaryParts.push(`${counts.clients} Clients`);
            if (counts.buildings > 0) summaryParts.push(`${counts.buildings} Buildings`);
            if (counts.floors > 0) summaryParts.push(`${counts.floors} Floors`);
            if (counts.areas > 0) summaryParts.push(`${counts.areas} Areas`);
            if (counts.sensors > 0) summaryParts.push(`${counts.sensors} Sensors`);
            
            const summary = summaryParts.length > 0 ? summaryParts.join(' - ') + ' selected' : '';
            
            // Calcola tooltipDetails semplificato
            if (config.entityNames?.client) details.push(`Client: ${config.entityNames.client}`);
            if (config.entityNames?.building) details.push(`Building: ${config.entityNames.building}`);
            if (config.entityNames?.floor) details.push(`Floor: ${config.entityNames.floor}`);
            if (config.entityNames?.area) details.push(`Area: ${config.entityNames.area}`);
            if (counts.sensors > 0) details.push(`Sensors: ${counts.sensors} selected`);
            
            return {
                clientInfo,
                summary,
                tooltipDetails: details.join('\n') || 'Widget configuration'
            };
        },

        async saveWidgetConfiguration(widgetData, editIndex = null) {
            try {
                const token = localStorage.getItem('jwtToken');
                
                // Prepara la configurazione COMPATTA da salvare (solo dati essenziali)
                const configurationToSave = {
                    id: widgetData.id,
                    type: widgetData.type,
                    title: widgetData.title,
                    size: widgetData.size,
                    kpiType: widgetData.kpiType,
                    dataLevel: widgetData.dataLevel,
                    entityId: widgetData.entityId,
                    // Salva solo le selezioni necessarie della gerarchia (compatto)
                    hierarchicalInfo: this.compactHierarchicalInfo(widgetData.hierarchicalInfo),
                    // Salva solo i devEui necessari per le chiamate API (compatto)
                    entityNames: this.compactEntityNames(widgetData.entityNames),
                    periodType: widgetData.periodType,
                    periodDays: widgetData.periodDays,
                    periodStartDate: widgetData.periodStartDate,
                    periodEndDate: widgetData.periodEndDate
                    // Rimossi: icon, color (rigenerati da kpiType al caricamento)
                    // Rimossi: clientInfo, summary, tooltipDetails (rigenerati da hierarchicalInfo al caricamento)
                };

                // Per i widget con dati mock pre-calcolati, li salviamo nelle preferences
                // così sono disponibili immediatamente al reload senza dipendere da availableClients
                if (widgetData.kpiType === 'dispenser_consumption_analysis' && widgetData.dispenserGroups) {
                    configurationToSave.dispenserGroups = widgetData.dispenserGroups;
                }

                // Leggi tutte le preferenze attuali dal server per non perdere altri dati
                const getUserPreferencesUrl = this.$store.state.BASE_URL + this.$store.state.URL_GET_USER_PREFERENCES;
                const preferencesResponse = await axios.get(getUserPreferencesUrl, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                // Estrai tutte le preferenze esistenti
                // La risposta di /v1/users/me contiene userPreferences come oggetto root
                const allPreferences = preferencesResponse.data?.userPreferences || {};
                const currentCharts = allPreferences.selectedCharts || [];
                
                console.log('Current charts before save:', currentCharts);
                console.log('Saving widget with id:', widgetData.id);
                
                // Aggiorna o aggiungi il widget nelle preferenze
                // Confronto robusto id (numero vs stringa da JSON/backend) per evitare duplicati in modifica
                let existingIndex = currentCharts.findIndex(
                    chart => chart.id == widgetData.id || String(chart.id) === String(widgetData.id)
                );
                // Fallback in modifica: se l'id non è trovato (es. tipo diverso dal server), aggiorna per indice
                if (existingIndex === -1 && editIndex !== null && editIndex >= 0 && editIndex < currentCharts.length) {
                    existingIndex = editIndex;
                    console.log('Fallback: aggiornamento widget per indice', editIndex);
                }
                let updatedCharts;
                
                if (existingIndex !== -1) {
                    // Aggiorna il widget esistente - mantieni TUTTI gli altri
                    updatedCharts = [...currentCharts];
                    updatedCharts[existingIndex] = configurationToSave;
                    console.log('Widget aggiornato all\'indice:', existingIndex);
                } else {
                    // Aggiungi nuovo widget - mantieni TUTTI quelli esistenti
                    updatedCharts = [...currentCharts, configurationToSave];
                    console.log('Nuovo widget aggiunto. Totale widgets:', updatedCharts.length);
                }

                // Prepara tutte le preferenze da inviare (mantieni tutto e modifica solo selectedCharts)
                const updatedPreferences = {
                    ...allPreferences,
                    selectedCharts: updatedCharts
                };
                
                console.log('Updated charts to save:', updatedCharts.length, 'widgets');
                
                // Calcola la dimensione del payload
                const payloadSize = new Blob([JSON.stringify({ userPreferences: updatedPreferences })]).size;
                console.log('Payload size:', payloadSize, 'bytes', '(limit: 1048576 bytes / 1 MB)');
                
                if (payloadSize > 1024 * 1024) {
                    console.warn('ATTENZIONE: Payload supera 1MB!', payloadSize, 'bytes');
                }

                const patchUrl = this.$store.state.BASE_URL + this.$store.state.URL_PATCH_USER_PREFERENCES;
                
                // La PATCH richiede la struttura userPreferences: { selectedCharts: [...] }
                await axios.patch(patchUrl, {
                    userPreferences: updatedPreferences
                }, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });

                // Aggiorna lo store locale con le nuove preferenze complete
                this.$store.state.userPreferences = updatedPreferences;

                console.log('Configurazione widget salvata con successo');
            } catch (error) {
                console.error('Errore nel salvataggio della configurazione del widget:', error);
                throw error;
            }
        },

        getEntityNames() {
            // Salva i nomi testuali delle entità selezionate per le chiamate API
            const names = {};

            if (this.selectedClient) {
                const client = this.availableClients.find(c => c.value === this.selectedClient);
                if (client) names.client = client.text;
            }

            if (this.selectedBuilding) {
                const building = this.availableBuildings.find(b => b.value === this.selectedBuilding);
                if (building) names.building = building.text;
            }

            if (this.selectedFloor) {
                const floor = this.availableFloors.find(f => f.value === this.selectedFloor);
                if (floor) names.floor = floor.text;
            }

            if (this.selectedArea) {
                const area = this.availableAreas.find(a => a.value === this.selectedArea);
                if (area) names.area = area.text;
            }

            // Per le selezioni multiple, salva array di nomi
            if (this.selectedBuildings && this.selectedBuildings.length > 0) {
                names.buildings = this.selectedBuildings.map(id => {
                    const building = this.availableBuildings.find(b => b.value === id);
                    return building ? building.text : null;
                }).filter(name => name !== null);
            }

            if (this.selectedFloors && this.selectedFloors.length > 0) {
                names.floors = this.selectedFloors.map(id => {
                    const floor = this.availableFloors.find(f => f.value === id);
                    return floor ? floor.text : null;
                }).filter(name => name !== null);
            }

            if (this.selectedAreas && this.selectedAreas.length > 0) {
                names.areas = this.selectedAreas.map(id => {
                    const area = this.availableAreas.find(a => a.value === id);
                    return area ? area.text : null;
                }).filter(name => name !== null);
            }

            if (this.selectedSensors && this.selectedSensors.length > 0) {
                names.sensors = this.selectedSensors.map(id => {
                    const sensor = this.availableSensors.find(s => s.value === id);
                    return sensor ? sensor.text : null;
                }).filter(name => name !== null);
                
                // Salva anche i devEui per le chiamate API
                names.sensorsDevEui = this.selectedSensors.map(id => {
                    const sensor = this.availableSensors.find(s => s.value === id);
                    return sensor ? sensor.devEui : null;
                }).filter(devEui => devEui !== null);
            }

            return names;
        },

        getSelectedEntityInfo() {
            const clientNames = [];
            const details = [];
            const counts = {
                clients: 0,
                buildings: 0,
                floors: 0,
                areas: 0,
                sensors: 0
            };

            // Calcola le informazioni basate sulle selezioni effettive
            switch (this.selectedDataLevel) {
                case 'client': {
                    counts.clients = this.selectedClients?.length || 0;
                    if (counts.clients > 0) {
                        this.selectedClients.forEach(clientId => {
                            const client = this.availableClients.find(c => c.value === clientId);
                            if (client) clientNames.push(client.text);
                        });
                        details.push(`Clients: ${clientNames.join(', ')}`);
                    }
                    break;
                }
                case 'building': {
                    counts.buildings = this.selectedBuildings?.length || 0;
                    
                    // Get client name (single selection)
                    if (this.selectedClient) {
                        const client = this.availableClients.find(c => c.value === this.selectedClient);
                        if (client) {
                            clientNames.push(client.text);
                            counts.clients = 1;
                        }
                    }
                    
                    // Get building names (multiple selection)
                    const buildingNames = [];
                    this.selectedBuildings.forEach(buildingId => {
                        const building = this.availableBuildings.find(b => b.value === buildingId);
                        if (building) buildingNames.push(building.text);
                    });
                    
                    if (clientNames.length > 0) details.push(`Client: ${clientNames[0]}`);
                    if (buildingNames.length > 0) details.push(`Buildings: ${buildingNames.join(', ')}`);
                    break;
                }
                case 'floor': {
                    counts.floors = this.selectedFloors?.length || 0;
                    
                    // Get hierarchy info (all single selections until floors)
                    if (this.selectedClient) {
                        const client = this.availableClients.find(c => c.value === this.selectedClient);
                        if (client) {
                            clientNames.push(client.text);
                            counts.clients = 1;
                        }
                    }
                    
                    let buildingName = '';
                    if (this.selectedBuilding) {
                        const building = this.availableBuildings.find(b => b.value === this.selectedBuilding);
                        if (building) {
                            buildingName = building.text;
                            counts.buildings = 1;
                        }
                    }
                    
                    // Get floor names (multiple selection)
                    const floorNames = [];
                    this.selectedFloors.forEach(floorId => {
                        const floor = this.availableFloors.find(f => f.value === floorId);
                        if (floor) floorNames.push(floor.text);
                    });
                    
                    if (clientNames.length > 0) details.push(`Client: ${clientNames[0]}`);
                    if (buildingName) details.push(`Building: ${buildingName}`);
                    if (floorNames.length > 0) details.push(`Floors: ${floorNames.join(', ')}`);
                    break;
                }
                case 'area': {
                    counts.areas = this.selectedAreas?.length || 0;
                    
                    // Get hierarchy info (all single selections until areas)
                    if (this.selectedClient) {
                        const client = this.availableClients.find(c => c.value === this.selectedClient);
                        if (client) {
                            clientNames.push(client.text);
                            counts.clients = 1;
                        }
                    }
                    
                    let buildingName = '';
                    if (this.selectedBuilding) {
                        const building = this.availableBuildings.find(b => b.value === this.selectedBuilding);
                        if (building) buildingName = building.text;
                    }
                    
                    let floorName = '';
                    if (this.selectedFloor) {
                        const floor = this.availableFloors.find(f => f.value === this.selectedFloor);
                        if (floor) floorName = floor.text;
                    }
                    
                    // Get area names (multiple selection)
                    const areaNames = [];
                    this.selectedAreas.forEach(areaId => {
                        const area = this.availableAreas.find(a => a.value === areaId);
                        if (area) areaNames.push(area.text);
                    });
                    
                    if (clientNames.length > 0) details.push(`Client: ${clientNames[0]}`);
                    if (buildingName) details.push(`Building: ${buildingName}`);
                    if (floorName) details.push(`Floor: ${floorName}`);
                    if (areaNames.length > 0) details.push(`Areas: ${areaNames.join(', ')}`);
                    break;
                }
                case 'sensor': {
                    counts.sensors = this.selectedSensors?.length || 0;
                    
                    // Get hierarchy info (all single selections until sensors)
                    if (this.selectedClient) {
                        const client = this.availableClients.find(c => c.value === this.selectedClient);
                        if (client) {
                            clientNames.push(client.text);
                            counts.clients = 1;
                        }
                    }
                    
                    let buildingName = '';
                    if (this.selectedBuilding) {
                        const building = this.availableBuildings.find(b => b.value === this.selectedBuilding);
                        if (building) buildingName = building.text;
                    }
                    
                    let floorName = '';
                    if (this.selectedFloor) {
                        const floor = this.availableFloors.find(f => f.value === this.selectedFloor);
                        if (floor) floorName = floor.text;
                    }
                    
                    let areaName = '';
                    if (this.selectedArea) {
                        const area = this.availableAreas.find(a => a.value === this.selectedArea);
                        if (area) areaName = area.text;
                    }
                    
                    // Get sensor names (multiple selection)
                    const sensorNames = [];
                    this.selectedSensors.forEach(sensorId => {
                        const sensor = this.availableSensors.find(s => s.value === sensorId);
                        if (sensor) sensorNames.push(sensor.text);
                    });
                    
                    if (clientNames.length > 0) details.push(`Client: ${clientNames[0]}`);
                    if (buildingName) details.push(`Building: ${buildingName}`);
                    if (floorName) details.push(`Floor: ${floorName}`);
                    if (areaName) details.push(`Area: ${areaName}`);
                    if (sensorNames.length > 0) details.push(`Sensors: ${sensorNames.join(', ')}`);
                    break;
                }
            }

            // Calcola clientInfo e summary intelligenti
            let clientInfo = '';
            let summary = '';

            if (counts.clients === 1) {
                clientInfo = clientNames[0] || '';
            } else if (counts.clients > 1) {
                clientInfo = `${counts.clients} Clients selected`;
            }

            // Crea summary basato sui conteggi
            const summaryParts = [];
            if (counts.clients > 1) summaryParts.push(`${counts.clients} Clients`);
            if (counts.buildings > 0) summaryParts.push(`${counts.buildings} Buildings`);
            if (counts.floors > 0) summaryParts.push(`${counts.floors} Floors`);
            if (counts.areas > 0) summaryParts.push(`${counts.areas} Areas`);
            if (counts.sensors > 0) summaryParts.push(`${counts.sensors} Sensors`);
            
            summary = summaryParts.join(' - ') + ' selected';

            return {
                clientInfo,
                summary,
                tooltipDetails: details.join('\n') || 'Configurazione widget KPI'
            };
        },

        getSelectedEntityId() {
            switch (this.selectedDataLevel) {
                case 'client': return this.selectedClients; // Array - multiple clients
                case 'building': return this.selectedBuildings; // Array - multiple buildings
                case 'floor': return this.selectedFloors; // Array - multiple floors
                case 'area': return this.selectedAreas; // Array - multiple areas
                case 'sensor': return this.selectedSensors; // Array - multiple sensors
                default: return null;
            }
        },

        checkIsYoutissueAdmin() {
            // Legge prima dallo store, poi da localStorage come fallback
            let userRole = this.$store.state.userRole;
            if (!userRole || (Array.isArray(userRole) && userRole.length === 0) || userRole === '') {
                try {
                    const saved = localStorage.getItem('userRole');
                    if (saved) userRole = JSON.parse(saved);
                } catch (e) { /* ignore */ }
            }
            if (Array.isArray(userRole)) {
                return userRole.some(r => {
                    const name = (r.Name || r.name || '').toLowerCase();
                    return name === 'youtissue admin';
                });
            }
            if (typeof userRole === 'string') {
                return userRole.toLowerCase().includes('youtissue admin');
            }
            return false;
        },

        heatmapCellColor(emptyLevel) {
            // emptyLevel: 0 = full (chiaro), 100 = empty (scuro)
            const opacity = 0.08 + (emptyLevel / 100) * 0.92;
            return `rgba(33, 58, 143, ${opacity.toFixed(2)})`;
        },

        generateDispenserFillHeatmapData(widget) {
            const areaName     = widget.entityNames?.area     || 'Main Washroom';
            const clientName   = widget.entityNames?.client   || 'YouTissue';
            const buildingName = widget.entityNames?.building || 'Headquarters';
            const floorName    = widget.entityNames?.floor    || 'Ground Floor';

            // Tabella di lookup: fills esatti per dispenser noti
            // Slot: 00-04 | 04-08 | 08-12 | 12-16 | 16-20 | 20-24
            const FILL_OVERRIDES = {
                // ── Marplast ──────────────────────────────────────────────
                'Autocut Skin 884 M':    [64, 60, 83, 70, 58, 50],
                'Autocut Skin 884 F':    [32, 26, 88, 44, 19, 10],
                'Midi Jumbo Skin 907 M': [72, 68, 86, 79, 71, 66],
                'Midi Jumbo Skin 907 F': [28, 22, 84, 38, 14,  8],
                'Skin Soap 924 M':       [58, 54, 72, 69, 61, 56],
                'Skin Soap 924 F':       [ 7,  5, 18, 78, 63, 55],
                // ── Medial / Customer 1 ───────────────────────────────────
                'Stilevo 920020 Z-C-V M': [68, 64, 79, 72, 61, 54],
                'Stilevo 920020 Z-C-V F': [42, 36, 84, 48, 18,  9],
                'Stilevo 920041 Soap F':  [14, 10, 26, 78, 66, 58],
                'Stilevo 920041 Soap M':  [57, 53, 71, 67, 60, 55],
                'Stilevo 920002 Jumbo M': [61, 57, 76, 63, 29, 16],
                'Stilevo 920002 Jumbo F': [66, 62, 81, 72, 60, 52],
            };

            const buildLocation = (area) => `${clientName} > ${buildingName} > ${floorName} > ${area}`;

            // Usa i dispenser reali salvati nel widget, altrimenti fallback Marplast
            const realDispensers = widget.dispensers;
            let dispenserList;

            if (realDispensers && realDispensers.length > 0) {
                dispenserList = realDispensers.map((d, i) => ({
                    id:       d.id || i + 1,
                    name:     d.name,
                    type:     d.type,
                    location: buildLocation(areaName),
                    // Se il nome corrisponde alla lookup, usa i valori esatti; altrimenti random
                    pattern:  FILL_OVERRIDES[d.name]
                              || Array.from({ length: 6 }, () => Math.round(20 + Math.random() * 70)),
                }));
            } else {
                // Fallback: dataset completo in base al client
                const cn = clientName.toLowerCase();
                const isMedialFallback = cn.includes('medial') || cn.includes('customer 1');
                if (isMedialFallback) {
                    dispenserList = [
                        { id: 1, name: 'Stilevo 920020 Z-C-V M', type: 'Folded Towel Dispenser', location: buildLocation('Men'),   pattern: FILL_OVERRIDES['Stilevo 920020 Z-C-V M'] },
                        { id: 2, name: 'Stilevo 920020 Z-C-V F', type: 'Folded Towel Dispenser', location: buildLocation('Women'), pattern: FILL_OVERRIDES['Stilevo 920020 Z-C-V F'] },
                        { id: 3, name: 'Stilevo 920041 Soap F',  type: 'Soap Dispenser',         location: buildLocation('Women'), pattern: FILL_OVERRIDES['Stilevo 920041 Soap F'] },
                        { id: 4, name: 'Stilevo 920041 Soap M',  type: 'Soap Dispenser',         location: buildLocation('Men'),   pattern: FILL_OVERRIDES['Stilevo 920041 Soap M'] },
                        { id: 5, name: 'Stilevo 920002 Jumbo M', type: 'Toilet Paper Roll',      location: buildLocation('Men'),   pattern: FILL_OVERRIDES['Stilevo 920002 Jumbo M'] },
                        { id: 6, name: 'Stilevo 920002 Jumbo F', type: 'Toilet Paper Roll',      location: buildLocation('Women'), pattern: FILL_OVERRIDES['Stilevo 920002 Jumbo F'] },
                    ];
                } else {
                    // Fallback Marplast (default)
                    dispenserList = [
                        { id: 1, name: 'Autocut Skin 884 M',    type: 'Paper rolls Dispenser',       location: buildLocation('Men'),   pattern: FILL_OVERRIDES['Autocut Skin 884 M'] },
                        { id: 2, name: 'Autocut Skin 884 F',    type: 'Paper rolls Dispenser',       location: buildLocation('Women'), pattern: FILL_OVERRIDES['Autocut Skin 884 F'] },
                        { id: 3, name: 'Midi Jumbo Skin 907 M', type: 'Toilet Paper Roll Dispenser', location: buildLocation('Men'),   pattern: FILL_OVERRIDES['Midi Jumbo Skin 907 M'] },
                        { id: 4, name: 'Midi Jumbo Skin 907 F', type: 'Toilet Paper Roll Dispenser', location: buildLocation('Women'), pattern: FILL_OVERRIDES['Midi Jumbo Skin 907 F'] },
                        { id: 5, name: 'Skin Soap 924 M',       type: 'Soap Dispenser',              location: buildLocation('Men'),   pattern: FILL_OVERRIDES['Skin Soap 924 M'] },
                        { id: 6, name: 'Skin Soap 924 F',       type: 'Soap Dispenser',              location: buildLocation('Women'), pattern: FILL_OVERRIDES['Skin Soap 924 F'] },
                    ];
                }
            }

            return dispenserList.map(d => ({
                id:       d.id,
                name:     d.name,
                type:     d.type || '',
                location: d.location,
                fills:    d.pattern,
            }));
        },

        async fetchAreaDispensers(hierarchicalInfo) {
            if (!hierarchicalInfo) return [];
            const areaId = hierarchicalInfo.selectedArea;
            if (!areaId) return [];
            try {
                const res = await axios.post(
                    this.$store.state.URL_GET_SENSORS_COMPANY_BUILDING_FLOOR_AREA,
                    {
                        companyId: hierarchicalInfo.selectedClient || '',
                        buildingId: hierarchicalInfo.selectedBuilding || '',
                        floorId: hierarchicalInfo.selectedFloor || '',
                        areaId: areaId,
                        type: 'dispenser'
                    }
                );
                if (!res.data || !Array.isArray(res.data)) return [];
                return res.data.map(el => ({
                    id: el.id,
                    name: el.DisplayName || el.DispenserUnique || el.id,
                    type: el.DispenserType || ''
                }));
            } catch (err) {
                console.warn('fetchAreaDispensers error:', err);
                return [];
            }
        },

        isNumericKpi(kpiType) {
            const numericKpis = [
                'refill_frequency_avg_day',
                'restroom_daily_usage_avg', 
                'alert_activity_avg_day'
            ];
            return numericKpis.includes(kpiType);
        },

        isTableKpi(kpiType) {
            const tableKpis = [
                'total_paper_consumption',
                'refill_frequency_avg_day',
                'restroom_daily_usage_avg',
                'alert_activity_avg_day'
            ];
            return tableKpis.includes(kpiType);
        },

        isChartKpi(kpiType) {
            const chartKpis = [
                'consumption_comparison',
                'usage_frequency_comparison',
                'refill_frequency_comparison',
                'heatmap_event_distribution',
                'consumable_cost_analysis'
            ];
            return chartKpis.includes(kpiType);
        },

        getTableHeaders(widget) {
            // Headers specifici per tipo di KPI
            switch (widget.kpiType) {
                case 'total_paper_consumption': {
                    // Determina se mostrare "Device" o "Article" basato sul dataLevel
                    let firstColumnText = this.$t('dashboard.tableHeaders.article');
                    if (widget.dataLevel && widget.dataLevel !== 'client') {
                        // Se il dataLevel è diverso da client, mostra "Device/Location"
                        firstColumnText = this.getFirstColumnName(widget.dataLevel);
                    }
                    
                    return [
                        {
                            text: firstColumnText,
                            value: 'name',
                            align: 'start',
                            sortable: true
                        },
                        {
                            text: this.$t('dashboard.tableHeaders.packsUsed'),
                            value: 'packsUsed',
                            align: 'center',
                            sortable: true
                        },
                        {
                            text: this.$t('dashboard.tableHeaders.trendVsPrevious'),
                            value: 'trend',
                            align: 'center',
                            sortable: true
                        }
                    ];
                }
                
                case 'refill_frequency_avg_day':
                    return [
                        {
                            text: this.$t('dashboard.tableHeaders.location'),
                            value: 'location',
                            align: 'start',
                            sortable: true
                        },
                        {
                            text: this.$t('dashboard.tableHeaders.refillsPerDay'),
                            value: 'refillsPerDay',
                            align: 'center',
                            sortable: true
                        },
                        {
                            text: this.$t('dashboard.tableHeaders.deltaVsPrevious'),
                            value: 'trend',
                            align: 'center',
                            sortable: true
                        }
                    ];
                
                case 'restroom_daily_usage_avg':
                    return [
                        {
                            text: this.$t('dashboard.tableHeaders.restroomAndLocation'),
                            value: 'deviceName',
                            align: 'start',
                            sortable: true,
                            width: '30%'
                        },
                        {
                            text: this.$t('dashboard.tableHeaders.avgEntriesPerDay'),
                            value: 'avgEntries',
                            align: 'center',
                            sortable: true,
                            width: '15%'
                        },
                        {
                            text: this.$t('dashboard.tableHeaders.dailyPattern'),
                            value: 'dailyPattern',
                            align: 'center',
                            sortable: false,
                            width: '35%'
                        },
                        {
                            text: this.$t('dashboard.tableHeaders.trendVsLastWeek'),
                            value: 'trend',
                            align: 'center',
                            sortable: true,
                            width: '20%'
                        }
                    ];
                
                case 'alert_activity_avg_day':
                    return [
                        {
                            text: this.getFirstColumnName(widget.dataLevel),
                            value: 'name',
                            align: 'start',
                            sortable: true
                        },
                        {
                            text: this.$t('dashboard.tableHeaders.location'),
                            value: 'location',
                            align: 'start',
                            sortable: true
                        },
                        {
                            text: this.$t('dashboard.tableHeaders.avgAlertsPerDay'),
                            value: 'avgAlerts',
                            align: 'center',
                            sortable: true
                        },
                        {
                            text: this.$t('dashboard.tableHeaders.deltaVsPrevious'),
                            value: 'trend',
                            align: 'center',
                            sortable: true
                        }
                    ];
                
                default:
                    return [
                        {
                            text: this.getFirstColumnName(widget.dataLevel),
                            value: 'name',
                            align: 'start',
                            sortable: true
                        },
                        {
                            text: this.$t('dashboard.tableHeaders.value'),
                            value: 'value',
                            align: 'center',
                            sortable: true
                        },
                        {
                            text: this.$t('dashboard.tableHeaders.trendVsPrevious'),
                            value: 'trend',
                            align: 'center',
                            sortable: true
                        }
                    ];
            }
        },

        getDefaultSortBy(kpiType) {
            switch (kpiType) {
                case 'total_paper_consumption':
                    return 'packsUsed';
                case 'refill_frequency_avg_day':
                    return 'refillsPerDay';
                case 'restroom_daily_usage_avg':
                    return 'avgEntries';
                case 'alert_activity_avg_day':
                    return 'avgAlerts';
                case 'dispenser_consumption_analysis':
                    return null;
                default:
                    return 'value';
            }
        },

        getFirstColumnName(dataLevel) {
            const columnNames = {
                'client': this.$t('dashboard.tableHeaders.client'),
                'building': this.$t('dashboard.tableHeaders.building'),
                'floor': this.$t('dashboard.tableHeaders.floor'),
                'area': this.$t('dashboard.tableHeaders.area'),
                'sensor': this.$t('dashboard.tableHeaders.dispenser'),
                'article': this.$t('dashboard.tableHeaders.article')
            };
            return columnNames[dataLevel] || this.$t('dashboard.tableHeaders.item');
        },

        getTrendClass(trend) {
            return {
                'trend-positive': trend > 0,
                'trend-negative': trend < 0,
                'trend-neutral': trend === 0
            };
        },

        getTrendColor(trend) {
            if (trend > 0) return '#4CAF50';
            if (trend < 0) return '#F44336';
            return '#9E9E9E';
        },

        getTrendIcon(trend) {
            if (trend > 0) return 'mdi-trending-up';
            if (trend < 0) return 'mdi-trending-down';
            return 'mdi-trending-neutral';
        },

        getStatusColor(status) {
            switch (status?.toLowerCase()) {
                case 'low':
                    return '#FFA726'; // Orange
                case 'empty':
                    return '#EF5350'; // Red
                case 'normal':
                case 'active':
                    return '#66BB6A'; // Green
                default:
                    return '#9E9E9E'; // Grey
            }
        },

        getStatusIcon(status) {
            switch (status?.toLowerCase()) {
                case 'low':
                    return 'mdi-alert-triangle';
                case 'empty':
                    return 'mdi-alert-circle';
                case 'normal':
                case 'active':
                    return 'mdi-check-circle';
                default:
                    return 'mdi-help-circle';
            }
        },

        formatDate(dateString) {
            if (!dateString) return '';
            const tz = this.$store.state.timezone || 'Europe/Rome';
            return formatDateDisplay(dateString, tz);
        },

        getChartPeriodSubtitle(widget) {
            // Genera il sottotitolo del periodo per i grafici in base alla lingua
            const locale = this.$i18n.locale || 'en';
            const periodType = widget.periodType || 'last_7_days';
            
            if (periodType === 'custom' && widget.periodStartDate && widget.periodEndDate) {
                return this.formatCustomPeriodSubtitle(widget.periodStartDate, widget.periodEndDate, locale);
            }
            
            // Per periodi predefiniti
            return this.formatPredefinedPeriodSubtitle(periodType, widget.periodEndDate || this.getYesterday(), locale);
        },

        formatCustomPeriodSubtitle(startDate, endDate, locale) {
            const start = new Date(startDate);
            const end = new Date(endDate);
            const diffDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
            
            // Controlla se è lo stesso giorno (giorno singolo)
            if (startDate === endDate) {
                return this.formatSingleDaySubtitle(end, locale);
            }
            
            // Multi-day period
            return this.formatMultiDaySubtitle(start, end, diffDays, locale);
        },

        formatSingleDaySubtitle(date, locale) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const compareDate = new Date(date);
            compareDate.setHours(0, 0, 0, 0);
            const isToday = compareDate.getTime() === today.getTime();
            
            const monthNames = {
                en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
                it: ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'],
                de: ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez']
            };
            
            const month = monthNames[locale][date.getMonth()];
            const day = date.getDate().toString().padStart(2, '0');
            const year = date.getFullYear();
            
            switch (locale) {
                case 'it':
                    return `Data: ${day} ${month} ${year}${isToday ? ' • Oggi' : ''}`;
                case 'de':
                    return `Datum: ${day}. ${month} ${year}${isToday ? ' • Heute' : ''}`;
                default: // en
                    return `Date: ${month} ${day}, ${year}${isToday ? ' • Today' : ''}`;
            }
        },

        formatMultiDaySubtitle(startDate, endDate, days, locale) {
            const monthNames = {
                en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
                it: ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'],
                de: ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez']
            };
            
            const startMonth = monthNames[locale][startDate.getMonth()];
            const startDay = startDate.getDate().toString().padStart(2, '0');
            const endMonth = monthNames[locale][endDate.getMonth()];
            const endDay = endDate.getDate().toString().padStart(2, '0');
            
            switch (locale) {
                case 'it':
                    return `Periodo: ${startDay} ${startMonth} - ${endDay} ${endMonth} • ${days} ${days === 1 ? 'Giorno' : 'Giorni'}`;
                case 'de':
                    return `Zeitraum: ${startDay}. ${startMonth} - ${endDay}. ${endMonth} • ${days} ${days === 1 ? 'Tag' : 'Tage'}`;
                default: // en
                    return `Period: ${startMonth} ${startDay} - ${endMonth} ${endDay} • ${days} ${days === 1 ? 'Day' : 'Days'}`;
            }
        },

        formatPredefinedPeriodSubtitle(periodType, endDate, locale) {
            const end = new Date(endDate);
            let days = 7; // default
            
            // Normalizza 'last_1_day' a 'last_day'
            let normalizedPeriod = periodType;
            if (normalizedPeriod === 'last_1_day') {
                normalizedPeriod = 'last_day';
            }
            
            switch (normalizedPeriod) {
                case 'last_day':
                    days = 1;
                    break;
                case 'last_7_days':
                    days = 7;
                    break;
                case 'last_14_days':
                    days = 14;
                    break;
                case 'last_30_days':
                    days = 30;
                    break;
            }
            
            const start = new Date(end);
            start.setDate(start.getDate() - days + 1);
            
            // Se è un giorno singolo, usa il formato single day
            if (days === 1) {
                return this.formatSingleDaySubtitle(end, locale);
            }
            
            // Multi-day period
            return this.formatMultiDaySubtitle(start, end, days, locale);
        },

        getChartTypeForKpi(kpiType) {
            switch (kpiType) {
                case 'consumption_comparison':
                    return 'line'; // Grafico a linee (tempo vs consumo)
                case 'refill_frequency_comparison':
                    return 'bar';
                case 'usage_frequency_comparison':
                    return 'line';
                case 'heatmap_event_distribution':
                    return 'heatmap';
                case 'consumable_cost_analysis':
                    return 'area';
                default:
                    return 'bar';
            }
        },

        getKpiChartOptions(widget) {
            const timezone = this.$store.state.timezone || 'Europe/Rome';
            const applyTz = (opts) => {
                applyTimezoneToApexCharts({ k: opts }, timezone);
                return opts;
            };
            const baseOptions = {
                chart: {
                    height: '100%',
                    toolbar: { show: false },
                    zoom: { enabled: false }
                },
                colors: [widget.color || '#213A8F'],
                dataLabels: { enabled: false },
                legend: { show: false },
                grid: {
                    show: true,
                    borderColor: '#e0e0e0',
                    strokeDashArray: 2,
                },
                tooltip: {
                    theme: 'light'
                }
            };

            // Configurazioni specifiche per tipo
            switch (widget.kpiType) {
                case 'heatmap_event_distribution':
                    return applyTz({
                        ...baseOptions,
                        colors: ['#1f77b4'], // Colore base per heatmap
                        dataLabels: { 
                            enabled: true,
                            style: {
                                fontSize: '10px',
                                fontWeight: 500,
                                colors: ['#fff']
                            }
                        },
                        plotOptions: {
                            heatmap: {
                                radius: 2,
                                enableShades: true,
                                shadeIntensity: 0.3,
                                reverseNegativeShade: false,
                                distributed: false,
                                useFillColorAsStroke: false,
                                colorScale: {
                                    ranges: [
                                        { 
                                            from: 0, 
                                            to: 5, 
                                            color: '#E8F4F8',
                                            name: 'Very Low' 
                                        },
                                        { 
                                            from: 6, 
                                            to: 15, 
                                            color: '#B8E0F0',
                                            name: 'Low' 
                                        },
                                        { 
                                            from: 16, 
                                            to: 25, 
                                            color: '#7BC8E2',
                                            name: 'Medium' 
                                        },
                                        { 
                                            from: 26, 
                                            to: 35, 
                                            color: '#3DA8CC',
                                            name: 'High' 
                                        },
                                        { 
                                            from: 36, 
                                            to: 50, 
                                            color: '#1E88A8',
                                            name: 'Very High' 
                                        },
                                        { 
                                            from: 51, 
                                            to: 999, 
                                            color: '#0F5F7A',
                                            name: 'Extreme' 
                                        }
                                    ]
                                }
                            }
                        },
                        xaxis: {
                            type: 'category',
                            position: 'bottom',
                            labels: {
                                style: { 
                                    fontSize: '11px',
                                    fontWeight: 500,
                                    colors: '#666'
                                },
                                rotate: -45
                            },
                            axisBorder: {
                                show: true,
                                color: '#e0e0e0'
                            },
                            axisTicks: {
                                show: true,
                                color: '#e0e0e0'
                            }
                        },
                        yaxis: {
                            labels: { 
                                style: { 
                                    fontSize: '11px',
                                    fontWeight: 500,
                                    colors: '#666'
                                }
                            }
                        },
                        grid: {
                            show: true,
                            borderColor: '#f1f1f1',
                            strokeDashArray: 0,
                            xaxis: {
                                lines: {
                                    show: true
                                }
                            },
                            yaxis: {
                                lines: {
                                    show: true
                                }
                            }
                        },
                        tooltip: {
                            theme: 'dark',
                            style: {
                                fontSize: '12px',
                                backgroundColor: 'rgba(0,0,0,0.8)',
                                color: '#fff'
                            },
                            marker: {
                                show: false
                            },
                            y: {
                                formatter: function(value) {
                                    return value + ' events';
                                },
                                title: {
                                    formatter: function(seriesName) {
                                        return '📡 ' + seriesName + ': ';
                                    }
                                }
                            },
                            x: {
                                formatter: function(value) {
                                    return '🕒 ' + value;
                                }
                            }
                        },
                        legend: {
                            show: false // Per heatmap la legenda è sostituita dalla scala colori
                        }
                    });
                case 'consumption_comparison':
                    return applyTz({
                        ...baseOptions,
                        colors: ['#213A8F', '#009CAB', '#4CAF50', '#FF9800', '#E91E63'], // Multiple colors for entities
                        legend: { 
                            show: true,
                            position: 'top',
                            fontSize: '12px'
                        },
                        stroke: {
                            curve: 'smooth',
                            width: 3
                        },
                        markers: {
                            size: 4,
                            hover: {
                                size: 6
                            }
                        },
                        xaxis: {
                            type: 'datetime',
                            labels: { 
                                style: { fontSize: '11px' },
                                datetimeFormatter: {
                                    year: 'yyyy',
                                    month: 'MMM',
                                    day: 'dd MMM',
                                    hour: 'HH:mm'
                                }
                            }
                        },
                        yaxis: {
                            title: {
                                text: 'Consumption (sheets)',
                                style: { fontSize: '12px', fontWeight: 600, color: '#666' }
                            },
                            labels: { 
                                style: { fontSize: '11px' },
                                formatter: (val) => Math.round(val)
                            }
                        },
                        tooltip: {
                            shared: true,
                            intersect: false,
                            x: {
                                format: 'dd MMM yyyy'
                            },
                            y: {
                                formatter: (val) => val + ' sheets'
                            }
                        }
                    });
                case 'usage_frequency_comparison': {
                    // Se accorpamento per giorno (last_14_days, last_30_days, custom multi-day): asse X = data; altrimenti ore
                    const periodType = widget.periodType || 'last_7_days';
                    const normalizedPeriod = periodType === 'last_1_day' ? 'last_day' : periodType;
                    const isAggregatedByDay = ['last_14_days', 'last_30_days'].includes(normalizedPeriod) ||
                        (normalizedPeriod === 'custom' && widget.periodStartDate && widget.periodEndDate && widget.periodStartDate !== widget.periodEndDate);
                    const opts = {
                        ...baseOptions,
                        colors: [
                            '#213A8F', // 1. Blu YouTissue scuro
                            '#FF9800', // 2. Arancione già usato
                            '#009CAB', // 3. Azzurro chiaro usage frequency
                            '#4CAF50', // 4. Verde
                            '#5DADE2', // 5. Sfumatura azzurro chiaro
                            '#3498DB', // 6. Sfumatura azzurro medio
                            '#2874A6', // 7. Sfumatura azzurro scuro
                            '#E74C3C', // 8. Rosso
                            '#85C1E9', // 9. Sfumatura azzurro molto chiaro
                            '#C0392B'  // 10. Rosso scuro
                        ],
                        legend: {
                            show: true,
                            position: 'top',
                            fontSize: '12px'
                        },
                        stroke: {
                            curve: 'smooth',
                            width: 2
                        },
                        markers: {
                            size: 3,
                            hover: {
                                size: 5
                            }
                        },
                        xaxis: {
                            type: 'datetime',
                            labels: {
                                style: { fontSize: '11px' },
                                ...(isAggregatedByDay
                                    ? {
                                        formatter: (value, timestamp) => {
                                            const v = timestamp != null ? timestamp : value;
                                            return formatDateDisplay(v, timezone) || value;
                                        }
                                    }
                                    : {
                                        datetimeFormatter: {
                                            year: 'yyyy',
                                            month: 'MMM',
                                            day: 'dd MMM',
                                            hour: 'HH:mm'
                                        }
                                    }
                                )
                            }
                        },
                        yaxis: {
                            title: {
                                text: 'Usage (entries/hour)',
                                style: { fontSize: '12px', fontWeight: 600, color: '#666' }
                            },
                            labels: {
                                style: { fontSize: '11px' },
                                formatter: (val) => Math.round(val)
                            }
                        },
                        tooltip: {
                            shared: true,
                            intersect: false,
                            x: isAggregatedByDay
                                ? {
                                    formatter: (value) => formatDateDisplay(value, timezone) || value
                                }
                                : {
                                    format: 'dd MMM yyyy HH:mm'
                                },
                            y: {
                                formatter: (val) => val + ' entries/hour'
                            }
                        }
                    };
                    // Con aggregazione per giorno NON chiamare applyTz: sovrascrive con formatTimeDisplay (solo ore)
                    if (isAggregatedByDay) return opts;
                    return applyTz(opts);
                }
                case 'refill_frequency_comparison':
                    return applyTz({
                        ...baseOptions,
                        colors: ['#4CAF50', '#2196F3', '#FF9800', '#9C27B0'], // Colori per refill frequency
                        legend: { 
                            show: true,
                            position: 'top',
                            fontSize: '12px'
                        },
                        stroke: {
                            curve: 'smooth',
                            width: 3
                        },
                        markers: {
                            size: 4,
                            hover: {
                                size: 6
                            }
                        },
                        xaxis: {
                            type: 'datetime',
                            labels: { 
                                style: { fontSize: '11px' },
                                datetimeFormatter: {
                                    year: 'yyyy',
                                    month: 'MMM',
                                    day: 'dd MMM'
                                }
                            }
                        },
                        yaxis: {
                            title: {
                                text: 'Refills per day',
                                style: { fontSize: '12px', fontWeight: 600, color: '#666' }
                            },
                            labels: { 
                                style: { fontSize: '11px' },
                                formatter: (val) => val.toFixed(1)
                            }
                        },
                        tooltip: {
                            shared: true,
                            intersect: false,
                            x: {
                                format: 'dd MMM yyyy'
                            },
                            y: {
                                formatter: (val) => val.toFixed(1) + ' refills/day'
                            }
                        }
                    });
                case 'consumable_cost_analysis':
                    return applyTz({
                        ...baseOptions,
                        fill: {
                            type: 'gradient',
                            gradient: {
                                shadeIntensity: 1,
                                opacityFrom: 0.7,
                                opacityTo: 0.3,
                                stops: [0, 90, 100]
                            }
                        },
                        xaxis: {
                            type: 'datetime',
                            labels: { style: { fontSize: '12px' } }
                        },
                        yaxis: {
                            labels: { 
                                formatter: (val) => '€' + val.toFixed(2),
                                style: { fontSize: '12px' }
                            }
                        }
                    });
                default:
                    return applyTz({
                        ...baseOptions,
                        xaxis: {
                            labels: { style: { fontSize: '12px' } }
                        },
                                                yaxis: {
                            labels: { style: { fontSize: '12px' } }
                        }
                    });
            }
        },

        async fetchChartData(widget) {
            try {
                const params = {
                    template: widget.template,
                    comparisonLevel: widget.comparisonLevel,
                    entities: widget.entities.map(e => e.value),
                    period: widget.period,
                    startDate: widget.startDate,
                    endDate: widget.endDate
                };

                const response = await axios.post(
                    this.$store.state.URL_GET_CHART_DATA,
                    params
                );

                return response.data;
            } catch (error) {
                console.error('Errore nel recupero dei dati del grafico:', error);
                return [];
            }
        },

        async fetchKpiData(widget) {
            try {
                // Chiamata API reale per Restroom Daily Usage
                if (widget.kpiType === 'restroom_daily_usage_avg') {
                    return await this.fetchRestroomUsageData(widget);
                }

                // Chiamata API reale per Usage Frequency Comparison
                if (widget.kpiType === 'usage_frequency_comparison') {
                    return await this.fetchUsageFrequencyComparisonData(widget);
                }

                // Chiamata API reale per Total Paper Consumption
                if (widget.kpiType === 'total_paper_consumption') {
                    return await this.fetchPaperConsumptionData(widget);
                }

                // Dati mock per Dispenser Consumption Analysis (temporaneo)
                if (widget.kpiType === 'dispenser_consumption_analysis') {
                    return this.generateDispenserConsumptionMockData(widget);
                }

                // Per altri KPI tabellari, usa dati mock (temporaneo)
                if (this.isTableKpi(widget.kpiType)) {
                    return this.generateMockTableData(widget.kpiType);
                }

                // Per KPI grafici (chart-based), genera dati mock
                if (this.isChartKpi(widget.kpiType)) {
                    return this.generateMockChartData(widget);
                }

                const response = await axios.post(
                    this.$store.state.URL_GET_KPI_DATA,
                    {
                        type: widget.kpiType,
                        level: widget.dataLevel,
                        entityId: widget.entityId,
                        period: widget.period || 'last_7_days',
                        startDate: widget.startDate,
                        endDate: widget.endDate
                    }
                );

                const data = response.data;
                return {
                    value: this.formatKpiValue(data.value, widget.kpiType),
                    trend: data.trend,
                    previousValue: data.previousValue,
                    label: this.getKpiLabel(widget.kpiType),
                    format: this.getKpiFormat(widget.kpiType)
                };
            } catch (error) {
                console.error('Errore nel recupero dei dati KPI:', error);
                if (this.isTableKpi(widget.kpiType)) {
                    return this.generateMockTableData();
                }
                if (this.isChartKpi(widget.kpiType)) {
                    return this.generateMockChartData(widget);
                }
                return {
                    value: 0,
                    trend: 0,
                    previousValue: 0,
                    label: this.getKpiLabel(widget.kpiType),
                    format: this.getKpiFormat(widget.kpiType)
                };
            }
        },

        async fetchPaperConsumptionData(widget) {
            try {
                // Costruisci il payload per l'API
                const payload = this.buildPaperConsumptionPayload(widget);
                
                if (!payload) {
                    console.warn('Impossibile costruire il payload per paper consumption');
                    return this.generateMockTableData('total_paper_consumption');
                }

                // Chiamata API GET con parametri come query string
                const baseUrl = this.$store.state.BASE_PATH;
                const endpoint = this.$store.state.URL_GET_PAPER_CONSUMPTION;
                const fullUrl = baseUrl + endpoint;

                const response = await axios.get(fullUrl, { params: payload });
                
                // Mappa la risposta alla struttura tabellare
                const apiData = response.data;
                const periodInfo = this.getSelectedPeriodInfo();
                
                // Crea le righe della tabella dai dispositivi
                const tableData = apiData.data.deviceBreakdown.map(device => ({
                    name: device.location || device.deviceId,
                    packsUsed: device.paperPacks || 0,
                    trend: device.percentage || 0
                }));

                // Ordina per packsUsed decrescente
                tableData.sort((a, b) => b.packsUsed - a.packsUsed);

                return {
                    tableData: tableData,
                    period: apiData.period || this.getPeriodDisplayText(),
                    periodEnd: periodInfo ? periodInfo.endDate : this.getYesterday(),
                    totalPacks: apiData.data.totalPaperPacks,
                    avgPacksPerDay: apiData.data.avgPacksPerDay,
                    percentageChange: apiData.data.percentageChange,
                    trend: apiData.data.trend
                };
            } catch (error) {
                console.error('Errore nel recupero dei dati Paper Consumption:', error);
                // Fallback a dati mock in caso di errore
                return this.generateMockTableData('total_paper_consumption');
            }
        },

        async fetchRestroomUsageData(widget) {
            try {
                // Costruisci i parametri query per l'API (stesso metodo di Usage Frequency Comparison)
                const params = this.buildUsageFrequencyComparisonParams(widget);
                
                if (!params) {
                    console.warn('Impossibile costruire i parametri per restroom usage');
                    return this.generateMockTableData('restroom_daily_usage_avg');
                }

                // Converti le date dal formato yyyymmdd al formato ISO 8601 per periodo custom
                if (params.timePeriod === 'custom' && params.startDate && params.endDate) {
                    // Converti "yyyymmdd" a "YYYY-MM-DDTHH:mm:ssZ"
                    const startDate = params.startDate; // es: "20231027"
                    const endDate = params.endDate;     // es: "20231030"
                    
                    // Converti in formato ISO 8601 con ora 00:00:00Z per startDate e 23:59:59Z per endDate
                    params.startDate = `${startDate.substring(0,4)}-${startDate.substring(4,6)}-${startDate.substring(6,8)}T00:00:00Z`;
                    params.endDate = `${endDate.substring(0,4)}-${endDate.substring(4,6)}-${endDate.substring(6,8)}T23:59:59Z`;
                }

                // Chiamata API GET con parametri come query string
                const baseUrl = this.$store.state.BASE_URL;
                const endpoint = this.$store.state.URL_ANALYTICS_WASHROOM_DAILY_USAGE;
                const fullUrl = `${baseUrl}${endpoint}`;

                console.log('Chiamata Washroom Daily Usage API:', {
                    url: fullUrl,
                    params: params
                });

                const token = localStorage.getItem('jwtToken');
                
                // Configurazione axios per serializzare correttamente gli array
                const response = await axios.get(fullUrl, { 
                    params: params,
                    paramsSerializer: params => {
                        // Serializza i parametri senza le parentesi quadre per gli array
                        const searchParams = new URLSearchParams();
                        Object.keys(params).forEach(key => {
                            if (Array.isArray(params[key])) {
                                params[key].forEach(value => {
                                    searchParams.append(key, value);
                                });
                            } else {
                                searchParams.append(key, params[key]);
                            }
                        });
                        return searchParams.toString();
                    },
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                
                // Mappa la risposta alla struttura tabellare
                const apiData = response.data;
                const periodInfo = this.getSelectedPeriodInfo();
                
                console.log('Washroom Daily Usage API response:', apiData);
                
                // Crea le righe della tabella dai dati dell'API
                const tableData = apiData.washroomStats.map(item => {
                    // Costruisci la stringa location dal oggetto location
                    const locationParts = [];
                    if (item.location.building) locationParts.push(item.location.building);
                    if (item.location.floor) locationParts.push(item.location.floor);
                    if (item.location.area) locationParts.push(item.location.area);
                    const locationString = locationParts.join(' > ') || 'N/A';
                    
                    // Identifica il tipo di sensore (se disponibile nell'API response)
                    const sensorSubtype = item.sensorSubtype || item.deviceSubtype || item.dispenserSubtype || null;
                    
                    // Applica l'aggiustamento Door Counter ai valori numerici; arrotondamento all'intero per Avg / Day
                    const adjustedAvgEntries = Math.round(Number(this.applyDoorCounterAdjustment(item.averageEntriesPerDay || 0, sensorSubtype)));
                    const adjustedAm = this.applyDoorCounterAdjustment(item.am || 0, sensorSubtype);
                    const adjustedPm = this.applyDoorCounterAdjustment(item.pm || 0, sensorSubtype);
                    const adjustedUsageCount = this.applyDoorCounterAdjustment(item.usageCount || 0, sensorSubtype);
                    
                    // Calcola le percentuali AM/PM con i valori aggiustati
                    const total = adjustedAm + adjustedPm;
                    const amPercentage = total > 0 ? ((adjustedAm / total) * 100).toFixed(1) : 0;
                    const pmPercentage = total > 0 ? ((adjustedPm / total) * 100).toFixed(1) : 0;
                    
                    return {
                        deviceId: item.deviceId || 'N/A',
                        deviceName: item.deviceName || 'N/A',
                        location: locationString,
                        avgEntries: adjustedAvgEntries,
                        am: adjustedAm,
                        pm: adjustedPm,
                        amPercentage: amPercentage,
                        pmPercentage: pmPercentage,
                        trend: item.deltaPercentage || 0,
                        usageCount: adjustedUsageCount
                    };
                });

                return {
                    tableData: tableData,
                    period: apiData.timeRange?.period || this.getPeriodDisplayText(),
                    periodEnd: periodInfo ? periodInfo.endDate : this.getYesterday(),
                    summary: apiData.summary
                };
            } catch (error) {
                console.error('Errore nel recupero dei dati Restroom Usage:', error);
                // Fallback a dati mock in caso di errore
                return this.generateMockTableData('restroom_daily_usage_avg');
            }
        },

        async fetchUsageFrequencyComparisonData(widget) {
            try {
                // Costruisci i parametri query per l'API Usage Frequency Comparison
                const params = this.buildUsageFrequencyComparisonParams(widget);
                
                if (!params) {
                    console.warn('Impossibile costruire i parametri per usage frequency comparison');
                    return this.generateMockChartData(widget);
                }

                // Converti le date dal formato yyyymmdd al formato ISO 8601 per periodo custom
                if (params.timePeriod === 'custom' && params.startDate && params.endDate) {
                    // Converti "yyyymmdd" a "YYYY-MM-DDTHH:mm:ssZ"
                    const startDate = params.startDate; // es: "20231027"
                    const endDate = params.endDate;     // es: "20231030"
                    
                    // Converti in formato ISO 8601 con ora 00:00:00Z per startDate e 23:59:59Z per endDate
                    params.startDate = `${startDate.substring(0,4)}-${startDate.substring(4,6)}-${startDate.substring(6,8)}T00:00:00Z`;
                    params.endDate = `${endDate.substring(0,4)}-${endDate.substring(4,6)}-${endDate.substring(6,8)}T23:59:59Z`;
                }

                // Chiamata API GET con parametri come query string
                const baseUrl = this.$store.state.BASE_URL;
                const endpoint = this.$store.state.URL_ANALYTICS_WASHROOM_USAGE_FREQUENCY_COMPARISON;
                const fullUrl = `${baseUrl}${endpoint}`;

                console.log('Chiamata Usage Frequency Comparison API:', {
                    url: fullUrl,
                    params: params
                });

                const token = localStorage.getItem('jwtToken');
                
                // Configurazione axios per serializzare correttamente gli array
                const response = await axios.get(fullUrl, { 
                    params: params,
                    paramsSerializer: params => {
                        // Serializza i parametri senza le parentesi quadre per gli array
                        const searchParams = new URLSearchParams();
                        Object.keys(params).forEach(key => {
                            if (Array.isArray(params[key])) {
                                params[key].forEach(value => {
                                    searchParams.append(key, value);
                                });
                            } else {
                                searchParams.append(key, params[key]);
                            }
                        });
                        return searchParams.toString();
                    },
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                
                // Mappa la risposta alla struttura per il grafico
                const apiData = response.data;
                
                console.log('Usage Frequency Comparison API response:', apiData);
                
                // I dati sono in apiData.data, che è un array di { deviceName, data: [{time, value}] }
                const devicesData = apiData.data || [];
                
                // Trasforma i dati in serie per il grafico
                const series = this.transformUsageFrequencyToSeries(devicesData);

                return {
                    series: series,
                    chartData: {
                        period: this.getPeriodDisplayText(),
                        summary: apiData.summary || ''
                    }
                };
            } catch (error) {
                console.error('Errore nel recupero dei dati Usage Frequency Comparison:', error);
                // Fallback a dati mock in caso di errore
                return this.generateMockChartData(widget);
            }
        },

        buildKpiQueryParams(widget) {
            // Costruisce i parametri query per le chiamate API KPI
            const params = {};

            // Aggiungi timePeriod
            if (widget.periodType) {
                // Normalizza 'last_1_day' a 'last_day'
                let timePeriod = widget.periodType;
                if (timePeriod === 'last_1_day') {
                    timePeriod = 'last_day';
                }
                params.timePeriod = timePeriod;
                
                // Se è un periodo custom, aggiungi le date in formato yyyymmdd
                if (widget.periodType === 'custom' && widget.periodStartDate && widget.periodEndDate) {
                    // Converti "YYYY-MM-DD" in "yyyymmdd"
                    params.startDate = widget.periodStartDate.replace(/-/g, '');
                    params.endDate = widget.periodEndDate.replace(/-/g, '');
                }
            } else {
                params.timePeriod = 'last_30_days'; // Default
            }

            // Per Restroom Daily Usage e Usage Frequency Comparison, passa gli ID dei sensori direttamente
            if ((widget.kpiType === 'restroom_daily_usage_avg' || widget.kpiType === 'usage_frequency_comparison') && widget.entityId) {
                params.sensorIds = Array.isArray(widget.entityId) ? widget.entityId : [widget.entityId];
                return params;
            }

            // Aggiungi i parametri di location basati su entityNames
            if (widget.entityNames) {
                if (widget.entityNames.building) {
                    params.building = widget.entityNames.building;
                }
                if (widget.entityNames.floor) {
                    params.floor = widget.entityNames.floor;
                }
                if (widget.entityNames.area) {
                    params.area = widget.entityNames.area;
                }
                
                // Se ci sono multiple selezioni, usa la prima
                if (!params.building && widget.entityNames.buildings && widget.entityNames.buildings.length > 0) {
                    params.building = widget.entityNames.buildings[0];
                }
                if (!params.floor && widget.entityNames.floors && widget.entityNames.floors.length > 0) {
                    params.floor = widget.entityNames.floors[0];
                }
                if (!params.area && widget.entityNames.areas && widget.entityNames.areas.length > 0) {
                    params.area = widget.entityNames.areas[0];
                }
            }

            // Verifica che ci sia almeno un parametro di location
            if (!params.building && !params.floor && !params.area) {
                console.warn('Nessun parametro di location valido trovato');
                return null;
            }

            return params;
        },

        determineRestroomStatus(avgEntries) {
            // Determina lo stato del bagno in base alla media degli ingressi
            if (avgEntries === 0) return this.$t('dashboard.status.empty');
            if (avgEntries < 5) return this.$t('dashboard.status.low');
            return this.$t('dashboard.status.active');
        },

        transformUsageFrequencyToSeries(data) {
            // Trasforma i dati dell'API in serie per il grafico ApexCharts
            if (!data || !Array.isArray(data)) {
                console.warn('Dati non validi per transformUsageFrequencyToSeries:', data);
                return [];
            }

            console.log('Trasformazione dati Usage Frequency, input:', data);

            // L'API restituisce: { data: [{ deviceName, sensorSubtype, data: [{time, value}] }] }
            const series = data.map(device => {
                const deviceName = device.deviceName;
                const deviceData = device.data || [];
                // Identifica il tipo di sensore (se disponibile nell'API response)
                const sensorSubtype = device.sensorSubtype || device.deviceSubtype || device.dispenserSubtype || null;
                
                // Trasforma i dati del dispositivo nel formato ApexCharts: [[timestamp, value], ...]
                const chartData = deviceData.map(point => {
                    // Le date dall'API sono in CET (Europe/Rome): interpretale come CET per ottenere il timestamp corretto
                    const isoTimeString = point.time.replace(/\//g, '-');
                    const d = parseDateAsCET(isoTimeString);
                    const timestamp = d ? d.getTime() : new Date(isoTimeString).getTime();
                    // Applica l'aggiustamento Door Counter al valore
                    const value = this.applyDoorCounterAdjustment(point.value || 0, sensorSubtype);
                    
                    return [timestamp, value];
                });
                
                // Ordina i dati per timestamp
                chartData.sort((a, b) => a[0] - b[0]);
                
                return {
                    name: deviceName,
                    data: chartData
                };
            });

            console.log('Trasformazione dati Usage Frequency, output:', series);
            return series;
        },

        buildUsageFrequencyComparisonParams(widget) {
            // Costruisce i parametri per l'API Usage Frequency Comparison
            const params = {};

            // Mappa il nostro periodType ai valori accettati dall'API
            let timePeriod = 'last_7_days'; // Default
            
            if (widget.periodType) {
                // Normalizza 'last_1_day' a 'last_day'
                let normalizedPeriod = widget.periodType;
                if (normalizedPeriod === 'last_1_day') {
                    normalizedPeriod = 'last_day';
                }
                
                switch (normalizedPeriod) {
                    case 'last_day':
                        timePeriod = 'last_day';
                        break;
                    case 'last_7_days':
                        timePeriod = 'last_7_days';
                        break;
                    case 'last_14_days':
                        timePeriod = 'last_14_days';
                        break;
                    case 'last_30_days':
                        timePeriod = 'last_30_days';
                        break;
                    case 'custom':
                        timePeriod = 'custom';
                        // Aggiungi startDate e endDate per periodo custom in formato yyyymmdd
                        if (widget.periodStartDate && widget.periodEndDate) {
                            // Converti "YYYY-MM-DD" in "yyyymmdd"
                            params.startDate = widget.periodStartDate.replace(/-/g, '');
                            params.endDate = widget.periodEndDate.replace(/-/g, '');
                        }
                        break;
                    default:
                        timePeriod = 'last_7_days';
                }
            }
            
            params.timePeriod = timePeriod;

            // Aggiungi gli ID dei dispositivi (sensori) nel formato "eui-{dev_eui}" - in minuscolo come richiesto dal servizio
            if (widget.entityNames && widget.entityNames.sensorsDevEui && widget.entityNames.sensorsDevEui.length > 0) {
                // Usa i devEui salvati nel formato "eui-{dev_eui}" convertiti in minuscolo
                params.devices = widget.entityNames.sensorsDevEui.map(devEui => `eui-${devEui.toLowerCase()}`);
            } else if (widget.entityId && Array.isArray(widget.entityId)) {
                // Fallback: cerca i devEui negli availableSensors se possibile
                const devEuis = [];
                widget.entityId.forEach(id => {
                    const sensor = this.availableSensors.find(s => s.value === id);
                    if (sensor && sensor.devEui) {
                        devEuis.push(`eui-${sensor.devEui.toLowerCase()}`);
                    }
                });
                
                if (devEuis.length > 0) {
                    params.devices = devEuis;
                } else {
                    console.warn('Impossibile trovare i devEui per i sensori selezionati');
                    return null;
                }
            } else {
                console.warn('Nessun device ID trovato per Usage Frequency Comparison');
                return null;
            }

            console.log('Parametri costruiti per Usage Frequency Comparison:', params);
            return params;
        },

        buildPaperConsumptionPayload(widget) {
            // Costruisce il payload basato sul data level e sulle selezioni
            const payload = {};

            // Costruisci locationFilter come oggetto
            const locationFilter = {};

            // Usa i nomi salvati nel widget (entityNames) se disponibili
            if (widget.entityNames) {
                if (widget.entityNames.building) {
                    locationFilter.building = widget.entityNames.building;
                }
                if (widget.entityNames.floor) {
                    locationFilter.floor = widget.entityNames.floor;
                }
                if (widget.entityNames.area) {
                    locationFilter.area = widget.entityNames.area;
                }
                
                // Se ci sono multiple selezioni, prendi la prima per la chiamata API
                if (!locationFilter.building && widget.entityNames.buildings && widget.entityNames.buildings.length > 0) {
                    locationFilter.building = widget.entityNames.buildings[0];
                }
                if (!locationFilter.floor && widget.entityNames.floors && widget.entityNames.floors.length > 0) {
                    locationFilter.floor = widget.entityNames.floors[0];
                }
                if (!locationFilter.area && widget.entityNames.areas && widget.entityNames.areas.length > 0) {
                    locationFilter.area = widget.entityNames.areas[0];
                }
            }

            // Verifica che locationFilter abbia almeno un campo valido
            if (!locationFilter.building && !locationFilter.floor && !locationFilter.area) {
                console.warn('Nessuna entità valida trovata per costruire il locationFilter');
                return null;
            }

            // Aggiungi locationFilter al payload
            payload.locationFilter = locationFilter;

            // Aggiungi informazioni sul periodo (timePeriod invece di period)
            if (widget.periodType) {
                // Normalizza 'last_1_day' a 'last_day'
                let timePeriod = widget.periodType;
                if (timePeriod === 'last_1_day') {
                    timePeriod = 'last_day';
                }
                payload.timePeriod = timePeriod;
                // Aggiungi startDate e endDate solo se il periodo è 'custom' in formato yyyymmdd
                if (widget.periodType === 'custom' && widget.periodStartDate && widget.periodEndDate) {
                    // Converti "YYYY-MM-DD" in "yyyymmdd"
                    payload.startDate = widget.periodStartDate.replace(/-/g, '');
                    payload.endDate = widget.periodEndDate.replace(/-/g, '');
                }
            } else {
                // Default a last_30_days se non specificato
                payload.timePeriod = 'last_30_days';
            }

            console.log('Payload costruito per Paper Consumption API:', payload);
            return payload;
        },

        generateMockTableData(kpiType = null) {
            // Use selected period info if available
            const periodInfo = this.getSelectedPeriodInfo();
            const periodText = this.selectedPeriod ? this.getPeriodDisplayText() : 'Last 30 days';
            const periodEnd = periodInfo ? periodInfo.endDate : this.getYesterday();

            // Usa il parametro kpiType passato o fallback su selectedKpiType
            const currentKpiType = kpiType || this.selectedKpiType;

            // Genera dati mock realistici per Total Paper Consumption
            if (currentKpiType === 'total_paper_consumption') {
                return this.generatePaperConsumptionMockData(periodText, periodEnd);
            }

            // Genera dati mock per Refill Frequency - Avg / Day
            if (currentKpiType === 'refill_frequency_avg_day') {
                return this.generateRefillFrequencyMockData(periodText, periodEnd);
            }

            // Genera dati mock per Restroom Daily Usage - Avg / Day
            if (currentKpiType === 'restroom_daily_usage_avg') {
                return this.generateRestroomUsageMockData(periodText, periodEnd);
            }

            // Genera dati mock per Alert Activity - Avg / Day
            if (currentKpiType === 'alert_activity_avg_day') {
                return this.generateAlertActivityMockData(periodText, periodEnd);
            }

            // Fallback per altri KPI
            return {
                tableData: [],
                period: periodText,
                periodEnd: periodEnd
            };
        },

        generateDispenserConsumptionMockData(widget) {
            const periodInfo = this.getSelectedPeriodInfo();
            const periodText = this.selectedPeriod ? this.getPeriodDisplayText() : 'Last 30 days';
            const periodEnd = periodInfo ? periodInfo.endDate : this.getYesterday();

            // Ricava il nome del client selezionato dal widget.
            // Prima usa il nome già salvato in hierarchicalInfo (disponibile anche al reload),
            // poi prova a risolvere l'ID tramite availableClients se già caricati.
            const hierarchicalInfo = widget?.hierarchicalInfo || {};
            const savedClientName = hierarchicalInfo.selectedClientName || '';
            const clientId = hierarchicalInfo.selectedClient
                || (hierarchicalInfo.selectedClients && hierarchicalInfo.selectedClients[0])
                || null;
            const clientObj = clientId
                ? this.availableClients.find(c => c.value === clientId)
                : null;
            const clientName = (clientObj ? clientObj.text : '') || savedClientName;
            const isMarplast = clientName.toLowerCase().includes('marplast');

            // ── Dati Marplast ──────────────────────────────────────────────
            if (isMarplast) {
                const groups = [
                    {
                        nameKey: 'groupHandTowelRoll',
                        unitKey: 'unitRolls',
                        maxConsumption: 45,
                        items: [
                            {
                                id: 'htw-m',
                                name: 'Autocut Skin 884 M',
                                type: 'Paper rolls dispenser',
                                location: 'Marplast > HQ > Floor 1 > Men',
                                totalConsumption: 27,
                                avgPerDay: 0.9,
                                consPer100Visits: 0.4,
                            },
                            {
                                id: 'htw-f',
                                name: 'Autocut Skin 884 F',
                                type: 'Paper rolls dispenser',
                                location: 'Marplast > HQ > Floor 1 > Women',
                                totalConsumption: 45,
                                avgPerDay: 1.5,
                                consPer100Visits: 0.7,
                            },
                        ],
                        groupTotal: { totalConsumption: 72, avgPerDay: 2.4 },
                    },
                    {
                        nameKey: 'groupToiletPaperRolls',
                        unitKey: 'unitRolls',
                        maxConsumption: 81,
                        items: [
                            {
                                id: 'tpr-m',
                                name: 'Midi Jumbo Skin 907 M',
                                type: 'Toilet paper roll dispenser',
                                location: 'Marplast > HQ > Floor 1 > Men',
                                totalConsumption: 52,
                                avgPerDay: 1.7,
                                consPer100Visits: 0.6,
                            },
                            {
                                id: 'tpr-f',
                                name: 'Midi Jumbo Skin 907 F',
                                type: 'Toilet paper roll dispenser',
                                location: 'Marplast > HQ > Floor 1 > Women',
                                totalConsumption: 81,
                                avgPerDay: 2.7,
                                consPer100Visits: 0.9,
                            },
                        ],
                        groupTotal: { totalConsumption: 133, avgPerDay: 4.4 },
                    },
                    {
                        nameKey: 'groupSoap',
                        unitKey: 'unitLiters',
                        maxConsumption: 36,
                        items: [
                            {
                                id: 'soap-m',
                                name: 'Skin Soap 924 M',
                                type: 'Soap dispenser',
                                location: 'Marplast > HQ > Floor 1 > Men',
                                totalConsumption: 24,
                                avgPerDay: 0.8,
                                consPer100Visits: 0.3,
                            },
                            {
                                id: 'soap-f',
                                name: 'Skin Soap 924 F',
                                type: 'Soap dispenser',
                                location: 'Marplast > HQ > Floor 1 > Women',
                                totalConsumption: 36,
                                avgPerDay: 1.2,
                                consPer100Visits: 0.5,
                            },
                        ],
                        groupTotal: { totalConsumption: 60, avgPerDay: 2.0 },
                    },
                ];
                return { groups, period: periodText, periodEnd };
            }

            // ── Dati Medial / Customer 1 ───────────────────────────────────
            const isMedial = clientName.toLowerCase().includes('medial')
                          || clientName.toLowerCase().includes('customer 1');
            if (isMedial) {
                const groups = [
                    {
                        nameKey: 'groupHandTowelRoll',
                        unitKey: 'unitRolls',
                        maxConsumption: 42,
                        items: [
                            {
                                id: 'htw-m',
                                name: 'Stilevo 920020 Z-C-V M',
                                type: 'Folded towel dispenser',
                                location: 'Medial > Building 1 > Basement > Men',
                                totalConsumption: 31,
                                avgPerDay: 1.0,
                                consPer100Visits: 0.5,
                            },
                            {
                                id: 'htw-f',
                                name: 'Stilevo 920020 Z-C-V F',
                                type: 'Folded towel dispenser',
                                location: 'Medial > Building 1 > Basement > Women',
                                totalConsumption: 42,
                                avgPerDay: 1.4,
                                consPer100Visits: 0.6,
                            },
                        ],
                        groupTotal: { totalConsumption: 73, avgPerDay: 2.4 },
                    },
                    {
                        nameKey: 'groupToiletPaperRolls',
                        unitKey: 'unitRolls',
                        maxConsumption: 76,
                        items: [
                            {
                                id: 'tpr-m',
                                name: 'Stilevo 920002 Jumbo M',
                                type: 'Toilet paper roll dispenser',
                                location: 'Medial > Building 1 > Basement > Men',
                                totalConsumption: 49,
                                avgPerDay: 1.6,
                                consPer100Visits: 0.6,
                            },
                            {
                                id: 'tpr-f',
                                name: 'Stilevo 920002 Jumbo F',
                                type: 'Toilet paper roll dispenser',
                                location: 'Medial > Building 1 > Basement > Women',
                                totalConsumption: 76,
                                avgPerDay: 2.5,
                                consPer100Visits: 0.9,
                            },
                        ],
                        groupTotal: { totalConsumption: 125, avgPerDay: 4.1 },
                    },
                    {
                        nameKey: 'groupSoap',
                        unitKey: 'unitLiters',
                        maxConsumption: 33,
                        items: [
                            {
                                id: 'soap-m',
                                name: 'Stilevo 920041 Soap M',
                                type: 'Soap dispenser',
                                location: 'Medial > Building 1 > Basement > Men',
                                totalConsumption: 27,
                                avgPerDay: 0.9,
                                consPer100Visits: 0.3,
                            },
                            {
                                id: 'soap-f',
                                name: 'Stilevo 920041 Soap F',
                                type: 'Soap dispenser',
                                location: 'Medial > Building 1 > Basement > Women',
                                totalConsumption: 33,
                                avgPerDay: 1.1,
                                consPer100Visits: 0.4,
                            },
                        ],
                        groupTotal: { totalConsumption: 60, avgPerDay: 2.0 },
                    },
                ];
                return { groups, period: periodText, periodEnd };
            }

            // ── Dati generici per altri client (placeholder finché non arrivano dati reali) ──
            return { groups: [], period: periodText, periodEnd };
        },

        generateRefillFrequencyMockData(periodText, periodEnd) {
            // Determina il livello di visualizzazione in base alle selezioni
            let mockData = [];
            
            // LIVELLO AGGREGATO: quando si selezionano multiple entità del livello più alto
            if (this.isAggregatedLevel()) {
                mockData = [
                    { location: 'Client A > Bldg 1 > Floor 2', refillsPerDay: 1.2, trend: 5.1 },
                    { location: 'Client A > Bldg 1 > Floor 3', refillsPerDay: 0.8, trend: -2.0 },
                    { location: 'Client A > Bldg 2 > Floor 1', refillsPerDay: 1.6, trend: 1.3 }
                ];
            }
            // LIVELLO AREA: quando si arriva fino alle aree
            else if (this.selectedDataLevel === 'area' || this.isAreaLevel()) {
                mockData = [
                    { location: 'Area A', refillsPerDay: 1.2, trend: 5.1 },
                    { location: 'Area B', refillsPerDay: 0.8, trend: -2.0 },
                    { location: 'Area C', refillsPerDay: 1.6, trend: 1.3 }
                ];
            }
            // LIVELLO SENSOR: quando si arriva fino ai sensori/dispenser
            else if (this.selectedDataLevel === 'sensor' || this.isSensorLevel()) {
                mockData = [
                    { location: 'Client A > Bldg 1 > Floor 2 > Area', refillsPerDay: 1.2, trend: 0.9 },
                    { location: 'Client A > Bldg 1 > Floor 3 > Area', refillsPerDay: 0.8, trend: -1.1 },
                    { location: 'Client A > Bldg 2 > Floor 1 > Area', refillsPerDay: 1.6, trend: 2.4 }
                ];
            }
            // DEFAULT: Livello aggregato generico
            else {
                mockData = [
                    { location: 'Client A > Bldg 1 > Floor 2', refillsPerDay: 1.2, trend: 5.1 },
                    { location: 'Client A > Bldg 1 > Floor 3', refillsPerDay: 0.8, trend: -2.0 },
                    { location: 'Client A > Bldg 2 > Floor 1', refillsPerDay: 1.6, trend: 1.3 }
                ];
            }

            return {
                tableData: mockData,
                period: periodText,
                periodEnd: periodEnd
            };
        },

        generateRestroomUsageMockData(periodText, periodEnd) {
            // Dati mock realistici per Restroom Daily Usage basati sull'immagine
            let mockData = [];
            
            // Scenario base come mostrato nell'immagine
            if (this.selectedDataLevel === 'building' || this.isAggregatedLevel()) {
                mockData = [
                    { 
                        restroom: 'WC-N1', 
                        location: 'Client A > Building 1 > Floor 2 > Area B',
                        avgEntries: 1.3,
                        status: this.$t('dashboard.status.low')
                    },
                    { 
                        restroom: 'WC-West-Lobby', 
                        location: 'Client A > Building 1 > Floor 1 > Area A',
                        avgEntries: 0.0,
                        status: this.$t('dashboard.status.empty')
                    },
                    { 
                        restroom: 'WC-Showroom', 
                        location: 'Client A > Building 2 > Floor 3 > Show',
                        avgEntries: 2.0,
                        status: this.$t('dashboard.status.low')
                    },
                    { 
                        restroom: 'WC-Lobby-F1', 
                        location: 'Client A > Building 1 > Floor 1 > Lobby',
                        avgEntries: 8.7,
                        status: this.$t('dashboard.status.active')
                    }
                ];
            }
            // Altri scenari possono essere aggiunti qui per diversi data levels
            else {
                mockData = [
                    { 
                        restroom: 'WC-Main', 
                        location: 'Ground Floor > Reception',
                        avgEntries: 12.5,
                        status: this.$t('dashboard.status.active')
                    },
                    { 
                        restroom: 'WC-Staff', 
                        location: 'First Floor > Office',
                        avgEntries: 8.2,
                        status: this.$t('dashboard.status.active')
                    },
                    { 
                        restroom: 'WC-Public', 
                        location: 'Ground Floor > Lobby',
                        avgEntries: 0.5,
                        status: this.$t('dashboard.status.low')
                    }
                ];
            }

            return {
                tableData: mockData,
                period: periodText,
                periodEnd: periodEnd
            };
        },

        generateAlertActivityMockData(periodText, periodEnd) {
            // Dati mock realistici per Alert Activity basati sull'immagine
            let mockData = [];
            
            // Scenario per diversi data levels
            switch (this.selectedDataLevel) {
                case 'floor':
                    // Scenario come mostrato nell'immagine (floor level)
                    mockData = [
                        { 
                            name: 'Floor 3', 
                            location: 'Client A > Building 1',
                            avgAlerts: 38,
                            trend: 6.0
                        },
                        { 
                            name: 'Floor 2', 
                            location: 'Client A > Building 1',
                            avgAlerts: 22,
                            trend: -3.0
                        },
                        { 
                            name: 'Floor 1', 
                            location: 'Client B > Building 2',
                            avgAlerts: 15,
                            trend: 0.0
                        },
                        { 
                            name: 'Floor -1', 
                            location: 'Client A > Building 1',
                            avgAlerts: 11,
                            trend: 2.0
                        }
                    ];
                    break;
                    
                case 'building':
                    mockData = [
                        { 
                            name: 'Building Alpha', 
                            location: 'Client A > North Campus',
                            avgAlerts: 45,
                            trend: 8.5
                        },
                        { 
                            name: 'Building Beta', 
                            location: 'Client A > South Campus',
                            avgAlerts: 32,
                            trend: -2.1
                        },
                        { 
                            name: 'Building Gamma', 
                            location: 'Client B > Main Site',
                            avgAlerts: 28,
                            trend: 4.3
                        }
                    ];
                    break;
                    
                case 'area':
                    mockData = [
                        { 
                            name: 'Reception Area', 
                            location: 'Client A > Building 1 > Floor 1',
                            avgAlerts: 12,
                            trend: 3.2
                        },
                        { 
                            name: 'Office Zone A', 
                            location: 'Client A > Building 1 > Floor 2',
                            avgAlerts: 18,
                            trend: -1.5
                        },
                        { 
                            name: 'Meeting Rooms', 
                            location: 'Client A > Building 1 > Floor 3',
                            avgAlerts: 9,
                            trend: 0.8
                        },
                        { 
                            name: 'Cafeteria', 
                            location: 'Client A > Building 1 > Floor 1',
                            avgAlerts: 25,
                            trend: 5.7
                        }
                    ];
                    break;
                    
                case 'sensor':
                    mockData = [
                        { 
                            name: 'DISP-137', 
                            location: 'Client A > Building 1 > Floor 2 > Area A',
                            avgAlerts: 8,
                            trend: 2.1
                        },
                        { 
                            name: 'DISP-143', 
                            location: 'Client A > Building 1 > Floor 2 > Area B',
                            avgAlerts: 14,
                            trend: -4.2
                        },
                        { 
                            name: 'DISP-166', 
                            location: 'Client A > Building 1 > Floor 3 > Area C',
                            avgAlerts: 6,
                            trend: 1.8
                        },
                        { 
                            name: 'DISP-201', 
                            location: 'Client B > Building 2 > Floor 1 > Area A',
                            avgAlerts: 19,
                            trend: 7.3
                        }
                    ];
                    break;
                    
                default:
                    // Fallback generico (client level)
                    mockData = [
                        { 
                            name: 'Client Alpha', 
                            location: 'Multiple Buildings',
                            avgAlerts: 156,
                            trend: 4.8
                        },
                        { 
                            name: 'Client Beta', 
                            location: 'Multiple Buildings',
                            avgAlerts: 89,
                            trend: -1.2
                        },
                        { 
                            name: 'Client Gamma', 
                            location: 'Multiple Buildings',
                            avgAlerts: 234,
                            trend: 12.5
                        }
                    ];
            }

            return {
                tableData: mockData,
                period: periodText,
                periodEnd: periodEnd
            };
        },

        generateMockChartData(widget) {
            const periodInfo = this.getSelectedPeriodInfo();
            
            switch (widget.kpiType) {
                case 'consumption_comparison':
                    return this.generateConsumptionComparisonData(periodInfo);
                case 'usage_frequency_comparison':
                    return this.generateUsageFrequencyComparisonData(periodInfo);
                case 'refill_frequency_comparison':
                    return this.generateRefillFrequencyComparisonData(periodInfo);
                case 'heatmap_event_distribution':
                    return this.generateHeatmapEventDistributionData(periodInfo);
                default:
                    return {
                        series: [],
                        chartData: {}
                    };
            }
        },

        generateConsumptionComparisonData(periodInfo) {
            // Genera dati temporali per il confronto dei consumi
            const days = periodInfo ? periodInfo.days : 7;
            const endDate = periodInfo ? new Date(periodInfo.endDate) : new Date();
            const startDate = new Date(endDate);
            startDate.setDate(startDate.getDate() - days + 1);

            // Genera date per l'asse X
            const dates = [];
            const currentDate = new Date(startDate);
            while (currentDate <= endDate) {
                dates.push(new Date(currentDate).getTime());
                currentDate.setDate(currentDate.getDate() + 1);
            }

            // Genera serie di dati per diverse entità basate sul data level
            const series = this.generateSeriesForDataLevel(dates);

            return {
                series: series,
                chartData: {
                    categories: dates,
                    period: this.getPeriodDisplayText()
                }
            };
        },

                generateSeriesForDataLevel(dates) {
            const baseConsumption = 150; // Consumo base giornaliero
            
            // Utilizza helper method per consistenza
            const entities = this.getEntitiesForDataLevel();

            return entities.map((entityName, index) => {
                const multiplier = 0.7 + (index * 0.4); // Variazione tra entità
                const variance = 0.3; // Variabilità giornaliera

                const data = dates.map(date => {
                    // Aggiungi variabilità giornaliera e trend
                    const dayOffset = Math.sin((date / (1000 * 60 * 60 * 24)) * 0.1) * 20;
                    const randomVariation = (Math.random() - 0.5) * variance * baseConsumption;
                    const consumption = Math.round((baseConsumption * multiplier) + dayOffset + randomVariation);
                    
                    return [date, Math.max(0, consumption)];
                });

                return {
                    name: entityName,
                    data: data,
                    type: 'line'
                };
            });
        },

        generateUsageFrequencyComparisonData(periodInfo) {
            // Genera dati temporali per il confronto della frequenza d'uso (tempo vs ingressi)
            const days = periodInfo ? periodInfo.days : 7;
            const endDate = periodInfo ? new Date(periodInfo.endDate) : new Date();
            const startDate = new Date(endDate);
            startDate.setDate(startDate.getDate() - days + 1);

            // Per usage frequency, generiamo dati orari per mostrare pattern comportamentali
            const timePoints = [];
            const currentTime = new Date(startDate);
            
            // Generiamo punti dati ogni 2 ore per il periodo selezionato
            while (currentTime <= endDate) {
                for (let hour = 8; hour <= 18; hour += 2) { // Ore lavorative 8-18 ogni 2 ore
                    const timePoint = new Date(currentTime);
                    timePoint.setHours(hour, 0, 0, 0);
                    if (timePoint <= endDate) {
                        timePoints.push(timePoint.getTime());
                    }
                }
                currentTime.setDate(currentTime.getDate() + 1);
            }

            // Genera serie per diverse entità
            const entities = this.getEntitiesForDataLevel();
            const series = entities.map((entityName, index) => {
                const baseUsage = 15; // Uso base per fascia oraria
                const peakMultiplier = 1.5 + (index * 0.2); // Variazione tra entità
                
                const data = timePoints.map(timestamp => {
                    const hour = new Date(timestamp).getHours();
                    
                    // Pattern di utilizzo realistico: picchi a metà mattina e pomeriggio
                    let hourlyMultiplier = 1;
                    if (hour >= 10 && hour <= 11) hourlyMultiplier = peakMultiplier; // Picco mattutino
                    else if (hour >= 14 && hour <= 15) hourlyMultiplier = peakMultiplier * 0.8; // Picco pomeridiano
                    else if (hour <= 9 || hour >= 17) hourlyMultiplier = 0.3; // Basso utilizzo inizio/fine giornata
                    else hourlyMultiplier = 0.7; // Utilizzo normale
                    
                    // Aggiungi variabilità giornaliera
                    const dayOfWeek = new Date(timestamp).getDay();
                    const weekendReduction = (dayOfWeek === 0 || dayOfWeek === 6) ? 0.3 : 1;
                    
                    const randomVariation = 0.8 + (Math.random() * 0.4); // ±20% variabilità
                    const usage = Math.round(baseUsage * hourlyMultiplier * weekendReduction * randomVariation);
                    
                    return [timestamp, Math.max(0, usage)];
                });

                return {
                    name: entityName,
                    data: data,
                    type: 'line'
                };
            });

            return {
                series: series,
                chartData: {
                    categories: timePoints,
                    period: this.getPeriodDisplayText()
                }
            };
        },

        generateRefillFrequencyComparisonData(periodInfo) {
            // Genera dati temporali per il confronto della frequenza di ricariche
            const days = periodInfo ? periodInfo.days : 7;
            const endDate = periodInfo ? new Date(periodInfo.endDate) : new Date();
            const startDate = new Date(endDate);
            startDate.setDate(startDate.getDate() - days + 1);

            // Generiamo punti dati giornalieri per le ricariche
            const dates = [];
            const currentDate = new Date(startDate);
            while (currentDate <= endDate) {
                dates.push(new Date(currentDate).getTime());
                currentDate.setDate(currentDate.getDate() + 1);
            }

            // Genera serie per diverse entità
            const entities = this.getEntitiesForDataLevel();
            const series = entities.map((entityName, index) => {
                const baseRefills = 2.5; // Ricariche base giornaliere
                const entityVariation = 0.8 + (index * 0.6); // Variazione tra entità (0.8x - 2.0x)
                
                const data = dates.map((timestamp, dayIndex) => {
                    // Pattern settimanale: più ricariche a inizio settimana
                    const dayOfWeek = new Date(timestamp).getDay();
                    let weeklyMultiplier = 1;
                    if (dayOfWeek === 1 || dayOfWeek === 2) weeklyMultiplier = 1.4; // Lunedì-Martedì
                    else if (dayOfWeek === 5 || dayOfWeek === 6) weeklyMultiplier = 0.6; // Venerdì-Sabato
                    else if (dayOfWeek === 0) weeklyMultiplier = 0.2; // Domenica
                    
                    // Trend graduale nel tempo (leggero aumento o diminuzione)
                    const trendFactor = 1 + ((dayIndex - days/2) * 0.02 * (Math.random() - 0.5));
                    
                    // Variabilità giornaliera
                    const randomVariation = 0.7 + (Math.random() * 0.6); // ±30% variabilità
                    
                    const refills = baseRefills * entityVariation * weeklyMultiplier * trendFactor * randomVariation;
                    
                    return [timestamp, Math.max(0, Math.round(refills * 10) / 10)]; // Arrotonda a 1 decimale
                });

                return {
                    name: entityName,
                    data: data,
                    type: 'line'
                };
            });

            return {
                series: series,
                chartData: {
                    categories: dates,
                    period: this.getPeriodDisplayText()
                }
            };
        },

        getEntitiesForDataLevel() {
            // Helper method per ottenere nomi di entità appropriate al data level
            switch (this.selectedDataLevel) {
                case 'building':
                    return ['Building Alpha', 'Building Beta', 'Building Gamma'];
                case 'floor':
                    return ['Floor 1', 'Floor 2', 'Floor 3', 'Floor -1'];
                case 'area':
                    return ['Reception Area', 'Office Zone A', 'Office Zone B', 'Meeting Rooms'];
                case 'sensor':
                    return ['DISP-137', 'DISP-143', 'DISP-166', 'DISP-201'];
                default:
                    return ['Client A', 'Client B', 'Client C'];
            }
                 },

        generateHeatmapEventDistributionData(periodInfo) {
            // Genera dati per heatmap distribuzione eventi nel tempo
            const days = periodInfo ? periodInfo.days : 7;
            const endDate = periodInfo ? new Date(periodInfo.endDate) : new Date();
            
            // Determina la granularità temporale
            const useHourlyData = days <= 3; // Se periodo <= 3 giorni, usa dati orari
            
            if (useHourlyData) {
                return this.generateHourlyHeatmapData();
            } else {
                return this.generateDailyHeatmapData(endDate, days);
            }
        },

        generateHourlyHeatmapData() {
            // Heatmap oraria: Y = Sensori, X = Ore del giorno (0-23)
            const sensors = this.getHeatmapSensors();
            const hours = Array.from({length: 24}, (_, i) => i); // 0-23
            
            // Simula diversi tipi di eventi
            const eventTypes = ['Usage', 'Alerts', 'Refills'];
            const selectedEventType = eventTypes[Math.floor(Math.random() * eventTypes.length)];
            
            const series = sensors.map(sensorName => {
                const data = hours.map(hour => {
                    let intensity = this.getEventIntensityByHour(hour, selectedEventType);
                    
                    // Aggiungi variabilità per sensore
                    const sensorVariation = 0.7 + (Math.random() * 0.6); // 0.7x - 1.3x
                    intensity = Math.round(intensity * sensorVariation);
                    
                    return {
                        x: hour.toString().padStart(2, '0') + ':00',
                        y: Math.max(0, intensity)
                    };
                });

                return {
                    name: sensorName,
                    data: data
                };
            });

            return {
                series: series,
                chartData: {
                    eventType: selectedEventType,
                    period: this.getPeriodDisplayText(),
                    granularity: 'hourly'
                }
            };
        },

        generateDailyHeatmapData(endDate, days) {
            // Heatmap giornaliera: Y = Sensori, X = Giorni della settimana/Date
            const sensors = this.getHeatmapSensors();
            
            // Genera date per il periodo
            const dates = [];
            const currentDate = new Date(endDate);
            currentDate.setDate(currentDate.getDate() - days + 1);
            
            for (let i = 0; i < days; i++) {
                const date = new Date(currentDate);
                date.setDate(date.getDate() + i);
                dates.push({
                    date: new Date(date),
                    label: date.getDate().toString().padStart(2, '0') + '/' + (date.getMonth() + 1).toString().padStart(2, '0')
                });
            }

            // Simula diversi tipi di eventi
            const eventTypes = ['Usage', 'Alerts', 'Refills'];
            const selectedEventType = eventTypes[Math.floor(Math.random() * eventTypes.length)];

            const series = sensors.map(sensorName => {
                const data = dates.map(dateInfo => {
                    let intensity = this.getEventIntensityByDay(dateInfo.date, selectedEventType);
                    
                    // Aggiungi variabilità per sensore
                    const sensorVariation = 0.6 + (Math.random() * 0.8); // 0.6x - 1.4x
                    intensity = Math.round(intensity * sensorVariation);
                    
                    return {
                        x: dateInfo.label,
                        y: Math.max(0, intensity)
                    };
                });

                return {
                    name: sensorName,
                    data: data
                };
            });

            return {
                series: series,
                chartData: {
                    eventType: selectedEventType,
                    period: this.getPeriodDisplayText(),
                    granularity: 'daily'
                }
            };
        },

        getHeatmapSensors() {
            // Genera sensori appropriati per heatmap (massimo 10 come da specifica)
            const allSensors = [
                'DISP-137', 'DISP-143', 'DISP-166', 'DISP-201', 'DISP-205', 
                'DISP-208', 'DISP-301', 'DISP-315', 'DISP-402', 'DISP-501'
            ];
            
            // Per demo, usa 6-8 sensori casualmente
            const sensorCount = Math.min(6 + Math.floor(Math.random() * 3), allSensors.length);
            return allSensors.slice(0, sensorCount);
        },

        getEventIntensityByHour(hour, eventType) {
            // Pattern realistici per ora del giorno
            switch (eventType) {
                case 'Usage':
                    // Picchi durante orari lavorativi
                    if (hour >= 8 && hour <= 18) {
                        if (hour === 10 || hour === 14) return 25 + Math.floor(Math.random() * 15); // Picchi
                        if (hour >= 9 && hour <= 17) return 15 + Math.floor(Math.random() * 10); // Normale
                        return 5 + Math.floor(Math.random() * 5); // Basso
                    }
                    return Math.floor(Math.random() * 3); // Notte
                    
                case 'Alerts': {
                    // Più casuali, ma leggermente più frequenti durante il giorno
                    const baseAlert = hour >= 7 && hour <= 19 ? 8 : 3;
                    return baseAlert + Math.floor(Math.random() * 8);
                }
                    
                case 'Refills':
                    // Concentrati in orari di manutenzione
                    if (hour >= 7 && hour <= 9) return 3 + Math.floor(Math.random() * 4); // Mattino
                    if (hour >= 17 && hour <= 19) return 2 + Math.floor(Math.random() * 3); // Sera
                    return Math.floor(Math.random() * 2); // Altri orari
                    
                default:
                    return Math.floor(Math.random() * 10);
            }
        },

        getEventIntensityByDay(date, eventType) {
            const dayOfWeek = date.getDay(); // 0 = Domenica
            
            switch (eventType) {
                case 'Usage':
                    // Meno usage durante weekend
                    if (dayOfWeek === 0 || dayOfWeek === 6) return 20 + Math.floor(Math.random() * 15); // Weekend
                    return 45 + Math.floor(Math.random() * 25); // Settimana
                    
                case 'Alerts': {
                    // Abbastanza costanti, leggermente meno nel weekend
                    const baseAlert = (dayOfWeek === 0 || dayOfWeek === 6) ? 15 : 25;
                    return baseAlert + Math.floor(Math.random() * 20);
                }
                    
                case 'Refills':
                    // Più refill a inizio settimana
                    if (dayOfWeek === 1 || dayOfWeek === 2) return 8 + Math.floor(Math.random() * 6); // Lun-Mar
                    if (dayOfWeek === 0) return 1 + Math.floor(Math.random() * 2); // Domenica
                    return 3 + Math.floor(Math.random() * 4); // Altri giorni
                    
                default:
                    return Math.floor(Math.random() * 30);
            }
        },

        // Metodi helper per determinare il livello di visualizzazione
        isAggregatedLevel() {
            // Livello aggregato quando si selezionano multipli client, building, o floor
            return (this.selectedDataLevel === 'client' && this.selectedClients?.length >= 1) ||
                   (this.selectedDataLevel === 'building' && this.selectedBuildings?.length >= 1) ||
                   (this.selectedDataLevel === 'floor' && this.selectedFloors?.length >= 1);
        },

        isAreaLevel() {
            // Livello area quando si selezionano multiple aree
            return this.selectedDataLevel === 'area' && this.selectedAreas?.length > 0;
        },

        isSensorLevel() {
            // Livello sensor quando si selezionano multipli sensori
            return this.selectedDataLevel === 'sensor' && this.selectedSensors?.length > 0;
        },

        generatePaperConsumptionMockData(periodText, periodEnd) {
            // Dati della tabella sempre uguali come nelle immagini
            const baseTableData = [
                { name: 'Art. 924', packsUsed: 1020, trend: 8.2 },
                { name: 'Art. 928', packsUsed: 640, trend: -3.5 },
                { name: 'Art. 911', packsUsed: 230, trend: 11.1 }
            ];

            return {
                tableData: baseTableData,
                period: periodText, // periodText già contiene il periodo selezionato dall'utente
                periodEnd: periodEnd
            };
        },

        getPaperConsumptionEntityInfo() {
            // Genera informazioni realistic come nelle immagini basate sul data level
            const scenarios = [
                {
                    // Scenario 1: Livello Building/Floor/Sensor (come prima immagine)
                    clientInfo: 'YouTissue',
                    summary: '2 Buildings - 7 Floors - 10 Dispensers selected',
                    tooltipDetails: 'Client: YouTissue\nBuildings: YouTissue HQ, Via Roma Building\nFloors: Ground Floor, First Floor, Second Floor (YouTissue HQ), Reception, Office Level, Executive Floor, Roof Terrace (Via Roma)\nDispensers: DISP-137, DISP-143, DISP-166, DISP-201, DISP-205, DISP-208, DISP-301, DISP-315, DISP-402, DISP-501'
                },
                {
                    // Scenario 2: Livello Client (come seconda immagine)  
                    clientInfo: 'Lavazza, Autogrill, Camst',
                    summary: '3 Clients selected',
                    tooltipDetails: 'Clients: Lavazza, Autogrill, Camst'
                },
                {
                    // Scenario 3: Livello YouTissue HQ con floors (come terza immagine)
                    clientInfo: 'YouTissue',
                    summary: 'YouTissue HQ - 3 Floors selected',
                    tooltipDetails: 'Client: YouTissue\nBuilding: YouTissue HQ\nFloors: Ground Floor, First Floor, Second Floor'
                }
            ];

            // Scegli scenario basato su data level o casualmente per demo
            let selectedScenario;
            switch (this.selectedDataLevel) {
                case 'client':
                    selectedScenario = Math.random() > 0.5 ? scenarios[1] : scenarios[0];
                    break;
                case 'building':
                case 'floor': 
                    selectedScenario = scenarios[2];
                    break;
                case 'sensor':
                    selectedScenario = scenarios[0];
                    break;
                default:
                    selectedScenario = scenarios[Math.floor(Math.random() * scenarios.length)];
            }

            return selectedScenario;
        },

        formatKpiValue(value, type) {
            const kpi = this.kpiTypes.find(k => k.value === type);
            switch (kpi?.format) {
                case 'sheets':
                    return Math.round(value).toLocaleString();
                case 'refills/day':
                case 'entries/day':
                case 'alerts/day':
                    return value.toFixed(1);
                case 'hours':
                    return value.toFixed(1);
                case 'percentage':
                    return value.toFixed(1) + '%';
                case 'count':
                    return Math.round(value);
                case 'currency':
                    return '€' + value.toFixed(2);
                case 'comparison':
                case 'heatmap':
                    return value; // Questi saranno gestiti come grafici
                default:
                    return value;
            }
        },

        getKpiLabel(type) {
            const kpi = this.kpiTypes.find(k => k.value === type);
            return kpi?.text || 'KPI';
        },

        getKpiFormat(type) {
            const kpi = this.kpiTypes.find(k => k.value === type);
            return kpi?.format || '';
        },

        async onComparisonLevelChange() {
            if (!this.selectedComparisonLevel) {
                this.availableEntities = [];
                this.selectedEntities = [];
                return;
            }

            this.loadingEntities = true;
            try {
                // Chiamata API per ottenere le entità disponibili in base al livello selezionato
                const response = await axios.post(
                    this.$store.state.URL_GET_ENTITIES,
                    {
                        level: this.selectedComparisonLevel,
                        companyId: this.$store.state.company
                    }
                );
                
                this.availableEntities = response.data.map(entity => ({
                    text: entity.name,
                    value: entity.id
                }));
            } catch (error) {
                console.error('Errore nel caricamento delle entità:', error);
                this.availableEntities = [];
            } finally {
                this.loadingEntities = false;
            }
            this.selectedEntities = [];
        },

        async selectKpiType(type) {
            this.selectedKpiType = type;
            const selectedKpi = this.kpiTypes.find(k => k.value === type);
            if (selectedKpi) {
                this.widgetTitle = selectedKpi.text;
            }
            
            // Per Restroom Daily Usage e Usage Frequency Comparison, forza la selezione a livello sensor e carica i clients
            if (type === 'restroom_daily_usage_avg' || type === 'usage_frequency_comparison') {
                this.selectedDataLevel = 'sensor';
                this.loadingEntities = true;
                try {
                    await this.loadClients();
                } catch (error) {
                    console.error('Errore nel caricamento dei clienti:', error);
                } finally {
                    this.loadingEntities = false;
                }
            }
            // Per Dispenser Fill Heatmap, forza la selezione a livello area e carica i clients
            if (type === 'dispenser_fill_heatmap') {
                // Usa il flusso sensor-level (gerarchia completa) così onHierarchicalAreaChange
                // viene attivato quando si seleziona l'area e carica i sensori reali
                this.selectedDataLevel = 'sensor';
                // Il periodo è fisso (ultimi 14 giorni) per la heatmap
                this.selectedPeriod = 'last_14_days';
                this.loadingEntities = true;
                try {
                    await this.loadClients();
                } catch (error) {
                    console.error('Errore nel caricamento dei clienti:', error);
                } finally {
                    this.loadingEntities = false;
                }
            }

            // Per Dispenser Consumption Analysis: forza size Large e carica i client
            if (type === 'dispenser_consumption_analysis') {
                this.widgetSize = 'large';
                this.loadingEntities = true;
                try {
                    await this.loadClients();
                } catch (error) {
                    console.error('Errore nel caricamento dei clienti:', error);
                } finally {
                    this.loadingEntities = false;
                }
            }
            
            // Prosegui automaticamente al passo 2 (configurazione)
            this.currentStep = 2;
        },

        async onDataLevelChange(level) {
            if (!level) {
                this.resetEntitySelections();
                return;
            }

            this.selectedDataLevel = level;
            this.loadingEntities = true;

            try {
                // Load clients for all levels since they all need client selection
                await this.loadClients();
            } catch (error) {
                console.error('Errore nel caricamento delle entità:', error);
            } finally {
                this.loadingEntities = false;
            }
        },

        resetEntitySelections() {
            // Reset single selections
            this.selectedClient = '';
            this.selectedBuilding = '';
            this.selectedFloor = '';
            this.selectedArea = '';
            // Reset multiple selections 
            this.selectedClients = [];
            this.selectedBuildings = [];
            this.selectedFloors = [];
            this.selectedAreas = [];
            this.selectedSensors = [];
            // Reset available options
            this.availableClients = [];
            this.availableBuildings = [];
            this.availableFloors = [];
            this.availableAreas = [];
            this.availableSensors = [];
            // Reset loading states
            this.loadingBuildings = false;
            this.loadingFloors = false;
            this.loadingAreas = false;
            this.loadingSensors = false;
        },

        async loadClients() {
            try {
                const response = await axios.post(this.$store.state.URL_GET_AVAILABLE_CLIENT);
                this.availableClients = response.data.map(el => ({
                    text: el.CompanyName,
                    value: el.id
                }));
            } catch (error) {
                console.error('Errore nel caricamento dei clienti:', error);
                this.availableClients = [];
            }
        },

        async loadBuildings() {
            if (!this.selectedClient) {
                this.availableBuildings = [];
                return;
            }

            try {
                const response = await axios.post(this.$store.state.URL_GET_AVAILABLE_BUILDINGS, {
                    companyId: this.selectedClient
                });
                this.availableBuildings = response.data.map(el => ({
                    text: el.BuildingName,
                    value: el.id
                }));
            } catch (error) {
                console.error('Errore nel caricamento degli edifici:', error);
                this.availableBuildings = [];
            }
        },

        async loadFloors() {
            if (!this.selectedBuilding) {
                this.availableFloors = [];
                return;
            }

            try {
                const response = await axios.post(this.$store.state.URL_GET_AVAILABLE_FLOORS, {
                    buildingId: this.selectedBuilding
                });
                this.availableFloors = response.data.map(el => ({
                    text: el.FloorName,
                    value: el.id
                }));
            } catch (error) {
                console.error('Errore nel caricamento dei piani:', error);
                this.availableFloors = [];
            }
        },

        async loadAreas() {
            if (!this.selectedFloor) {
                this.availableAreas = [];
                return;
            }

            try {
                const response = await axios.post(this.$store.state.URL_GET_AVAILABLE_AREAS, {
                    floorId: this.selectedFloor
                });
                this.availableAreas = response.data.map(el => ({
                    text: el.AreaName,
                    value: el.id
                }));
            } catch (error) {
                console.error('Errore nel caricamento delle aree:', error);
                this.availableAreas = [];
            }
        },

        async loadSensors() {
            if (!this.selectedArea) {
                this.availableSensors = [];
                return;
            }

            try {
                const response = await axios.post(this.$store.state.URL_GET_SENSORS_COMPANY_BUILDING_FLOOR_AREA, {
                    companyId: this.selectedClient,
                    buildingId: this.selectedBuilding,
                    floorId: this.selectedFloor,
                    areaId: this.selectedArea,
                    type: this.$route.params.type
                });
                this.availableSensors = response.data.map(el => ({
                    text: el.DispenserUnique,
                    value: el.id
                }));
            } catch (error) {
                console.error('Errore nel caricamento dei sensori:', error);
                this.availableSensors = [];
            }
        },

        validateKpiConfiguration() {
            return (
                this.selectedDataLevel &&
                this.selectedEntity &&
                this.selectedPeriod &&
                this.widgetSize
            );
        },

        showArticleTypeSelection() {
            return this.selectedKpiType && ['total_consumption', 'most_used_article'].includes(this.selectedKpiType);
        },

        dragStart() {
            this.isDragging = true;
        },

        dragEnd() {
            this.isDragging = false;
            this.saveWidgetPositions();
        },

        async saveWidgetPositions() {
            try {
                const token = localStorage.getItem('jwtToken');
                
                // Recupera le preferenze attuali dal server
                const getUserPreferencesUrl = this.$store.state.BASE_URL + this.$store.state.URL_GET_USER_PREFERENCES;
                const preferencesResponse = await axios.get(getUserPreferencesUrl, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                // Estrai tutte le preferenze esistenti
                const allPreferences = preferencesResponse.data?.userPreferences || {};
                const currentCharts = allPreferences.selectedCharts || [];
                
                console.log('Riordinamento widget - posizione attuale:', this.widgets.map(w => ({ id: w.id, title: w.title })));
                
                // Crea un mapping dei widget per ID
                const widgetsById = new Map();
                currentCharts.forEach(chart => {
                    widgetsById.set(chart.id, chart);
                });
                
                // Riordina i widget nell'array selectedCharts secondo il nuovo ordine in this.widgets
                const reorderedCharts = this.widgets.map(widget => {
                    const savedWidget = widgetsById.get(widget.id);
                    if (savedWidget) {
                        return savedWidget;
                    }
                    console.warn(`Widget ${widget.id} non trovato nelle preferenze salvate`);
                    return null;
                }).filter(w => w !== null);
                
                // Aggiungi eventuali widget nelle preferenze che non sono nella vista corrente
                currentCharts.forEach(chart => {
                    if (!this.widgets.find(w => w.id === chart.id)) {
                        reorderedCharts.push(chart);
                    }
                });
                
                console.log('Nuovo ordine widget:', reorderedCharts.map(w => ({ id: w.id, title: w.title })));
                
                // Prepara tutte le preferenze da inviare con il nuovo ordine
                const updatedPreferences = {
                    ...allPreferences,
                    selectedCharts: reorderedCharts
                };
                
                const patchUrl = this.$store.state.BASE_URL + this.$store.state.URL_PATCH_USER_PREFERENCES;
                
                await axios.patch(patchUrl, {
                    userPreferences: updatedPreferences
                }, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });

                // Aggiorna lo store locale con le nuove preferenze
                this.$store.state.userPreferences = updatedPreferences;

                console.log('Ordine widget salvato con successo');
            } catch (error) {
                console.error('Errore nel salvataggio delle posizioni:', error);
            }
        },


        async changeWidgetSize(widget, newSize) {
            try {
                this.overlay = true;
                
                widget.size = newSize;
                
                // Salva la configurazione aggiornata
                await this.saveWidgetConfiguration(widget);
                
                // Forza il re-render del widget per applicare le nuove dimensioni
                this.$forceUpdate();
                
                this.$nextTick(() => {
                    // $refs.kpiChart è un array quando il ref è dentro v-for
                    const charts = this.$refs.kpiChart;
                    if (charts) {
                        const chartArray = Array.isArray(charts) ? charts : [charts];
                        chartArray.forEach(chart => {
                            if (chart && typeof chart.updateOptions === 'function') {
                                chart.updateOptions({
                                    chart: { height: this.getWidgetHeight(newSize) }
                                });
                            }
                        });
                    }
                });
            } catch (error) {
                console.error('Errore nel cambio dimensione widget:', error);
            } finally {
                this.overlay = false;
            }
        },

        defaultWidgetState() {
            return {
                title: '',
                type: null,
                size: 'medium',
                entities: [],
                dataLevel: null,
                articleType: null,
                startDate: null,
                endDate: null
            };
        },

        // Hierarchical selection methods
        async onHierarchicalClientChange(clientId) {
            // Reset dependent selections
            this.selectedBuilding = '';
            this.selectedFloor = '';
            this.selectedArea = '';
            this.selectedBuildings = [];
            this.selectedFloors = [];
            this.selectedAreas = [];
            this.selectedSensors = [];
            this.availableBuildings = [];
            this.availableFloors = [];
            this.availableAreas = [];
            this.availableSensors = [];

            if (!clientId) {
                return;
            }

            // Load buildings for the selected client
            this.loadingBuildings = true;
            try {
                const response = await axios.post(this.$store.state.URL_GET_AVAILABLE_BUILDINGS, {
                    companyId: clientId
                });
                this.availableBuildings = response.data.map(el => ({
                    text: el.BuildingName,
                    value: el.id
                }));
            } catch (error) {
                console.error('Errore nel caricamento degli edifici:', error);
                this.availableBuildings = [];
            } finally {
                this.loadingBuildings = false;
            }
        },

        async onHierarchicalClientChangeForWashroomKpis(clientId) {
            // Reset selections
            this.selectedSensors = [];
            this.availableSensors = [];

            if (!clientId) {
                return;
            }

            // Load all Washroom sensors for the selected client directly
            // Usato per Restroom Daily Usage e Usage Frequency Comparison
            this.loadingSensors = true;
            try {
                const response = await axios.post(this.$store.state.URL_GET_SENSORS_COMPANY_BUILDING_FLOOR_AREA, {
                    companyId: clientId,
                    // Omettendo buildingId, floorId e areaId per ottenere tutti i sensori del client
                    // Specifichiamo esplicitamente il tipo Washroom
                    type: 'Washroom'
                });
                this.availableSensors = response.data.map(el => ({
                    text: el.DisplayName || el.DispenserUnique,
                    value: el.id,
                    devEui: el.DispenserUnique // Salva il dev_eui per le chiamate API
                }));
            } catch (error) {
                console.error('Errore nel caricamento dei sensori Washroom:', error);
                this.availableSensors = [];
            } finally {
                this.loadingSensors = false;
            }
        },

        async onHierarchicalBuildingChange(buildingId) {
            // Reset dependent selections
            this.selectedFloor = '';
            this.selectedArea = '';
            this.selectedFloors = [];
            this.selectedAreas = [];
            this.selectedSensors = [];
            this.availableFloors = [];
            this.availableAreas = [];
            this.availableSensors = [];

            if (!buildingId) {
                return;
            }

            // Load floors for the selected building
            this.loadingFloors = true;
            try {
                const response = await axios.post(this.$store.state.URL_GET_AVAILABLE_FLOORS, {
                    buildingId: buildingId
                });
                this.availableFloors = response.data.map(el => ({
                    text: el.FloorName,
                    value: el.id
                }));
            } catch (error) {
                console.error('Errore nel caricamento dei piani:', error);
                this.availableFloors = [];
            } finally {
                this.loadingFloors = false;
            }
        },

        async onHierarchicalFloorChange(floorId) {
            // Reset dependent selections
            this.selectedArea = '';
            this.selectedAreas = [];
            this.selectedSensors = [];
            this.availableAreas = [];
            this.availableSensors = [];

            if (!floorId) {
                return;
            }

            // Load areas for the selected floor
            this.loadingAreas = true;
            try {
                const response = await axios.post(this.$store.state.URL_GET_AVAILABLE_AREAS, {
                    floorId: floorId
                });
                this.availableAreas = response.data.map(el => ({
                    text: el.AreaName,
                    value: el.id
                }));
            } catch (error) {
                console.error('Errore nel caricamento delle aree:', error);
                this.availableAreas = [];
            } finally {
                this.loadingAreas = false;
            }
        },

        async onHierarchicalAreaChange(areaId) {
            // Reset dependent selections
            this.selectedSensors = [];
            this.availableSensors = [];
            this.availableSensorsFullData = [];

            if (!areaId) {
                return;
            }

            // Load sensors for the selected area
            this.loadingSensors = true;
            try {
                const response = await axios.post(this.$store.state.URL_GET_SENSORS_COMPANY_BUILDING_FLOOR_AREA, {
                    companyId: this.selectedClient,
                    buildingId: this.selectedBuilding,
                    floorId: this.selectedFloor,
                    areaId: areaId
                });
                this.availableSensors = response.data.map(el => ({
                    text: el.DisplayName || el.DispenserUnique,
                    value: el.id
                }));
                // Salva i dati completi (nome + tipologia da DispenserSubtype) per la heatmap
                this.availableSensorsFullData = response.data
                    .filter(el => el.DispenserType === 'Dispenser' || !el.DispenserType || el.DispenserType === 'dispenser')
                    .map(el => ({
                        id: el.id,
                        name: el.DisplayName || el.DispenserUnique || el.id,
                        type: el.DispenserSubtype || el.DispenserType || ''
                    }));
            } catch (error) {
                console.error('Errore nel caricamento dei sensori:', error);
                this.availableSensors = [];
                this.availableSensorsFullData = [];
            } finally {
                this.loadingSensors = false;
            }
        },

        // Period selection methods
        onPeriodChange() {
            // Reset custom dates when switching away from custom
            if (this.selectedPeriod !== 'custom') {
                this.customDateRange = [];
            }
        },

        getYesterday() {
            const yesterday = new Date();
            yesterday.setDate(yesterday.getDate() - 1);
            return toBackendDate(yesterday);
        },

        getMinSelectableDate() {
            // Allow selection up to 30 days back for custom period
            const minDate = new Date();
            minDate.setDate(minDate.getDate() - 30);
            return toBackendDate(minDate);
        },

        getDateDifference(startDate, endDate) {
            const start = new Date(startDate);
            const end = new Date(endDate);
            const diffTime = Math.abs(end - start);
            // Calcola i giorni inclusi nel range (non la differenza)
            // Es: 09/02 ~ 10/02 = 2 giorni (il 9 e il 10)
            const daysDifference = Math.floor(diffTime / (1000 * 60 * 60 * 24));
            return daysDifference + 1;
        },

        getPeriodDisplayText() {
            const tz = this.$store.state.timezone || 'Europe/Rome';
            const yesterday = this.getYesterday();
            const yesterdayFormatted = formatDateDisplay(yesterday, tz);

            switch (this.selectedPeriod) {
                case 'last_day':
                case 'last_1_day':
                    return `Data from yesterday (${yesterdayFormatted}) - Up to ${yesterdayFormatted}`;
                case 'last_7_days':
                    return `Data from last 7 days - Up to ${yesterdayFormatted}`;
                case 'last_14_days':
                    return `Data from last 14 days - Up to ${yesterdayFormatted}`;
                case 'last_30_days':
                    return `Data from last 30 days - Up to ${yesterdayFormatted}`;
                case 'custom':
                    if (this.customStartDate && this.customEndDate) {
                        const startFormatted = formatDateDisplay(this.customStartDate, tz);
                        const endFormatted = formatDateDisplay(this.customEndDate, tz);
                        return `Custom period: ${startFormatted} to ${endFormatted} - Up to ${endFormatted}`;
                    }
                    return `Select custom date range - Up to ${yesterdayFormatted}`;
                default:
                    return `Up to ${yesterdayFormatted}`;
            }
        },

        formatPeriodText(periodType, startDate, endDate) {
            const tz = this.$store.state.timezone || 'Europe/Rome';
            const endFormatted = formatDateDisplay(endDate, tz);

            switch (periodType) {
                case 'last_day':
                case 'last_1_day':
                    return `Data from yesterday (${endFormatted}) - Up to ${endFormatted}`;
                case 'last_7_days':
                    return `Data from last 7 days - Up to ${endFormatted}`;
                case 'last_14_days':
                    return `Data from last 14 days - Up to ${endFormatted}`;
                case 'last_30_days':
                    return `Data from last 30 days - Up to ${endFormatted}`;
                case 'custom':
                    if (startDate && endDate) {
                        const startFormatted = formatDateDisplay(startDate, tz);
                        return `Custom period: ${startFormatted} to ${endFormatted} - Up to ${endFormatted}`;
                    }
                    return `Up to ${endFormatted}`;
                default:
                    return `Up to ${endFormatted}`;
            }
        },

        getSelectedPeriodInfo() {
            // Returns period information for widget configuration
            const yesterday = this.getYesterday();
            
            switch (this.selectedPeriod) {
                case 'last_day':
                case 'last_1_day': // Normalizza 'last_1_day' a 'last_day'
                    return {
                        type: 'last_day',
                        days: 1,
                        startDate: yesterday,
                        endDate: yesterday
                    };
                case 'last_7_days': {
                    const startDate = new Date(yesterday);
                    startDate.setDate(startDate.getDate() - 6);
                    return {
                        type: 'last_7_days',
                        days: 7,
                        startDate: toBackendDate(startDate),
                        endDate: yesterday
                    };
                }
                case 'last_14_days': {
                    const startDate = new Date(yesterday);
                    startDate.setDate(startDate.getDate() - 13);
                    return {
                        type: 'last_14_days',
                        days: 14,
                        startDate: toBackendDate(startDate),
                        endDate: yesterday
                    };
                }
                case 'last_30_days': {
                    const startDate = new Date(yesterday);
                    startDate.setDate(startDate.getDate() - 29);
                    return {
                        type: 'last_30_days',
                        days: 30,
                        startDate: toBackendDate(startDate),
                        endDate: yesterday
                    };
                }
                case 'custom':
                    return {
                        type: 'custom',
                        startDate: this.customStartDate,
                        endDate: this.customEndDate,
                        days: this.customStartDate && this.customEndDate ? 
                              this.getDateDifference(this.customStartDate, this.customEndDate) : 0
                    };
                default:
                    return null;
            }
        },

        onSensorsSelectionChange(value) {
            // Gestisce il "Select All" per i sensori
            if (value && value.includes('all')) {
                // Rimuovi 'all' dalla selezione per evitare che venga incluso nei valori
                const sensorsWithoutAll = value.filter(v => v !== 'all');
                
                // Se tutti i sensori sono già selezionati, deseleziona tutto
                const allSensorValues = this.availableSensors.map(sensor => sensor.value);
                const allSelected = allSensorValues.length > 0 && 
                                   allSensorValues.every(sensorValue => sensorsWithoutAll.includes(sensorValue));
                
                if (allSelected) {
                    // Deseleziona tutti
                    this.selectedSensors = [];
                } else {
                    // Seleziona tutti i sensori disponibili
                    this.selectedSensors = allSensorValues;
                }
            }
        },
    },

    watch: {
        // Watchers removed since hierarchical selection is now handled by onChange methods
        // The multiple selection system uses explicit change handlers for better control
    },

    created() {
        // Le opzioni localizzate sono ora gestite tramite computed properties
        // per supportare il cambio dinamico della lingua
    },

    async mounted() {
        // Mostra il loader immediatamente
        this.overlay = true;
        
        // Aspetta che il componente sia montato e verifica la disponibilità del token
        await this.$nextTick();
        
        const token = localStorage.getItem('jwtToken');
        if (!token) {
            console.warn('JWT token non disponibile, impossibile caricare i widget');
            this.overlay = false;
            return;
        }

        // Carica le configurazioni dei widget salvate dalle preferenze utente
        // Il loader viene nascosto automaticamente alla fine di loadWidgetConfigurations()
        await this.loadWidgetConfigurations();
    }
};
</script>

<style scoped>
/* Draggable widgets container */
.draggable-widgets {
    display: flex;
    flex-wrap: wrap;
    width: 100%;
}

.widget-container {
    transition: transform 0.2s ease;
}

.widget-card {
    display: flex;
    flex-direction: column;
    transition: all 0.3s ease;
    overflow: hidden;
}

.widget-card.small {
    min-height: 250px;
    max-height: 290px;
}

.widget-card.medium {
    min-height: 410px;
    max-height: 450px;
}

.widget-card.large {
    min-height: 560px;
    max-height: 610px;
}

.widget-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: nowrap;
    padding: 8px 12px !important;
}

.widget-title-text {
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-weight: 500;
    font-size: 0.95rem;
    color: #213A8F;
    line-height: 1.3;
}

.widget-title-section {
    flex-grow: 1;
    margin-right: 16px;
}

.widget-main-title {
    font-size: 1.1rem;
    font-weight: 500;
    color: #213A8F;
    line-height: 1.2;
    margin-bottom: 4px;
    display: flex;
    align-items: center;
}

.widget-subtitle {
    font-size: 0.85rem;
    color: #666;
    margin-bottom: 2px;
}

.widget-summary {
    font-size: 0.8rem;
    color: #999;
    display: flex;
    align-items: center;
}

.widget-actions {
    display: flex;
    gap: 4px;
}

.widget-action-btn {
    opacity: 0.7;
    transition: opacity 0.2s ease;
}

.widget-container:hover .widget-action-btn {
    opacity: 1;
}

.widget-content {
    flex-grow: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 8px 12px !important;
}

.widget-card.small .widget-content {
    max-height: 240px;
}

.widget-card.medium .widget-content {
    max-height: 400px;
}

.widget-card.large .widget-content {
    max-height: 560px;
}

.widget-card.small .widget-header {
    padding: 4px 8px !important;
}

.widget-card.small .widget-title-text {
    font-size: 0.8rem;
}

.ghost-widget {
    opacity: 0.5;
    background: #f5f5f5;
    border: 2px dashed #213A8F;
    border-radius: 4px;
}

.kpi-title {
    font-size: 1rem;
    font-weight: 500;
    color: #213A8F;
    line-height: 1.2;
}

.kpi-description {
    font-size: 0.85rem;
    color: #666;
    line-height: 1.3;
}

.kpi-container {
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.kpi-value {
    font-size: 3rem;
    font-weight: bold;
    color: #234a98;
}

.kpi-label {
    font-size: 1.2rem;
    color: #666;
    margin-top: 1rem;
}

.widget-type-card {
    height: 100%;
    cursor: pointer;
    border: 2px solid transparent;
    transition: all 0.3s ease;
}

.widget-type-card:hover {
    border-color: #213A8F;
}

.widget-type-card.selected {
    border-color: #213A8F;
    background-color: #f5f5f5;
}

.headline {
    color: #213A8F;
    font-weight: 500;
}

.chart-template-card {
    cursor: pointer;
    border: 2px solid transparent;
    transition: all 0.3s ease;
    margin-bottom: 16px;
}

.chart-template-card:hover {
    border-color: #213A8F;
}

.chart-template-card.selected {
    border-color: #213A8F;
    background-color: #f5f5f5;
}

.chart-preview {
    background-color: #f5f5f5;
    border-right: 1px solid #eee;
}

.template-title {
    font-size: 1.1rem;
    font-weight: 500;
    color: #213A8F;
    margin-bottom: 8px;
}

.template-description {
    font-size: 0.9rem;
    color: #666;
    margin-bottom: 12px;
}

.template-details {
    display: flex;
    align-items: center;
}

.kpi-type-card {
    cursor: pointer;
    border: 2px solid transparent;
    transition: all 0.3s ease;
}

.kpi-type-card:hover {
    border-color: #213A8F;
    transform: translateY(-2px);
}

.kpi-type-card.selected {
    border-color: #213A8F;
    background-color: #f5f5f5;
}

.title {
    font-size: 1.1rem;
    font-weight: 500;
    color: #213A8F;
}

.caption {
    color: #666;
    line-height: 1.2;
}

.v-list-item-subtitle {
    white-space: normal;
    line-height: 1.2;
}

.font-italic {
    font-style: italic;
    color: #666;
    font-size: 0.85rem;
}

.widget-drag-handle {
    cursor: move;
    opacity: 0.5;
    transition: opacity 0.2s ease;
}

.widget-container:hover .widget-drag-handle {
    opacity: 1;
}



.widget-card {
    transition: all 0.3s ease;
}

.widget-card.dragging {
    transform: scale(1.02);
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

/* Table KPI Styles */
.table-kpi-container {
    height: 100%;
    display: flex;
    flex-direction: column;
    min-height: 0; /* Importante per flex shrinking */
}

.kpi-table {
    flex-grow: 1;
    flex-shrink: 1;
    min-height: 0; /* Importante per flex shrinking */
}

.kpi-table >>> .v-data-table__wrapper {
    overflow-y: auto;
    overflow-x: auto;
}

/* Altezze specifiche per tabelle basate sulla dimensione del widget */
.widget-card.small .kpi-table >>> .v-data-table__wrapper {
    max-height: 120px;
}

.widget-card.medium .kpi-table >>> .v-data-table__wrapper {
    max-height: 250px;
}

.widget-card.large .kpi-table >>> .v-data-table__wrapper {
    max-height: 400px;
}

.kpi-table >>> th {
    font-size: 0.85rem !important;
    font-weight: 600 !important;
    color: #213A8F !important;
    padding: 8px 12px !important;
    border-bottom: 2px solid #213A8F !important;
    white-space: nowrap;
}

.kpi-table >>> td {
    font-size: 0.9rem !important;
    padding: 8px 12px !important;
    border-bottom: 1px solid #e0e0e0 !important;
}

.widget-card.small .kpi-table >>> th {
    font-size: 0.75rem !important;
    padding: 4px 8px !important;
}

.widget-card.small .kpi-table >>> td {
    font-size: 0.8rem !important;
    padding: 4px 8px !important;
}

.widget-card.medium .kpi-table >>> th {
    font-size: 0.8rem !important;
    padding: 6px 10px !important;
}

.widget-card.medium .kpi-table >>> td {
    font-size: 0.85rem !important;
    padding: 6px 10px !important;
}

.kpi-table >>> tbody tr:hover {
    background-color: #f5f5f5 !important;
}

/* Trend Styles */
.trend-positive {
    color: #4CAF50 !important;
    font-weight: 600;
}

.trend-negative {
    color: #F44336 !important;
    font-weight: 600;
}

.trend-neutral {
    color: #9E9E9E !important;
    font-weight: 600;
}

/* Period Info */
.period-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: auto;
    padding-top: 12px;
    border-top: 1px solid #e0e0e0;
    font-size: 0.8rem;
    color: #666;
}

.period-label {
    font-weight: 500;
}

.period-date {
    color: #999;
}

/* Hierarchical Selection Styles */
.v-alert.info--text {
    background-color: #e3f2fd !important;
    border-left-color: #213A8F !important;
}

.v-alert.info--text .v-alert__content {
    color: #213A8F !important;
}

/* Multiple Selection Styles */
.v-select--is-menu-active .v-input__control {
    border-color: #213A8F !important;
}

.v-chip {
    background-color: #213A8F !important;
    color: white !important;
}

.v-chip .v-chip__close {
    color: white !important;
}

.v-chip .v-chip__close:hover {
    background-color: rgba(255, 255, 255, 0.2) !important;
}

.v-select.v-select--chips .v-select__selections {
    min-height: 42px;
}

.v-select.v-select--chips .v-chip {
    margin: 2px 4px 2px 0;
    font-size: 12px;
    height: 28px;
}

/* Loading indicator for hierarchical selects */
.v-select .v-progress-circular {
    color: #213A8F !important;
}

/* Improved spacing for multiple chips */
.v-text-field.v-text-field--outlined .v-select__selections {
    padding: 8px 12px;
}

/* Custom style for select all option */
.v-list-item--active .v-list-item__title {
    color: #213A8F !important;
    font-weight: 600;
}

/* Period Selection Styles */
.period-info-card {
    background-color: #f8f9fa !important;
    border-color: #213A8F !important;
    border-width: 1px !important;
}

.period-info-text {
    display: flex;
    align-items: center;
    font-size: 0.9rem;
    color: #666 !important;
}

.period-info-card .v-card__text {
    padding: 12px 16px !important;
}

/* Custom period validation alerts */
.v-alert.success--text {
    background-color: #e8f5e8 !important;
    border-left-color: #4CAF50 !important;
}

.v-alert.warning--text {
    background-color: #fff3e0 !important;
    border-left-color: #FF9800 !important;
}

.v-alert.error--text {
    background-color: #ffebee !important;
    border-left-color: #F44336 !important;
}

/* Date picker styling */
.v-menu .v-picker {
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

/* Heatmap Widget Styling */
.widget-card .apexcharts-heatmap-rect {
    cursor: pointer;
    stroke: rgba(255, 255, 255, 0.8);
    stroke-width: 1;
}

.widget-card .apexcharts-heatmap-rect:hover {
    stroke: rgba(255, 255, 255, 1);
    stroke-width: 2;
    filter: brightness(1.1);
}

.widget-card .apexcharts-tooltip {
    border-radius: 6px !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.v-date-picker-header {
    background-color: #213A8F !important;
}

.v-date-picker-header .v-btn {
    color: white !important;
}

/* Date input focus state */
.v-text-field.v-text-field--focused .v-input__control {
    border-color: #213A8F !important;
}

.v-text-field.v-text-field--focused .v-input__icon--prepend-inner {
    color: #213A8F !important;
}

/* Refill Frequency Value Styling */
.refill-value {
    font-weight: 600;
    color: #213A8F;
    font-size: 0.95rem;
}

/* Packs Used Value Styling */
.packs-value {
    font-weight: 600;
    color: #213A8F;
    font-size: 0.95rem;
}

/* Entries Value Styling */
.entries-value {
    font-weight: 600;
    color: #009CAB;
    font-size: 0.95rem;
}

/* Status Chip Styling */
.status-chip {
    min-width: 70px;
    justify-content: center;
}

.status-chip .v-chip__content {
    font-size: 0.8rem;
    font-weight: 600;
}

/* Alerts Value Styling */
.alerts-value {
    font-weight: 600;
    color: #FF9800;
    font-size: 0.95rem;
}

/* Restroom Info Styling */
.restroom-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.restroom-name {
    font-weight: 600;
    color: #2c3e50;
    font-size: 0.95rem;
}

.restroom-location {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 0.8rem;
    color: #666;
}

/* Daily Pattern AM/PM Styling */
.daily-pattern-container {
    width: 100%;
    padding: 4px 0;
}

.pattern-bar {
    display: flex;
    width: 100%;
    height: 28px;
    border-radius: 4px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.pattern-am {
    background: linear-gradient(135deg, #64B5F6 0%, #42A5F5 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: width 0.3s ease;
    position: relative;
}

.pattern-pm {
    background: linear-gradient(135deg, #1E3A8A 0%, #1E40AF 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: width 0.3s ease;
    position: relative;
}

.pattern-label {
    color: white;
    font-size: 0.75rem;
    font-weight: 600;
    text-shadow: 0 1px 2px rgba(0,0,0,0.2);
}

.pattern-labels {
    display: flex;
    justify-content: space-between;
    margin-top: 4px;
    font-size: 0.7rem;
    color: #666;
}

.label-am {
    color: #42A5F5;
    font-weight: 600;
}

.label-pm {
    color: #1E40AF;
    font-weight: 600;
}

/* Widget Title Input Styling */
.v-text-field input {
    color: #2c3e50 !important;
    font-weight: 500 !important;
    font-size: 1rem !important;
}

.v-text-field .v-label {
    color: #666 !important;
    font-weight: 500 !important;
}

.v-text-field .v-label--active {
    color: #213A8F !important;
    font-weight: 600 !important;
}

/* Enhanced input field visibility */
.v-text-field--outlined .v-input__control {
    background-color: #fff !important;
}

.v-text-field--outlined input {
    color: #2c3e50 !important;
    font-weight: 500 !important;
}

/* Focus state for better visibility */
.v-text-field--outlined.v-input--is-focused .v-input__control {
    border-color: #213A8F !important;
    border-width: 2px !important;
}

.v-text-field--outlined.v-input--is-focused input {
    color: #213A8F !important;
    font-weight: 600 !important;
}

/* Specific styling for Widget Title Input */
.widget-title-input input {
    color: #2c3e50 !important;
    font-weight: 600 !important;
    font-size: 1.1rem !important;
    text-shadow: 0 0 1px rgba(44, 62, 80, 0.3) !important;
}

.widget-title-input .v-label {
    color: #213A8F !important;
    font-weight: 600 !important;
}

/* ===== DISPENSER FILL HEATMAP ===== */
.heatmap-widget-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    gap: 8px;
}

/* ── Header row: titolo centrato + legenda a destra ── */
.heatmap-header-row {
    position: relative;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    flex-shrink: 0;
    padding-bottom: 4px;
}

.heatmap-title-area {
    text-align: center;
    flex: 1;
}

.heatmap-main-title {
    font-size: 1rem;
    font-weight: 700;
    color: #213A8F;
    line-height: 1.3;
}

.heatmap-main-subtitle {
    font-size: 0.78rem;
    color: #6b7a99;
    margin-top: 2px;
    line-height: 1.3;
}

.heatmap-legend {
    position: absolute;
    right: 0;
    top: 2px;
    display: flex;
    align-items: center;
    gap: 6px;
}

.heatmap-legend-label {
    font-size: 0.72rem;
    color: #6b7a99;
    white-space: nowrap;
}

.heatmap-legend-bar {
    display: flex;
    gap: 3px;
}

.heatmap-legend-cell {
    width: 18px;
    height: 14px;
    border-radius: 3px;
    border: 1px solid rgba(33, 58, 143, 0.15);
}

.heatmap-table-wrapper {
    overflow: auto;
    flex: 1;
}

.heatmap-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.82rem;
    table-layout: fixed;
}

.heatmap-th {
    background: #fff;
    color: #213A8F;
    font-weight: 600;
    padding: 8px 6px;
    text-align: center;
    white-space: nowrap;
    font-size: 0.78rem;
    min-width: 80px;
}

.heatmap-th-location {
    text-align: left;
    min-width: 180px;
    width: 30%;
}

.heatmap-td-location {
    padding: 8px 10px;
    border-bottom: 1px solid #e8eaf0;
    vertical-align: middle;
    background: #fff;
}

.heatmap-dispenser-name {
    font-weight: 600;
    color: #1a237e;
    font-size: 0.83rem;
    line-height: 1.3;
}

.heatmap-dispenser-type {
    font-size: 0.72rem;
    color: #5c6bc0;
    font-style: italic;
    margin-top: 1px;
    line-height: 1.2;
}

.heatmap-dispenser-location {
    display: flex;
    align-items: center;
    gap: 2px;
    font-size: 0.73rem;
    color: #777;
    margin-top: 2px;
}

.heatmap-td-cell {
    padding: 8px 4px;
    text-align: center;
    font-weight: 600;
    font-size: 0.83rem;
    border-bottom: 1px solid rgba(255,255,255,0.15);
    border-left: 1px solid rgba(255,255,255,0.15);
    transition: filter 0.2s;
}

.heatmap-td-cell:hover {
    filter: brightness(1.1);
    cursor: default;
}
/* ===== END HEATMAP ===== */

/* ===== DISPENSER CONSUMPTION ANALYSIS ===== */
.dca-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    gap: 8px;
}

.dca-header {
    padding: 4px 2px 2px;
    flex-shrink: 0;
}

.dca-title {
    font-size: 1rem;
    font-weight: 700;
    color: #213A8F;
    line-height: 1.3;
}

.dca-subtitle {
    font-size: 0.75rem;
    color: #777;
    margin-top: 2px;
}

.dca-table-wrapper {
    overflow: auto;
    flex: 1;
}

.dca-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.82rem;
}

/* Column header */
.dca-th {
    background: transparent;
    color: #213A8F;
    font-weight: 700;
    font-size: 0.72rem;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    padding: 6px 8px;
    border-bottom: 2px solid #BFCCE4;
    white-space: nowrap;
}

.dca-col-dispenser  { text-align: left;   width: 22%; }
.dca-col-location   { text-align: left;   width: 26%; }
.dca-col-consumption{ text-align: left;   width: 28%; }
.dca-col-avg        { text-align: center; width: 12%; }
.dca-col-visits     { text-align: center; width: 12%; }

/* Group header row */
.dca-group-header-row {
    background: #f0f4fb;
}

.dca-group-name {
    font-weight: 700;
    font-size: 0.8rem;
    color: #213A8F;
    padding: 6px 8px;
}

.dca-group-unit {
    font-size: 0.72rem;
    color: #888;
    font-weight: 600;
    letter-spacing: 0.05em;
    padding: 6px 8px;
}

/* Item rows */
.dca-item-row {
    border-bottom: 1px solid #e8eaf0;
}

.dca-item-row:hover {
    background: #f7f9fd;
}

.dca-td-dispenser {
    padding: 8px 8px;
    vertical-align: middle;
}

.dca-dispenser-name {
    font-weight: 600;
    color: #1a237e;
    font-size: 0.83rem;
    line-height: 1.3;
}

.dca-dispenser-type {
    font-size: 0.72rem;
    color: #5c6bc0;
    font-style: italic;
    margin-top: 1px;
}

.dca-td-location {
    padding: 8px 8px;
    font-size: 0.78rem;
    color: #444;
    vertical-align: middle;
}

.dca-td-consumption {
    padding: 8px 8px;
    vertical-align: middle;
}

.dca-bar-cell {
    display: flex;
    align-items: center;
    gap: 8px;
}

.dca-bar-track {
    flex: 1;
    height: 8px;
    background: #e0e7f3;
    border-radius: 4px;
    overflow: hidden;
    min-width: 60px;
}

.dca-bar-fill {
    height: 100%;
    background: #213A8F;
    border-radius: 4px;
    transition: width 0.3s ease;
}

.dca-bar-value {
    font-weight: 600;
    color: #213A8F;
    font-size: 0.83rem;
    min-width: 22px;
    text-align: right;
}

.dca-td-avg {
    padding: 8px 8px;
    text-align: center;
    font-size: 0.83rem;
    color: #333;
    vertical-align: middle;
}

.dca-td-visits {
    padding: 8px 8px;
    text-align: center;
    font-weight: 700;
    font-size: 0.83rem;
    vertical-align: middle;
}

.dca-visits-low  { color: #2e7d32; }
.dca-visits-high { color: #e65100; }

/* Group total row */
.dca-group-total-row {
    background: #f0f4fb;
    border-top: 2px solid #BFCCE4;
}

.dca-total-label {
    padding: 6px 8px;
    font-weight: 700;
    font-size: 0.78rem;
    color: #213A8F;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}

.dca-total-value {
    padding: 6px 8px;
    text-align: center;
    font-weight: 700;
    font-size: 0.83rem;
    color: #213A8F;
}

.dca-total-dash {
    padding: 6px 8px;
    text-align: center;
    color: #aaa;
    font-size: 0.83rem;
}
/* ===== END DISPENSER CONSUMPTION ANALYSIS ===== */

.widget-title-input .v-label--active {
    color: #213A8F !important;
    font-weight: 700 !important;
    transform: translateY(-18px) scale(0.75) !important;
}

.widget-title-input.v-text-field--outlined .v-input__control {
    background-color: #ffffff !important;
    border: 2px solid #e0e0e0 !important;
}

.widget-title-input.v-text-field--outlined.v-input--is-focused .v-input__control {
    border-color: #213A8F !important;
    border-width: 2px !important;
    box-shadow: 0 0 0 2px rgba(33, 58, 143, 0.1) !important;
}
</style> 